import React, { useState, useEffect, useMemo } from 'react';
import { supabase, sendConfirmationEmail } from '../lib/supabase';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Plus, 
  Trash2, 
  Pencil,
  CheckCircle, 
  XCircle, 
  X,
  User,
  UserPlus,
  Mail,
  Phone,
  LayoutDashboard, 
  CalendarDays, 
  Scissors,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Users,
  Contact,
  Shield,
  Star,
  Globe,
  Lock,
  UserCheck,
  Store,
  Settings,
  Check,
  AlertCircle,
  Bell,
  MessageSquare,
  Send,
  ChevronDown,
  ChevronUp,
  UserRound,
  Edit2,
  Eye,
  CreditCard,
  ExternalLink,
  MapPin,
  BarChart3,
  TrendingUp,
  PieChart as PieChartIcon,
  Upload,
  Download,
  Target,
  Minus
} from 'lucide-react';
import { format, addDays, addMonths, startOfToday, isSameDay, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isToday, isPast, isBefore, startOfWeek, endOfWeek, subDays, isSameMonth, subMonths } from 'date-fns';
import { toast } from 'sonner';
import { Service, Slot, Reservation, Profile, BarberShop, Barber, Subscription, Review, Message, PromoCode, InventoryItem, Commission } from '../types';
import { motion, AnimatePresence } from 'motion/react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  Cell,
  PieChart,
  Pie,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { Logo } from './Logo';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';
import { LegalModal } from './Legal';
import { ConfirmDialog } from './ConfirmDialog';
import { ErrorBoundary } from './ErrorBoundary';

const ModernTimePicker = ({ value, onChange, className }: { value: string, onChange: (v: string) => void, className?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  const [h, m] = value ? value.split(':') : ['09', '00'];
  
  // Convert 24h to 12h for display
  const get12hDisplay = (time24: string) => {
    if (!time24) return '--:--';
    const [h24, m24] = time24.split(':');
    const hour = parseInt(h24);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m24} ${ampm}`;
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button 
        type="button" 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-between text-left transition-all group ${className || "w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"}`}
      >
        <div className="flex items-center gap-3">
          <Clock size={16} className={`transition-colors ${isOpen ? 'text-zinc-900 dark:text-amber-500' : 'text-zinc-400 dark:text-zinc-500'}`} />
          <div className="flex flex-col">
            <span className="text-sm tracking-tight">{value || '--:--'}</span>
            <span className="text-[10px] text-zinc-400 dark:text-zinc-500 font-bold uppercase tracking-widest leading-none">
              {get12hDisplay(value)}
            </span>
          </div>
        </div>
        <ChevronDown size={14} className={`text-zinc-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute z-50 left-0 mt-3 p-3 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-[2rem] shadow-2xl overflow-hidden flex flex-col w-64 origin-top"
          >
            <div className="flex items-center justify-center p-3 border-b border-zinc-50 dark:border-zinc-800 mb-2">
               <div className="flex items-center gap-1">
                 <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                 <span className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">{get12hDisplay(`${h}:${m}`)}</span>
               </div>
            </div>

            <div className="flex gap-2 h-56">
               <div className="flex-1 overflow-y-auto no-scrollbar snap-y text-center pr-1 custom-scrollbar">
                 <div className="h-2" />
                 {hours.map(hour => (
                   <div 
                     key={`h-${hour}`} 
                     onClick={() => onChange(`${hour}:${m || '00'}`)}
                     className={`py-3 cursor-pointer snap-center font-black text-sm transition-all rounded-xl mb-1 ${h === hour ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 scale-[1.05] shadow-lg shadow-zinc-200 dark:shadow-none' : 'text-zinc-400 dark:text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                   >
                     {hour}
                   </div>
                 ))}
                 <div className="h-2" />
               </div>
               <div className="w-px bg-zinc-50 dark:bg-zinc-800 self-stretch my-2" />
               <div className="flex-1 overflow-y-auto no-scrollbar snap-y text-center pl-1 custom-scrollbar">
                 <div className="h-2" />
                 {minutes.map(minute => (
                   <div 
                     key={`m-${minute}`} 
                     onClick={() => {
                       onChange(`${h || '09'}:${minute}`);
                       setIsOpen(false);
                     }}
                     className={`py-3 cursor-pointer snap-center font-black text-sm transition-all rounded-xl mb-1 ${m === minute ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 scale-[1.05] shadow-lg shadow-zinc-200 dark:shadow-none' : 'text-zinc-400 dark:text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-800'}`}
                   >
                     {minute}
                   </div>
                 ))}
                 <div className="h-2" />
               </div>
            </div>

            <div className="mt-2 grid grid-cols-4 gap-1 p-1 bg-zinc-50 dark:bg-zinc-800 rounded-xl">
               {['09', '12', '15', '18'].map(hour => (
                 <button
                   key={`quick-${hour}`}
                   type="button"
                   onClick={() => {
                     onChange(`${hour}:00`);
                     setIsOpen(false);
                   }}
                   className="py-2 text-[10px] font-black hover:bg-white dark:hover:bg-zinc-700 rounded-lg transition-all text-zinc-500 dark:text-zinc-400 flex flex-col items-center"
                 >
                   <span>{hour}:00</span>
                   <span className="text-[8px] opacity-50">{parseInt(hour) >= 12 ? 'PM' : 'AM'}</span>
                 </button>
               ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LoadingSpinner = ({ size = 16, className = "" }: { size?: number, className?: string }) => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
    className={className}
  >
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="3" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  </motion.div>
);

export default function AdminDashboard({ 
  profile: initialProfile, 
  subscription,
  refreshProfile 
}: { 
  profile: Profile | null, 
  subscription: Subscription | null,
  refreshProfile?: () => Promise<void> 
}) {
  const { t, language, dateLocale } = useLanguage();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  // Super Admins default to 'shops', others default to 'reservations', but since profile is not available immediately, we'll conditionally update it or set it inside a useEffect. Actually, setting it initially to 'reservations' is fine, we can update it in an effect once profile loads.

  const timeToMinutes = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedChatUser, setSelectedChatUser] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [legalType, setLegalType] = useState<'terms' | 'privacy' | 'cookies' | null>(null);
  const [confirmConfig, setConfirmConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    variant: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger'
  });
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [profile, setProfile] = useState<Profile | null>(initialProfile);
  const [activeTab, setActiveTab] = useState<'reservations' | 'customers' | 'slots' | 'services' | 'team' | 'shops' | 'settings' | 'roles' | 'analytics' | 'inventory' | 'commissions' | 'marketing' | 'staff_dashboard' | 'chat' | 'email_templates'>('reservations');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    operations: true,
    management: false,
    financials: false,
    marketing: false,
    system: false
  });

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Auto-expand section when tab changes
  useEffect(() => {
    const tabToSection: Record<string, string> = {
      reservations: 'operations',
      customers: 'operations',
      slots: 'operations',
      staff_dashboard: 'operations',
      services: 'management',
      inventory: 'management',
      team: 'management',
      shops: 'management',
      roles: 'management',
      analytics: 'financials',
      commissions: 'financials',
      marketing: 'marketing',
      email_templates: 'marketing',
      chat: 'system',
      settings: 'system'
    };

    const section = tabToSection[activeTab];
    if (section && !openSections[section]) {
      setOpenSections(prev => ({ ...prev, [section]: true }));
    }
  }, [activeTab]);

  const isSuperAdmin = profile?.role === 'super_admin';
  const isTestAccount = Boolean(profile?.email?.toLowerCase().includes('test') || 
                        profile?.email?.toLowerCase().includes('admin') || 
                        profile?.email?.toLowerCase() === 'devthines@gmail.com');
  const isShopAdmin = profile?.role === 'barber_admin' || isTestAccount;
  const isAdmin = isSuperAdmin || isShopAdmin;

  useEffect(() => {
    if (profile?.role === 'super_admin') {
      setActiveTab('shops');
    } else {
      setActiveTab('reservations');
    }
  }, [profile?.role]);
  const [allProfiles, setAllProfiles] = useState<Profile[]>([]);
  const [roleSearchQuery, setRoleSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [shops, setShops] = useState<BarberShop[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [newReservationsCount, setNewReservationsCount] = useState(0);
  const [allSubscriptions, setAllSubscriptions] = useState<Subscription[]>([]);
  const [expandedSettings, setExpandedSettings] = useState({
    general: true,
    location: true,
    hours: true,
    reminders: true,
    subscription: true
  });
  
  // Slot creation state
  const [selectedDate, setSelectedDate] = useState<Date>(startOfToday());
  const [newSlotTime, setNewSlotTime] = useState('09:00');
  const [generateStartTime, setGenerateStartTime] = useState('09:00');
  const [generateEndTime, setGenerateEndTime] = useState('17:00');
  const [generateInterval, setGenerateInterval] = useState(30);
  const [generateBreakStart, setGenerateBreakStart] = useState('12:00');
  const [generateBreakEnd, setGenerateBreakEnd] = useState('13:00');
  const [generateDays, setGenerateDays] = useState(1);
  const [generateEndDate, setGenerateEndDate] = useState<Date>(addDays(startOfToday(), 7));
  const [generateWorkingDays, setGenerateWorkingDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [generateServiceId, setGenerateServiceId] = useState<string>('');
  const [skipWeekends, setSkipWeekends] = useState(false);

  const WORKING_DAYS_LIST = [
    { value: 1, label: 'mon', short: 'M' },
    { value: 2, label: 'tue', short: 'T' },
    { value: 3, label: 'wed', short: 'W' },
    { value: 4, label: 'thu', short: 'T' },
    { value: 5, label: 'fri', short: 'F' },
    { value: 6, label: 'sat', short: 'S' },
    { value: 7, label: 'sun', short: 'S' },
  ];
  const [isGeneratingSlots, setIsGeneratingSlots] = useState(false);
  const [selectedBarberId, setSelectedBarberId] = useState<string>('');
  const [currentBarberId, setCurrentBarberId] = useState<string | null>(null);
  const [newSlotBarberId, setNewSlotBarberId] = useState<string>('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isAddingSlotModalOpen, setIsAddingSlotModalOpen] = useState(false);
  const [isFetchingSlots, setIsFetchingSlots] = useState(false);
  const [customCategories, setCustomCategories] = useState<any[]>([]);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState<{old: string, new: string} | null>(null);

  const [isCreatingNewCategoryInForm, setIsCreatingNewCategoryInForm] = useState(false);
  const [isCreatingNewCategoryInEditForm, setIsCreatingNewCategoryInEditForm] = useState(false);
  const [isSubmittingSlot, setIsSubmittingSlot] = useState(false);
  const [isSubmittingService, setIsSubmittingService] = useState(false);
  const [isSubmittingBarber, setIsSubmittingBarber] = useState(false);
  const [isSubmittingStaff, setIsSubmittingStaff] = useState(false);
  const [isSubmittingShop, setIsSubmittingShop] = useState(false);
  const [isSubmittingReservation, setIsSubmittingReservation] = useState(false);

  // Slot booking state
  const [slotToBook, setSlotToBook] = useState<Slot | null>(null);
  const [isBookSlotModalOpen, setIsBookSlotModalOpen] = useState(false);
  const [bookingServices, setBookingServices] = useState<string[]>([]);
  const [bookingCustomerName, setBookingCustomerName] = useState('Walk-in');
  const [bookingCustomerEmail, setBookingCustomerEmail] = useState('walkin@example.com');
  const [bookingCustomerPhone, setBookingCustomerPhone] = useState('+41 00 000 00 00');
  
  // New Booking State from Admin
  const [isAddingReservation, setIsAddingReservation] = useState(false);
  const [adminBookingBarberId, setAdminBookingBarberId] = useState('');
  const [adminBookingDate, setAdminBookingDate] = useState<Date>(startOfToday());
  const [adminBookingSlotId, setAdminBookingSlotId] = useState('');
  const [adminBookingServices, setAdminBookingServices] = useState<string[]>([]);
  const [adminAvailableSlots, setAdminAvailableSlots] = useState<Slot[]>([]);

  useEffect(() => {
    if (isAddingReservation && adminBookingBarberId && adminBookingDate && selectedShopId) {
      const fetchDaySlots = async () => {
        const dateStr = format(adminBookingDate, 'yyyy-MM-dd');
        const { data } = await supabase
          .from('slots')
          .select('*')
          .eq('barber_shop_id', selectedShopId)
          .eq('barber_id', adminBookingBarberId)
          .eq('date', dateStr)
          .order('time', { ascending: true });
        
        if (data) {
          setAdminAvailableSlots(data);
        }
      };
      fetchDaySlots();
    }
  }, [isAddingReservation, adminBookingBarberId, adminBookingDate, selectedShopId]);

  // Barber creation state
  const [isAddingBarber, setIsAddingBarber] = useState(false);
  const [isEditingBarber, setIsEditingBarber] = useState(false);
  const [isRestockingItem, setIsRestockingItem] = useState(false);
  const [isUsingItem, setIsUsingItem] = useState(false);
  const [selectedInventoryItem, setSelectedInventoryItem] = useState<any>(null);
  const [inventoryActionAmount, setInventoryActionAmount] = useState(1);
  const [isAddingStaffModalOpen, setIsAddingStaffModalOpen] = useState(false);
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffPassword, setNewStaffPassword] = useState('');
  const [newStaffConfirmPassword, setNewStaffConfirmPassword] = useState('');
  const [newStaffFullName, setNewStaffFullName] = useState('');
  const [editingBarber, setEditingBarber] = useState<Barber | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [barberPhotoFile, setBarberPhotoFile] = useState<File | null>(null);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [newBarber, setNewBarber] = useState({
    name: '',
    bio: '',
    photo_url: '',
    user_id: '',
    working_days: [1, 2, 3, 4, 5],
    start_time: '09:00',
    end_time: '18:00',
    break_start: '12:00',
    break_end: '13:00'
  });

  // Email Templates state
  interface EmailTemplate {
    id: string;
    scenario: 'booking_confirmation' | 'reminder' | 'password_reset' | 'welcome' | 'custom';
    name: string;
    subject: string;
    body: string;
  }
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [isEditingTemplate, setIsEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState<EmailTemplate>({
    id: '', scenario: 'custom', name: '', subject: '', body: ''
  });
  const [resendApiKey, setResendApiKey] = useState('');

  // Service creation state
  const [isAddingService, setIsAddingService] = useState(false);
  const [isAddingPromoCode, setIsAddingPromoCode] = useState(false);
  const [isManagingPlatformPromos, setIsManagingPlatformPromos] = useState(false);
  const [isConfiguringRewards, setIsConfiguringRewards] = useState(false);
  const [referralRewardAmount, setReferralRewardAmount] = useState(50);
  const [newPromoCode, setNewPromoCode] = useState<Partial<PromoCode>>({
    code: '',
    discount_type: 'percent',
    value: 10,
    active: true
  });
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    price: 0,
    duration: 30,
    category: t('admin.haircut')
  });

  // Shop creation state
  const [isAddingShop, setIsAddingShop] = useState(false);
  const [newShop, setNewShop] = useState({ 
    name: '', 
    address: '',
    phone: '',
    email: '',
    opening_time: '09:00',
    closing_time: '18:00',
    reminder_enabled: true,
    reminder_hours_before: 24,
    reminder_type: 'email' as 'email' | 'sms' | 'both'
  });

  // Search and Filter state
  const [reservationSearch, setReservationSearch] = useState('');
  const [inventorySearch, setInventorySearch] = useState('');
  const [reservationFilter, setReservationFilter] = useState<'all' | 'pending' | 'confirmed' | 'cancelled'>('all');
  const [reservationDateFilter, setReservationDateFilter] = useState('');
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [isEditingReservation, setIsEditingReservation] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<string | null>(null);
  const [teamSearch, setTeamSearch] = useState('');
  const [teamFilter, setTeamFilter] = useState<'all' | 'super_admin' | 'barber_admin' | 'barber_staff' | 'customer'>('all');
  const [teamShopFilter, setTeamShopFilter] = useState<string>('all');
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState('all');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [inventory, setInventory] = useState<any[]>([]);
  const [promoCodes, setPromoCodes] = useState<any[]>([]);
  const [commissions, setCommissions] = useState<any[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const shopAnalytics = useMemo(() => {
    if (isSuperAdmin) return null;

    const hasData = reservations.length > 0;
    
    // Revenue for last 30 days
    const last30Days = eachDayOfInterval({
      start: subDays(new Date(), 29),
      end: new Date()
    });

    const revenueData = last30Days.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayReservations = reservations.filter(r => r.status === 'confirmed' && format(new Date(r.created_at), 'yyyy-MM-dd') === dateStr);
      const dayRevenue = dayReservations.reduce((sum: number, r) => sum + Number(r.total_price || 0), 0);
      const dayBookings = dayReservations.length;
      
      return {
        date: format(date, 'MMM d'),
        revenue: dayRevenue,
        bookings: dayBookings
      };
    });

    // Bookings by Day of Week
    const dayOfWeekData = [
      { name: t('common.sunday') || 'Sunday', bookings: !hasData ? 12 : 0, key: 0 },
      { name: t('common.monday') || 'Monday', bookings: !hasData ? 25 : 0, key: 1 },
      { name: t('common.tuesday') || 'Tuesday', bookings: !hasData ? 30 : 0, key: 2 },
      { name: t('common.wednesday') || 'Wednesday', bookings: !hasData ? 28 : 0, key: 3 },
      { name: t('common.thursday') || 'Thursday', bookings: !hasData ? 35 : 0, key: 4 },
      { name: t('common.friday') || 'Friday', bookings: !hasData ? 45 : 0, key: 5 },
      { name: t('common.saturday') || 'Saturday', bookings: !hasData ? 50 : 0, key: 6 },
    ];

    if (hasData) {
      reservations.forEach(r => {
        const day = new Date(r.created_at).getDay();
        if (dayOfWeekData[day]) {
          dayOfWeekData[day].bookings++;
        }
      });
    }

    // Service Popularity
    const realServicePopularity = services.map(service => {
      const count = reservations.filter(r => r.services?.some((s: any) => s.id === service.id)).length;
      return {
        name: service.name,
        value: count
      };
    }).filter(s => s.value > 0).sort((a, b) => b.value - a.value).slice(0, 5);

    const sampleServicePopularity = [
      { name: 'Classic Cut', value: 45 },
      { name: 'Beard Groom', value: 30 },
      { name: 'Hot Shave', value: 15 },
      { name: 'Full Service', value: 10 }
    ];

    // Barber Performance
    const realBarberPopularity = barbers.map(barber => {
      const barberReservations = reservations.filter(r => r.barber_id === barber.id);
      const bookingCount = barberReservations.length;
      const confirmedReservations = barberReservations.filter(r => r.status === 'confirmed');
      const revenue = confirmedReservations.reduce((sum, r) => sum + Number(r.total_price || 0), 0);
      
      return {
        name: barber.name,
        bookings: bookingCount,
        revenue: revenue,
        avgBookingValue: bookingCount > 0 ? revenue / bookingCount : 0
      };
    }).filter(b => b.bookings > 0).sort((a, b) => b.bookings - a.bookings).slice(0, 5);

    const sampleBarberPopularity = [
      { name: 'Alex Rivera', bookings: 85, revenue: 2550, avgBookingValue: 30 },
      { name: 'Sam Chen', bookings: 65, revenue: 1950, avgBookingValue: 30 },
      { name: 'Jordan Taylor', bookings: 45, revenue: 1350, avgBookingValue: 30 }
    ];

    return {
      revenueData,
      dayOfWeekData,
      servicePopularity: realServicePopularity.length > 0 ? realServicePopularity : sampleServicePopularity,
      barberPopularity: realBarberPopularity.length > 0 ? realBarberPopularity : sampleBarberPopularity,
      totalRevenue: reservations.filter(r => r.status === 'confirmed').reduce((sum, r) => sum + Number(r.total_price || 0), 0),
      totalBookings: reservations.length,
      avgRating: reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0,
      activeCustomers: new Set(reservations.map(r => r.customer_email)).size,
      hasData
    };
  }, [reservations, reviews, services, barbers, isSuperAdmin, t]);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', quantity: 0, min_stock_level: 0, unit: 'pcs' });

  useEffect(() => {
    if (initialProfile) {
      setProfile(initialProfile);
      if (initialProfile.role !== 'super_admin') {
        setSelectedShopId(initialProfile.barber_shop_id || null);
      }
    }
  }, [initialProfile]);

  useEffect(() => {
    if (profile) {
      fetchData();
    }
  }, [profile, selectedShopId]);

  useEffect(() => {
    if (!profile) return;

    const channel = supabase
      .channel('new-reservations')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'reservations',
          filter: selectedShopId ? `barber_shop_id=eq.${selectedShopId}` : undefined,
        },
        (payload) => {
          console.log('New reservation received!', payload);
          if (profile?.role === 'super_admin') return; // Do not notify Super Admins
          
          if (activeTab !== 'reservations') {
            setNewReservationsCount(prev => prev + 1);
          }
          
          // Play sound alert
          const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
          audio.play().catch(e => console.log('Audio play failed:', e));
          
          toast.info(t('admin.newReservation'), {
            description: t('admin.newBookingMade'),
            action: {
              label: t('common.view'),
              onClick: () => setActiveTab('reservations')
            }
          });
          
          fetchData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile, selectedShopId, activeTab]);

  useEffect(() => {
    if (activeTab === 'reservations') {
      setNewReservationsCount(0);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!profile) return;

    let isMounted = true;
    
    const fetchMessages = async () => {
      try {
        const { data, error } = await supabase.from('messages').select('*').order('created_at', { ascending: true });
        if (data && isMounted) setMessages(data);
      } catch (e) {
        // Table might not exist yet
      }
    };
    
    fetchMessages();
    
    const msgChannel = supabase
      .channel('public:messages')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, payload => {
        fetchMessages();
      })
      .subscribe();
      
    return () => {
      isMounted = false;
      supabase.removeChannel(msgChannel);
    };
  }, [profile]);

  useEffect(() => {
    if (activeTab === 'slots' && selectedShopId) {
      fetchSlotsForMonth();
    }
  }, [activeTab, selectedShopId, currentMonth, selectedBarberId]);

  useEffect(() => {
    if (newSlotBarberId) {
      const barber = barbers.find(b => b.id === newSlotBarberId);
      if (barber) {
        if (barber.start_time) setGenerateStartTime(barber.start_time.substring(0, 5));
        if (barber.end_time) setGenerateEndTime(barber.end_time.substring(0, 5));
        if (barber.break_start) setGenerateBreakStart(barber.break_start.substring(0, 5));
        if (barber.break_end) setGenerateBreakEnd(barber.break_end.substring(0, 5));
        if (barber.working_days) setGenerateWorkingDays(barber.working_days);
      }
    }
  }, [newSlotBarberId, barbers]);

  useEffect(() => {
    if (selectedBarberId) {
      setNewSlotBarberId(selectedBarberId);
    }
  }, [selectedBarberId]);


  const fetchSlotsForMonth = async () => {
    if (!selectedShopId) return;
    setIsFetchingSlots(true);
    try {
      const start = format(startOfWeek(startOfMonth(currentMonth)), 'yyyy-MM-dd');
      const end = format(endOfWeek(endOfMonth(currentMonth)), 'yyyy-MM-dd');

      let query = supabase
        .from('slots')
        .select('*')
        .eq('barber_shop_id', selectedShopId)
        .gte('date', start)
        .lte('date', end)
        .order('date', { ascending: true })
        .order('time', { ascending: true });

      if (selectedBarberId) {
        query = query.eq('barber_id', selectedBarberId);
      }

      const { data, error } = await query;

      if (error) throw error;
      if (data) {
        setSlots(data);
      }
    } catch (error: any) {
      console.error('Error fetching slots:', error.message);
    } finally {
      setIsFetchingSlots(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let currentShopId = selectedShopId;

      // Fetch shops
      if (profile?.role === 'super_admin') {
        const { data: shopsData } = await supabase.from('barber_shops').select('*').order('name');
        if (shopsData) {
          setShops(shopsData);
          if (!currentShopId && shopsData.length > 0) {
            currentShopId = shopsData[0].id;
            setSelectedShopId(currentShopId);
          }
        }
      } else if (profile?.barber_shop_id) {
        const { data: shopData } = await supabase.from('barber_shops').select('*').eq('id', profile.barber_shop_id).single();
        if (shopData) {
          setShops([shopData]);
          currentShopId = shopData.id;
          setSelectedShopId(shopData.id);
        }
      }

      let resQuery = supabase.from('reservations').select('*, reservation_services(services(*)), reservation_slots(slots(*)), barbers(*)').order('created_at', { ascending: false });
      let slotQuery = supabase.from('slots').select('*').order('date', { ascending: true }).order('time', { ascending: true });
      let serQuery = supabase.from('services').select('*').order('name', { ascending: true });
      let profQuery = supabase.from('profiles').select('*').order('created_at', { ascending: false });
      let barberQuery = supabase.from('barbers').select('*').order('name', { ascending: true });

      if (currentShopId) {
        resQuery = resQuery.eq('barber_shop_id', currentShopId);
        slotQuery = slotQuery.eq('barber_shop_id', currentShopId);
        serQuery = serQuery.eq('barber_shop_id', currentShopId);
        // Super admins should see all profiles for global role management
        if (profile?.role !== 'super_admin') {
          profQuery = profQuery.eq('barber_shop_id', currentShopId);
        }
        barberQuery = barberQuery.eq('barber_shop_id', currentShopId);
      }

      let invQuery = supabase.from('inventory_items').select('*');
      let promoQuery = supabase.from('promo_codes').select('*');
      let reviewQuery = supabase.from('reviews').select('*, barbers!inner(barber_shop_id)');

      if (currentShopId) {
        invQuery = invQuery.eq('shop_id', currentShopId);
        promoQuery = promoQuery.eq('shop_id', currentShopId);
        reviewQuery = reviewQuery.eq('barbers.barber_shop_id', currentShopId);
      }

      const [resData, slotData, serData, profData, barberData, invData, promoData, commData, reviewData, subData] = await Promise.all([
        resQuery, slotQuery, serQuery, profQuery, barberQuery,
        invQuery,
        promoQuery,
        supabase.from('commissions').select('*, barbers(name), reservations(*)').order('created_at', { ascending: false }),
        reviewQuery,
        supabase.from('subscriptions').select('*').order('created_at', { ascending: false })
      ]);

      // Try fetching categories safely
      try {
        let catQuery = supabase.from('service_categories').select('*').order('name', { ascending: true });
        if (currentShopId) catQuery = catQuery.eq('barber_shop_id', currentShopId);
        const { data: catData } = await catQuery;
        if (catData) setCustomCategories(catData);
      } catch (err) {
        console.warn('service_categories table may not exist yet.');
      }

      if (resData.error) throw resData.error;
      if (slotData.error) throw slotData.error;
      if (serData.error) throw serData.error;
      if (barberData.error) throw barberData.error;
      if (reviewData.error) throw reviewData.error;
      if (subData.error) throw subData.error;

      setInventory(invData.data || []);
      setPromoCodes(promoData.data || []);
      setCommissions(commData.data || []);
      setReviews(reviewData.data || []);
      setAllSubscriptions(subData.data || []);

      // Transform reservations to include nested data
      const transformedReservations = resData.data.map((res: any) => ({
        ...res,
        services: res.reservation_services?.map((rs: any) => rs.services) || [],
        slots: res.reservation_slots?.map((rs: any) => rs.slots) || [],
        barber: res.barbers
      }));

      setReservations(transformedReservations);
      setSlots(slotData.data);
      setServices(serData.data);
      setBarbers(barberData.data || []);

      // Find current user's barber ID if they are staff
      if (profile?.role === 'barber_staff' && barberData.data) {
        const myBarber = barberData.data.find((b: any) => b.user_id === profile.id);
        if (myBarber) {
          setCurrentBarberId(myBarber.id);
          setSelectedBarberId(myBarber.id);
        }
      }
      
      if (profile?.role === 'super_admin') {
        const { data: allProf } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
        if (allProf) setAllProfiles(allProf);
      } else {
        if (profData.data) setAllProfiles(profData.data);
      }
      
    } catch (error: any) {
      toast.error('Failed to fetch data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string, shopId?: string | null) => {
    setConfirmConfig({
      isOpen: true,
      title: t('admin.updateRole'),
      message: t('admin.confirmUpdateRole'),
      variant: 'warning',
      onConfirm: async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error("No active session");

          const response = await fetch('/api/admin/update-role', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId,
              newRole,
              shopId,
              adminToken: session.access_token
            })
          });

          const result = await response.json();
          if (!response.ok) throw new Error(result.error || "Failed to update role");
          
          const updates: any = { role: newRole };
          if (shopId !== undefined) updates.barber_shop_id = shopId;
          
          const roleLabel = newRole === 'super_admin' ? t('admin.superAdmin') || 'Super Admin' :
                           newRole === 'barber_admin' ? t('admin.barberAdmin') || 'Shop Admin' :
                           newRole === 'barber_staff' ? t('admin.barberStaff') || 'Barber Staff' :
                           t('admin.customerRole') || 'Customer';
          
          setAllProfiles(allProfiles.map(p => p.id === userId ? { ...p, ...updates } : p));
          toast.success(t('admin.roleUpdated') + ' ' + roleLabel);
        } catch (error: any) {
          toast.error(t('admin.failedUpdateRole') + ': ' + error.message);
        }
      }
    });
  };

  const handleUpdateSubscription = async (shopId: string, action: 'update_plan' | 'cancel', planId?: string) => {
    const isCancel = action === 'cancel';
    setConfirmConfig({
      isOpen: true,
      title: isCancel ? t('admin.cancelShopSubscription') || 'Cancel Subscription' : t('admin.manualPlanUpdate') || 'Manual Plan Update',
      message: isCancel ? t('admin.confirmManualCancel') || 'Are you sure?' : t('admin.confirmManualUpdate') || 'Are you sure?',
      variant: isCancel ? 'danger' : 'warning',
      onConfirm: async () => {
        try {
          if (isCancel) {
            const { error } = await supabase
              .from('subscriptions')
              .update({
                status: 'canceled',
                canceled_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })
              .eq('shop_id', shopId);
              
            if (error) throw error;
          } else {
            // Check if subscription exists
            const { data: existingSub } = await supabase
              .from('subscriptions')
              .select('id')
              .eq('shop_id', shopId)
              .maybeSingle();

            if (existingSub) {
              const { error } = await supabase
                .from('subscriptions')
                .update({
                  plan_id: planId,
                  status: 'active',
                  updated_at: new Date().toISOString(),
                  current_period_start: new Date().toISOString(),
                  // Add roughly 30 or 365 days depending on the plan type
                  current_period_end: new Date(Date.now() + (planId?.includes('annual') ? 365 : 30) * 24 * 60 * 60 * 1000).toISOString()
                })
                .eq('shop_id', shopId);
                
              if (error) throw error;
            } else {
              // Create new manual subscription record
              const { error } = await supabase
                .from('subscriptions')
                .insert({
                  shop_id: shopId,
                  plan_id: planId,
                  status: 'active',
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  current_period_start: new Date().toISOString(),
                  current_period_end: new Date(Date.now() + (planId?.includes('annual') ? 365 : 30) * 24 * 60 * 60 * 1000).toISOString()
                });
                
              if (error) throw error;
            }
          }
          
          toast.success(t('admin.planUpdated') || "Updated successfully");
          fetchData(); // Refresh data
        } catch (error: any) {
          toast.error(error.message);
        }
      }
    });
  };

  const handleCreateCheckoutSession = async (shopId: string, planId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");
      
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: profile?.id,
          email: profile?.email,
          shopId: shopId,
          planId: planId,
          agreedToTerms: true
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to create checkout session");
      
      if (result.url) {
        window.open(result.url, '_blank');
      } else {
        throw new Error("No Stripe URL returned");
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleAddSlot = async () => {
    if (!selectedShopId) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }
    setIsSubmittingSlot(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Check if slot already exists for this barber
      const exists = slots.find(s => 
        s.date === dateStr && 
        s.time === newSlotTime && 
        s.barber_shop_id === selectedShopId &&
        s.barber_id === newSlotBarberId
      );
      if (exists) {
        toast.error(t('admin.slotExists'));
        return;
      }

      if (!newSlotBarberId) {
        toast.error(t('admin.selectBarberFirst'));
        return;
      }

      const { data, error } = await supabase
        .from('slots')
        .insert([{ 
          barber_shop_id: selectedShopId, 
          date: dateStr, 
          time: newSlotTime, 
          is_available: true,
          barber_id: newSlotBarberId
        }])
        .select();

      if (error) throw error;

      setSlots([...slots, data[0]]);
      toast.success(t('admin.slotAdded'));

      // Increment time by 30 mins for easier bulk adding
      try {
        const [hours, minutes] = newSlotTime.split(':').map(Number);
        const date = new Date();
        date.setHours(hours);
        date.setMinutes(minutes + 30);
        setNewSlotTime(format(date, 'HH:mm'));
      } catch (e) {
        console.error('Failed to increment time:', e);
      }
    } catch (error: any) {
      toast.error(t('admin.failedAddSlot') + ': ' + error.message);
    } finally {
      setIsSubmittingSlot(false);
    }
  };

  const handleGenerateSlots = async () => {
    if (!selectedShopId) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }
    if (!newSlotBarberId) {
      toast.error(t('admin.selectBarberFirst'));
      return;
    }
    
    setIsGeneratingSlots(true);
    try {
      const barber = barbers.find(b => b.id === newSlotBarberId);
      if (!barber) throw new Error('Barber not found');

      const newSlots = [];
      const dateStrStart = format(selectedDate, 'yyyy-MM-dd');
      const dateStrEnd = format(generateEndDate, 'yyyy-MM-dd');
      
      const totalDays = Math.max(1, Math.ceil((generateEndDate.getTime() - selectedDate.getTime()) / (1000 * 60 * 60 * 24)) + 1);

      // Fetch ALL existing slots for this barber in the range to avoid duplicates
      const { data: existingSlots, error: fetchError } = await supabase
        .from('slots')
        .select('date, time')
        .eq('barber_id', newSlotBarberId)
        .gte('date', dateStrStart)
        .lte('date', dateStrEnd);

      if (fetchError) throw fetchError;

      const existingSlotMap = new Set(existingSlots?.map(s => `${s.date}_${s.time.substring(0, 5)}`));
      
      // Loop through each day
      for (let dayOffset = 0; dayOffset < totalDays; dayOffset++) {
        const currentDate = addDays(selectedDate, dayOffset);
        if (isBefore(generateEndDate, currentDate) && !isSameDay(generateEndDate, currentDate)) break;
        
        const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const dayOfWeekForMatch = dayOfWeek === 0 ? 7 : dayOfWeek; // Monday=1, ..., Sunday=7
        
        // 1. Consider Custom Working Days
        if (!generateWorkingDays.includes(dayOfWeekForMatch)) {
          continue;
        }

        // 2. Skip Weekends (if explicitly requested)
        if (skipWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
          continue;
        }

        const dateStr = format(currentDate, 'yyyy-MM-dd');
        
        // 3. Use Custom Working Hours
        const startTime = generateStartTime;
        const endTime = generateEndTime;
        const breakStart = generateBreakStart;
        const breakEnd = generateBreakEnd;

        const [startH, startM] = startTime.split(':').map(Number);
        const [endH, endM] = endTime.split(':').map(Number);
        const [breakStartH, breakStartM] = breakStart.split(':').map(Number);
        const [breakEndH, breakEndM] = breakEnd.split(':').map(Number);
        
        const startTotalMins = startH * 60 + startM;
        const endTotalMins = endH * 60 + endM;
        const breakStartTotalMins = breakStartH * 60 + breakStartM;
        const breakEndTotalMins = breakEndH * 60 + breakEndM;
        
        for (let currentMins = startTotalMins; currentMins < endTotalMins; currentMins += generateInterval) {
          const slotEndMins = currentMins + generateInterval;

          // 4. Accurate Break Time Check (Overlap check)
          // A slot overlaps with a break if it starts before the break ends AND ends after the break starts
          const overlapsWithBreak = currentMins < breakEndTotalMins && slotEndMins > breakStartTotalMins;
          if (overlapsWithBreak) {
            continue;
          }
          
          const h = Math.floor(currentMins / 60).toString().padStart(2, '0');
          const m = (currentMins % 60).toString().padStart(2, '0');
          const timeStr = `${h}:${m}`;
          
          // 5. Check against pre-fetched existing slots
          if (!existingSlotMap.has(`${dateStr}_${timeStr}`)) {
            newSlots.push({
              barber_shop_id: selectedShopId, 
              date: dateStr, 
              time: timeStr, 
              is_available: true,
              barber_id: newSlotBarberId
            });
          }
        }
      }
      
      if (newSlots.length === 0) {
        toast.info(t('admin.slotExists'));
        return;
      }
      
      // Chunk the inserts to avoid potential limits
      const chunkSize = 100;
      const chunks = [];
      for (let i = 0; i < newSlots.length; i += chunkSize) {
        chunks.push(newSlots.slice(i, i + chunkSize));
      }

      let insertedSlots: any[] = [];
      for (const chunk of chunks) {
        const { data: chunkData, error: chunkError } = await supabase
          .from('slots')
          .insert(chunk)
          .select();
        
        if (chunkError) throw chunkError;
        if (chunkData) insertedSlots = [...insertedSlots, ...chunkData];
      }

      // Update local state if the generated slots are within the current month view
      const startOfView = format(startOfWeek(startOfMonth(currentMonth)), 'yyyy-MM-dd');
      const endOfView = format(endOfWeek(endOfMonth(currentMonth)), 'yyyy-MM-dd');
      
      const visibleNewSlots = insertedSlots.filter(s => s.date >= startOfView && s.date <= endOfView);
      setSlots([...slots, ...visibleNewSlots]);
      
      toast.success(t('admin.slotsGenerated') || `Successfully generated ${newSlots.length} slots`);
    } catch (error: any) {
      toast.error(t('admin.failedGenerateSlots') + ': ' + error.message);
    } finally {
      setIsGeneratingSlots(false);
    }
  };

  const handleClearDayAvailability = async (barberId: string) => {
    if (!selectedShopId) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }
    
    setConfirmConfig({
      isOpen: true,
      title: t('admin.clearAvailability'),
      message: t('admin.confirmClearAvailability'),
      variant: 'danger',
      onConfirm: async () => {
        setIsFetchingSlots(true);
        try {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          const { data: slotsToDelete, error: fetchError } = await supabase
            .from('slots')
            .select('id')
            .eq('barber_shop_id', selectedShopId)
            .eq('barber_id', barberId)
            .eq('date', dateStr)
            .eq('is_available', true);
            
          if (fetchError) throw fetchError;
          if (!slotsToDelete || slotsToDelete.length === 0) {
            toast.info(t('admin.noSlotsToDelete') || 'No available slots to delete');
            return;
          }
          
          const slotIds = slotsToDelete.map(s => s.id);
          const { error: deleteError } = await supabase
            .from('slots')
            .delete()
            .in('id', slotIds);
            
          if (deleteError) throw deleteError;
          
          setSlots(slots.filter(s => !slotIds.includes(s.id)));
          toast.success(t('admin.availabilityCleared'));
        } catch (error: any) {
          toast.error(error.message);
        } finally {
          setIsFetchingSlots(false);
        }
      }
    });
  };

  const handleClearAvailability = async () => {
    if (!selectedShopId) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }
    if (!newSlotBarberId) {
      toast.error(t('admin.selectBarberFirst'));
      return;
    }
    
    setConfirmConfig({
      isOpen: true,
      title: t('admin.clearAvailability'),
      message: t('admin.confirmClearAvailability'),
      variant: 'danger',
      onConfirm: async () => {
        setIsGeneratingSlots(true);
        try {
          const dateStrStart = format(selectedDate, 'yyyy-MM-dd');
          const dateStrEnd = format(generateEndDate, 'yyyy-MM-dd');
          
          // Find all available slots for this range and barber
          const { data: slotsToDelete, error: fetchError } = await supabase
            .from('slots')
            .select('id')
            .eq('barber_shop_id', selectedShopId)
            .eq('barber_id', newSlotBarberId)
            .eq('is_available', true)
            .gte('date', dateStrStart)
            .lte('date', dateStrEnd);
          
          if (fetchError) throw fetchError;
          
          if (!slotsToDelete || slotsToDelete.length === 0) {
            toast.info('No available slots to clear in this range');
            return;
          }
          
          const slotIds = slotsToDelete.map(s => s.id);
          
          // Chunk the deletes
          const chunkSize = 100;
          for (let i = 0; i < slotIds.length; i += chunkSize) {
            const chunk = slotIds.slice(i, i + chunkSize);
            const { error: deleteError } = await supabase
              .from('slots')
              .delete()
              .in('id', chunk);
            
            if (deleteError) throw deleteError;
          }
          
          // Update local state
          setSlots(slots.filter(s => !slotIds.includes(s.id)));
          toast.success(t('admin.availabilityCleared') || `Successfully cleared ${slotIds.length} available slots`);
        } catch (error: any) {
          toast.error(t('admin.failedClearAvailability') + ': ' + error.message);
        } finally {
          setIsGeneratingSlots(false);
        }
      }
    });
  };
  
  const handleAddStaffAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShopId) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }

    if (newStaffPassword !== newStaffConfirmPassword) {
      toast.error(t('auth.passwordsDoNotMatch') || 'Passwords do not match');
      return;
    }

    setIsSubmittingStaff(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("No active session");

      const response = await fetch('/api/admin/create-staff', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: newStaffEmail,
          password: newStaffPassword,
          fullName: newStaffFullName,
          shopId: selectedShopId,
          adminToken: session.access_token
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to create staff account");

      toast.success(t('admin.staffAccountCreated'));
      setIsAddingStaffModalOpen(false);
      setNewStaffEmail('');
      setNewStaffPassword('');
      setNewStaffConfirmPassword('');
      setNewStaffFullName('');
      
      // Refresh profiles to show the new user
      fetchData();
    } catch (error: any) {
      toast.error(t('admin.failedCreateStaff') + ': ' + error.message);
    } finally {
      setIsSubmittingStaff(false);
    }
  };

  const validateBarberInput = (barber: Partial<Barber> | typeof newBarber) => {
    if (!barber.name?.trim()) {
      toast.error(t('admin.nameRequired') || 'Name is required');
      return false;
    }
    if (!barber.working_days || barber.working_days.length === 0) {
      toast.error('At least one working day must be selected');
      return false;
    }
    
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    
    if (!barber.start_time || !timeRegex.test(barber.start_time)) {
      toast.error('Valid start time required (HH:MM)');
      return false;
    }
    if (!barber.end_time || !timeRegex.test(barber.end_time)) {
      toast.error('Valid end time required (HH:MM)');
      return false;
    }
    
    if (timeToMinutes(barber.start_time) >= timeToMinutes(barber.end_time)) {
      toast.error('Start time must be before end time');
      return false;
    }

    if (!barber.break_start || !timeRegex.test(barber.break_start)) {
      toast.error('Valid break start time required (HH:MM)');
      return false;
    }
    if (!barber.break_end || !timeRegex.test(barber.break_end)) {
      toast.error('Valid break end time required (HH:MM)');
      return false;
    }
    
    const breakStartMin = timeToMinutes(barber.break_start);
    const breakEndMin = timeToMinutes(barber.break_end);
    
    if (breakStartMin >= breakEndMin) {
      toast.error('Break start time must be before break end time');
      return false;
    }
    if (breakStartMin < timeToMinutes(barber.start_time) || breakEndMin > timeToMinutes(barber.end_time)) {
      toast.error('Break times must fall within the working hours');
      return false;
    }
    
    return true;
  };

  const handleAddBarber = async () => {
    if (!selectedShopId) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }
    
    if (!validateBarberInput(newBarber)) {
      return;
    }

    setIsSubmittingBarber(true);
    try {
      setIsUploading(true);
      setUploadProgress(0);
      let photoUrl = newBarber.photo_url;

      if (barberPhotoFile) {
        setUploadProgress(10);
        const fileExt = barberPhotoFile.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `barber-photos/${fileName}`;

        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 200);

        const { error: uploadError } = await supabase.storage
          .from('barber-photos')
          .upload(filePath, barberPhotoFile);

        clearInterval(progressInterval);
        setUploadProgress(100);

        if (uploadError) {
          console.error("Storage upload error:", uploadError);
          throw new Error("Storage Upload Failed: " + uploadError.message);
        }

        const { data: { publicUrl } } = supabase.storage
          .from('barber-photos')
          .getPublicUrl(filePath);

        photoUrl = publicUrl;
      }

      const { data, error } = await supabase
        .from('barbers')
        .insert([{ 
          ...newBarber, 
          photo_url: photoUrl, 
          barber_shop_id: selectedShopId,
          user_id: newBarber.user_id || null,
          working_days: newBarber.working_days,
          start_time: newBarber.start_time,
          end_time: newBarber.end_time,
          break_start: newBarber.break_start,
          break_end: newBarber.break_end
        }])
        .select();

      if (error) throw error;
      if (data) {
        setBarbers([...barbers, data[0]]);
        setIsAddingBarber(false);
        setNewBarber({
          name: '',
          bio: '',
          photo_url: '',
          user_id: '',
          working_days: [1, 2, 3, 4, 5],
          start_time: '09:00',
          end_time: '18:00',
          break_start: '12:00',
          break_end: '13:00'
        });
        setBarberPhotoFile(null);
        setUploadProgress(0);
        toast.success(t('admin.barberAdded'));
      }
    } catch (error: any) {
      toast.error(t('admin.failedAddBarber') + ': ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setIsSubmittingBarber(false);
    }
  };

  const handleUpdateBarber = async () => {
    if (!editingBarber) return;
    
    if (!validateBarberInput(editingBarber)) {
      return;
    }

    setIsSubmittingBarber(true);
    try {
      setIsUploading(true);
      setUploadProgress(0);
      let photoUrl = editingBarber.photo_url;

      if (barberPhotoFile) {
        setUploadProgress(10);
        const fileExt = barberPhotoFile.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `barber-photos/${fileName}`;

        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 200);

        const { error: uploadError } = await supabase.storage
          .from('barber-photos')
          .upload(filePath, barberPhotoFile);

        clearInterval(progressInterval);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('barber-photos')
          .getPublicUrl(filePath);

        photoUrl = publicUrl;
        setUploadProgress(100);
      }

      const { error } = await supabase
        .from('barbers')
        .update({
          name: editingBarber.name,
          bio: editingBarber.bio,
          photo_url: photoUrl,
          portfolio_urls: editingBarber.portfolio_urls || [],
          user_id: editingBarber.user_id || null,
          working_days: editingBarber.working_days,
          start_time: editingBarber.start_time,
          end_time: editingBarber.end_time,
          break_start: editingBarber.break_start,
          break_end: editingBarber.break_end
        })
        .eq('id', editingBarber.id);

      if (error) throw error;

      setBarbers(barbers.map(b => b.id === editingBarber.id ? { ...editingBarber, photo_url: photoUrl } : b));
      setEditingBarber(null);
      setBarberPhotoFile(null);
      toast.success(t('admin.barberUpdated'));
    } catch (error: any) {
      toast.error(t('admin.failedUpdateBarber') + ': ' + error.message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setIsSubmittingBarber(false);
    }
  };

  const handleDeleteBarber = async (id: string) => {
    if (!id || id === 'undefined') {
      toast.error(t('admin.failedDeleteBarber') + ': Invalid Barber ID');
      return;
    }
    setConfirmConfig({
      isOpen: true,
      title: t('admin.deleteBarber'),
      message: t('admin.confirmDeleteBarber'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          // First, check if there are any upcoming reservations for this barber
          const { data: upcomingReservations, error: resCheckError } = await supabase
            .from('reservations')
            .select('id')
            .eq('barber_id', id)
            .eq('status', 'confirmed')
            .limit(1);

          if (resCheckError) throw resCheckError;

          if (upcomingReservations && upcomingReservations.length > 0) {
            toast.error(t('admin.failedDeleteBarber') + ': ' + (t('admin.hasUpcomingReservations') || 'Barber has active reservations. Cancel them first.'));
            return;
          }

          // Delete slots first to avoid FK constraint errors
          const { error: slotsError } = await supabase
            .from('slots')
            .delete()
            .eq('barber_id', id);

          if (slotsError) throw slotsError;

          // Nullify barber_id in past reservations to avoid FK constraint errors
          const { error: updateResError } = await supabase
            .from('reservations')
            .update({ barber_id: null })
            .eq('barber_id', id);

          if (updateResError) {
            console.error('Failed to nullify barber_id in reservations:', updateResError);
            // We don't necessarily throw here if we want to try deleting the barber anyway, 
            // but usually this is required for the delete to succeed.
            throw updateResError;
          }

          const { error } = await supabase
            .from('barbers')
            .delete()
            .eq('id', id);

          if (error) throw error;
          setBarbers(barbers.filter(b => b.id !== id));
          toast.success(t('admin.barberDeleted'));
        } catch (error: any) {
          toast.error(t('admin.failedDeleteBarber') + ': ' + error.message);
        }
      }
    });
  };

  const handleDeleteSlot = async (id: string) => {
    setSlotToDelete(id);
  };

  const handleBookSlot = async () => {
    if (!slotToBook || bookingServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    setLoading(true);
    try {
      const selectedServiceObjs = services.filter(s => bookingServices.includes(s.id));
      const totalPrice = selectedServiceObjs.reduce((sum, s) => sum + s.price, 0);
      const totalDuration = selectedServiceObjs.reduce((sum, s) => sum + Number(s.duration), 0);

      const shop = shops.find(s => s.id === selectedShopId);
      const closingTime = shop?.closing_time || '18:00';

      // Find consumed slots
      const barberSlots = slots
        .filter(s => s.barber_id === slotToBook.barber_id && s.date === slotToBook.date)
        .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

      const consumedSlots: Slot[] = [];
      let accumulated = 0;
      const startIndex = barberSlots.findIndex(s => s.id === slotToBook.id);
      
      if (startIndex !== -1) {
        for (let j = startIndex; j < barberSlots.length; j++) {
          consumedSlots.push(barberSlots[j]);
          const currentSlotMinutes = timeToMinutes(barberSlots[j].time);
          const nextSlotMinutes = j + 1 < barberSlots.length 
            ? timeToMinutes(barberSlots[j + 1].time) 
            : timeToMinutes(closingTime);
            
          accumulated += (nextSlotMinutes - currentSlotMinutes);
          if (accumulated >= totalDuration) break;
        }
      }

      // Check if all consumed slots are available
      const unavailableSlots = consumedSlots.filter(s => !s.is_available && s.id !== slotToBook.id);
      if (unavailableSlots.length > 0) {
        throw new Error('Some slots required for this duration are already booked');
      }

      const { data: resData, error: resError } = await supabase
        .from('reservations')
        .insert([{
          barber_shop_id: selectedShopId,
          barber_id: slotToBook.barber_id,
          customer_name: bookingCustomerName,
          customer_email: bookingCustomerEmail,
          customer_phone: bookingCustomerPhone,
          total_price: totalPrice,
          status: 'confirmed'
        }])
        .select();

      if (resError) throw resError;

      const reservation = resData[0];

      const serviceLinks = bookingServices.map(serviceId => ({
        reservation_id: reservation.id,
        service_id: serviceId
      }));
      const { error: linkError } = await supabase.from('reservation_services').insert(serviceLinks);
      if (linkError) throw linkError;

      const slotLinks = consumedSlots.map(s => ({
        reservation_id: reservation.id,
        slot_id: s.id
      }));
      const { error: slotLinkError } = await supabase.from('reservation_slots').insert(slotLinks);
      if (slotLinkError) throw slotLinkError;

      const consumedSlotIds = consumedSlots.map(s => s.id);
      const { error: slotUpdateError } = await supabase
        .from('slots')
        .update({ is_available: false })
        .in('id', consumedSlotIds);

      if (slotUpdateError) throw slotUpdateError;

      setSlots(slots.map(s => consumedSlotIds.includes(s.id) ? { ...s, is_available: false } : s));
      
      // Add the new reservation to the state
      const newReservation = {
        ...reservation,
        services: selectedServiceObjs,
        slots: consumedSlots,
        barber: barbers.find(b => b.id === slotToBook.barber_id)
      };
      setReservations([newReservation, ...reservations]);

      toast.success('Slot booked successfully');
      setIsBookSlotModalOpen(false);
      setSlotToBook(null);
      setBookingServices([]);
    } catch (error: any) {
      toast.error('Failed to book slot: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminCreateReservation = async () => {
    if (!adminBookingSlotId || adminBookingServices.length === 0 || !adminBookingBarberId) {
      toast.error('Please select a barber, service, and valid time slot');
      return;
    }
    setLoading(true);
    try {
      const selectedServiceObjs = services.filter(s => adminBookingServices.includes(s.id));
      const totalPrice = selectedServiceObjs.reduce((sum, s) => sum + s.price, 0);
      const totalDuration = selectedServiceObjs.reduce((sum, s) => sum + Number(s.duration), 0);

      const shop = shops.find(s => s.id === selectedShopId);
      const closingTime = shop?.closing_time || '18:00';

      const slotToBook = adminAvailableSlots.find(s => s.id === adminBookingSlotId);
      if (!slotToBook) throw new Error("Selected slot no longer exists");

      // Find consumed slots
      const barberSlots = adminAvailableSlots
        .filter(s => s.barber_id === slotToBook.barber_id && s.date === slotToBook.date)
        .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

      const consumedSlots: Slot[] = [];
      let accumulated = 0;
      const startIndex = barberSlots.findIndex(s => s.id === slotToBook.id);
      
      if (startIndex !== -1) {
        for (let j = startIndex; j < barberSlots.length; j++) {
          consumedSlots.push(barberSlots[j]);
          const currentSlotMinutes = timeToMinutes(barberSlots[j].time);
          const nextSlotMinutes = j + 1 < barberSlots.length 
            ? timeToMinutes(barberSlots[j + 1].time) 
            : timeToMinutes(closingTime);
            
          accumulated += (nextSlotMinutes - currentSlotMinutes);
          if (accumulated >= totalDuration) break;
        }
      }

      // Check if all consumed slots are available
      const unavailableSlots = consumedSlots.filter(s => !s.is_available && s.id !== slotToBook.id);
      if (unavailableSlots.length > 0) {
        throw new Error('Some slots required for this duration are already booked');
      }

      const { data: resData, error: resError } = await supabase
        .from('reservations')
        .insert([{
          barber_shop_id: selectedShopId,
          barber_id: adminBookingBarberId,
          customer_name: bookingCustomerName,
          customer_email: bookingCustomerEmail,
          customer_phone: bookingCustomerPhone,
          total_price: totalPrice,
          status: 'confirmed'
        }])
        .select();

      if (resError) throw resError;
      const reservation = resData[0];

      const serviceLinks = adminBookingServices.map(serviceId => ({
        reservation_id: reservation.id,
        service_id: serviceId
      }));
      const { error: linkError } = await supabase.from('reservation_services').insert(serviceLinks);
      if (linkError) throw linkError;

      const consumedSlotIds = consumedSlots.map(s => s.id);
      const slotLinks = consumedSlotIds.map(slotId => ({
        reservation_id: reservation.id,
        slot_id: slotId
      }));
      const { error: slotLinkError } = await supabase.from('reservation_slots').insert(slotLinks);
      if (slotLinkError) throw slotLinkError;

      const { error: slotUpdateError } = await supabase
        .from('slots')
        .update({ is_available: false })
        .in('id', consumedSlotIds);

      if (slotUpdateError) throw slotUpdateError;

      setSlots(slots.map(s => consumedSlotIds.includes(s.id) ? { ...s, is_available: false } : s));
      
      // Add the new reservation to the state
      const newReservation = {
        ...reservation,
        services: selectedServiceObjs,
        slots: consumedSlots,
        barber: barbers.find(b => b.id === slotToBook.barber_id)
      };
      
      setReservations([newReservation, ...reservations]);

      toast.success(t('admin.reservationAdded') || 'Reservation created successfully');
      
      setIsAddingReservation(false);
      setAdminBookingBarberId('');
      setAdminBookingSlotId('');
      setAdminBookingServices([]);
    } catch (error: any) {
      toast.error('Failed to book slot: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteSlot = async () => {
    if (!slotToDelete) return;
    
    setLoading(true);
    try {
      const { error } = await supabase.from('slots').delete().eq('id', slotToDelete);
      if (error) throw error;
      setSlots(slots.filter(s => s.id !== slotToDelete));
      toast.success(t('admin.slotDeleted'));
      setSlotToDelete(null);
    } catch (error: any) {
      toast.error(t('admin.failedDeleteSlot') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportReservations = () => {
    const headers = ["ID", "Customer Name", "Customer Email", "Status", "Date", "Time", "Shop", "Services", "Total Price"];
    const rows = filteredReservations.map(r => {
      const shop = shops.find(s => s.id === r.barber_shop_id)?.name || 'Unknown';
      const slot = r.reservation_slots?.[0]?.slots;
      const date = slot ? slot.date : '';
      const time = slot ? slot.time : '';
      const services = (r as any).reservation_services?.map((rs: any) => rs.services?.name).join(', ') || '';
      return [
        r.id,
        r.customer_name,
        r.customer_email,
        r.status,
        date,
        time,
        shop,
        `"${services}"`,
        r.total_price
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `reservations_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportInventory = () => {
    const headers = ["Item Name", "Stock Level", "Min. Stock Level", "Unit", "Status"];
    const rows = inventory.map(item => {
      const status = item.quantity <= item.min_stock_level ? 'Low Stock' : 'In Stock';
      return [
        `"${item.name}"`,
        item.quantity,
        item.min_stock_level,
        `"${item.unit}"`,
        status
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `inventory_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddInventoryItem = async () => {
    if (!selectedShopId) return;
    if (!newItem.name.trim()) {
      toast.error('Please enter an item name');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .insert([{
          shop_id: selectedShopId,
          name: newItem.name,
          quantity: newItem.quantity,
          min_stock_level: newItem.min_stock_level,
          unit: newItem.unit
        }])
        .select();

      if (error) throw error;
      setInventory([...inventory, data[0]]);
      setIsAddItemModalOpen(false);
      setNewItem({ name: '', quantity: 0, min_stock_level: 0, unit: 'pcs' });
      toast.success('Inventory item added successfully');
    } catch (error: any) {
      toast.error('Failed to add inventory item: ' + error.message);
    }
  };

  const handleUpdateCommissionStatus = async (id: string, status: 'paid' | 'pending' | 'cancelled') => {
    try {
      const { error } = await supabase
        .from('commissions')
        .update({ status })
        .eq('id', id);

      if (error) throw error;
      setCommissions(commissions.map(c => c.id === id ? { ...c, status } : c));
      toast.success(`Commission marked as ${status}`);
    } catch (error: any) {
      toast.error('Failed to update commission status: ' + error.message);
    }
  };

  const handleUpdateInventory = async (itemId: string, newQuantity: number) => {
    try {
      const { error } = await supabase
        .from('inventory_items')
        .update({ quantity: Math.max(0, newQuantity) })
        .eq('id', itemId);

      if (error) throw error;
      
      setInventory(prev => prev.map(item => 
        item.id === itemId ? { ...item, quantity: Math.max(0, newQuantity) } : item
      ));
      
      toast.success(t('common.save') || 'Updated successfully');
      setIsRestockingItem(false);
      setIsUsingItem(false);
      setSelectedInventoryItem(null);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleExportCommissions = () => {
    const headers = ["Barber", "Amount", "Status", "Date"];
    const rows = commissions.map(comm => {
      return [
        `"${comm.barbers?.name || 'Unknown'}"`,
        comm.amount,
        comm.status,
        format(parseISO(comm.created_at), 'yyyy-MM-dd')
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `commissions_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpdateReservationStatus = async (id: string, status: 'confirmed' | 'cancelled') => {
    if (!id || id === 'undefined') {
      toast.error(t('admin.failedUpdateStatus') + ': Invalid Reservation ID');
      return;
    }
    const action = async () => {
      try {
        const reservation = reservations.find(r => r.id === id);
        
        const { error } = await supabase
          .from('reservations')
          .update({ status })
          .eq('id', id);

        if (error) throw error;

        // Send Email Notification
        try {
          if (reservation) {
            const shop = shops.find(s => s.id === reservation.barber_shop_id);
            const firstSlot = reservation.reservation_slots?.[0]?.slots;
            const dateStr = firstSlot ? format(parseISO(firstSlot.date), 'PPP', { locale: dateLocale }) : '';
            const timeStr = firstSlot ? firstSlot.time.substring(0, 5) : '';

            await sendConfirmationEmail(reservation.customer_email, status === 'confirmed' ? 'confirmed' : 'cancelled', {
              shopName: shop?.name || 'Sharp Cuts',
              customerName: reservation.customer_name,
              date: dateStr,
              time: timeStr
            });
          }
        } catch (emailError) {
          console.warn('Email notification failed but status update was successful:', emailError);
        }

        // If cancelled, make slots available again
        if (status === 'cancelled' && reservation?.reservation_slots) {
          const slotIds = reservation.reservation_slots.map((rs: any) => rs.slot_id);
          if (slotIds.length > 0) {
            const { error: slotError } = await supabase
              .from('slots')
              .update({ is_available: true })
              .in('id', slotIds);
            
            if (slotError) throw slotError;
          }
          // Update local slots state
          setSlots(slots.map(s => slotIds.includes(s.id) ? { ...s, is_available: true } : s));
        }

        setReservations(reservations.map(r => r.id === id ? { ...r, status } : r));
        toast.success(t('admin.reservationStatusUpdated') + ' ' + t(`common.${status}`));
      } catch (error: any) {
        toast.error(t('admin.failedUpdateStatus') + ': ' + error.message);
      }
    };

    if (status === 'cancelled') {
      setConfirmConfig({
        isOpen: true,
        title: t('admin.cancelReservation'),
        message: t('admin.confirmCancelReservation'),
        variant: 'danger',
        onConfirm: action
      });
    } else {
      action();
    }
  };

  const handleUpdateReservation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReservation) return;

    // Swiss Phone Validation
    const swissPhoneRegex = /^(\+41|0) ?([1-9][0-9]) ?([0-9]{3}) ?([0-9]{2}) ?([0-9]{2})$/;
    if (!swissPhoneRegex.test(selectedReservation.customer_phone)) {
      toast.error('Please enter a valid Swiss phone number (e.g., 079 123 45 67 or +41 79 123 45 67)');
      return;
    }

    setIsSubmittingReservation(true);
    try {
      const { error } = await supabase
        .from('reservations')
        .update({
          customer_name: selectedReservation.customer_name,
          customer_email: selectedReservation.customer_email,
          customer_phone: selectedReservation.customer_phone
        })
        .eq('id', selectedReservation.id);

      if (error) throw error;

      setReservations(reservations.map(r => r.id === selectedReservation.id ? selectedReservation : r));
      toast.success(t('admin.reservationUpdated'));
      setIsEditingReservation(false);
      setSelectedReservation(null);
    } catch (error: any) {
      toast.error(t('admin.failedUpdateReservation') + ': ' + error.message);
    } finally {
      setIsSubmittingReservation(false);
    }
  };

  const handleAddPromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShopId && !isSuperAdmin) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }
    
    if (!newPromoCode.code || newPromoCode.value === undefined || newPromoCode.value <= 0) {
      toast.error('Please enter a valid code and value');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .insert([{
          shop_id: isSuperAdmin && isManagingPlatformPromos ? null : (selectedShopId || null),
          code: newPromoCode.code.toUpperCase().replace(/\s+/g, ''),
          discount_type: newPromoCode.discount_type,
          value: newPromoCode.value,
          active: newPromoCode.active,
          expires_at: newPromoCode.expires_at || null
        }])
        .select()
        .single();

      if (error) throw error;
      setPromoCodes([...promoCodes, data]);
      setIsAddingPromoCode(false);
      setNewPromoCode({ code: '', discount_type: 'percent', value: 10, active: true });
      toast.success('Promo code created successfully');
    } catch (error: any) {
      console.error('Error creating promo code:', error);
      toast.error(error.message || 'Failed to create promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePromoCode = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('promo_codes')
        .update({ active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      setPromoCodes(promoCodes.map(p => p.id === id ? { ...p, active: !currentStatus } : p));
      toast.success('Promo code status updated');
    } catch (error: any) {
      console.error('Error updating promo code:', error);
      toast.error('Failed to update promo code');
    }
  };

  const handleDeletePromoCode = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    try {
      const { error } = await supabase
        .from('promo_codes')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      setPromoCodes(promoCodes.filter(p => p.id !== id));
      toast.success('Promo code deleted');
    } catch (error: any) {
      console.error('Error deleting promo code:', error);
      toast.error('Failed to delete promo code');
    }
  };

  const handleAddService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShopId) {
      toast.error(t('admin.selectShopFirst'));
      return;
    }
    if (newService.price < 0 || newService.duration <= 0) {
      toast.error(t('admin.invalidPriceDuration'));
      return;
    }
    setIsSubmittingService(true);
    try {
      // If a new category was typed in the form, add it to customCategories
      if (isCreatingNewCategoryInForm && newService.category.trim()) {
        const trimmedCat = newService.category.trim();
        if (!uniqueCategories.includes(trimmedCat)) {
          let pushedCat: any = { name: trimmedCat };
          try {
            const { data: insertedCat, error: catError } = await supabase.from('service_categories')
              .insert({ name: trimmedCat, barber_shop_id: selectedShopId })
              .select().single();
            if (!catError && insertedCat) pushedCat = insertedCat;
          } catch(err) {}
          setCustomCategories(prev => [...prev, pushedCat]);
        }
        setIsCreatingNewCategoryInForm(false);
      }

      const { data, error } = await supabase
        .from('services')
        .insert([{ ...newService, barber_shop_id: selectedShopId }])
        .select();

      if (error) throw error;

      setServices([...services, data[0]]);
      setIsAddingService(false);
      setNewService({ name: '', description: '', price: 0, duration: 30, category: t('admin.haircut') });
      toast.success(t('admin.serviceAdded'));
    } catch (error: any) {
      toast.error(t('admin.failedAddService') + ': ' + error.message);
    } finally {
      setIsSubmittingService(false);
    }
  };

  const handleUpdateService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingService) return;
    if (editingService.price < 0 || editingService.duration <= 0) {
      toast.error(t('admin.invalidPriceDuration'));
      return;
    }
    setIsSubmittingService(true);
    try {
      // If a new category was typed in the edit form, add it to customCategories
      if (isCreatingNewCategoryInEditForm && editingService.category.trim()) {
        const trimmedCat = editingService.category.trim();
        if (!uniqueCategories.includes(trimmedCat)) {
          let pushedCat: any = { name: trimmedCat };
          try {
            const { data: insertedCat, error: catError } = await supabase.from('service_categories')
              .insert({ name: trimmedCat, barber_shop_id: selectedShopId })
              .select().single();
            if (!catError && insertedCat) pushedCat = insertedCat;
          } catch(err) {}
          setCustomCategories(prev => [...prev, pushedCat]);
        }
        setIsCreatingNewCategoryInEditForm(false);
      }

      const { error } = await supabase
        .from('services')
        .update({
          name: editingService.name,
          description: editingService.description,
          price: editingService.price,
          duration: editingService.duration,
          category: editingService.category
        })
        .eq('id', editingService.id);

      if (error) throw error;

      setServices(services.map(s => s.id === editingService.id ? editingService : s));
      setEditingService(null);
      toast.success(t('admin.serviceUpdated'));
    } catch (error: any) {
      toast.error(t('admin.failedUpdateService') + ': ' + error.message);
    } finally {
      setIsSubmittingService(false);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    const catName = newCategoryName.trim();
    if (uniqueCategories.includes(catName)) {
      toast.error(t('admin.categoryExists'));
      setIsAddingCategory(false);
      setNewCategoryName('');
      return;
    }

    try {
      if (selectedShopId) {
        const { data, error } = await supabase
          .from('service_categories')
          .insert({ name: catName, barber_shop_id: selectedShopId })
          .select()
          .single();
        if (error) throw error;
        setCustomCategories(prev => [...prev, data]);
      } else {
        setCustomCategories(prev => [...prev, { name: catName }]);
      }
    } catch (err: any) {
      console.warn('Failed to insert service category to Supabase. Falling back to local state.', err.message);
      setCustomCategories(prev => [...prev, { name: catName }]);
    }
    
    setNewCategoryName('');
    setIsAddingCategory(false);
    toast.success(t('admin.categoryAdded') || "Category added");
  };

  const handleDeleteCategory = async (categoryToDelete: string) => {
    // Only allow deleting if it's a custom category and not used in any service
    const isUsed = services.some(s => s.category === categoryToDelete);
    if (isUsed) {
      toast.error(t('admin.categoryInUse'));
      return;
    }

    try {
      if (selectedShopId) {
        await supabase
          .from('service_categories')
          .delete()
          .eq('name', categoryToDelete)
          .eq('barber_shop_id', selectedShopId);
      }
    } catch(err) {
      console.warn("Failed to delete from supabase", err);
    }

    setCustomCategories(customCategories.filter(c => (c.name || c) !== categoryToDelete));
    toast.success(t('admin.categoryDeleted'));
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory || !editingCategory.new.trim()) return;
    
    const oldName = editingCategory.old;
    const newName = editingCategory.new.trim();
    
    if (oldName === newName) {
      setEditingCategory(null);
      return;
    }

    try {
      // Update customCategories list
      setCustomCategories(prev => prev.map(c => {
        if ((c.name || c) === oldName) {
          if (c.id) return { ...c, name: newName };
          return newName; // local fallback obj
        }
        return c;
      }));

      if (selectedShopId) {
        // Optimistically update categories in DB
        await supabase
          .from('service_categories')
          .update({ name: newName })
          .eq('name', oldName)
          .eq('barber_shop_id', selectedShopId);
      }
      
      // Update all services using this category in the database
      const { error } = await supabase
        .from('services')
        .update({ category: newName })
        .eq('category', oldName);
        
      if (error) throw error;
      
      // Update services in local state
      setServices(prev => prev.map(s => s.category === oldName ? { ...s, category: newName } : s));
      
      setEditingCategory(null);
      toast.success(t('admin.categoryUpdated') || 'Category updated');
    } catch (error: any) {
      toast.error((t('admin.failedUpdateCategory') || 'Failed to update category') + ': ' + error.message);
    }
  };

  const handleDeleteService = async (id: string) => {
    setConfirmConfig({
      isOpen: true,
      title: t('admin.deleteService'),
      message: t('admin.confirmDeleteService'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('services').delete().eq('id', id);
          if (error) throw error;
          setServices(services.filter(s => s.id !== id));
          toast.success(t('admin.serviceDeleted'));
        } catch (error: any) {
          toast.error(t('admin.failedDeleteService'));
        }
      }
    });
  };

  const handleAddShop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newShop.name.trim()) {
      toast.error(t('admin.shopNameRequired'));
      return;
    }

    // Validation
    if (newShop.opening_time && newShop.closing_time) {
      if (newShop.closing_time <= newShop.opening_time) {
        toast.error(t('admin.invalidClosingTime'));
        return;
      }
    }

    setIsSubmittingShop(true);
    try {
      const { data, error } = await supabase
        .from('barber_shops')
        .insert([newShop])
        .select();

      if (error) throw error;

      setShops([...shops, data[0]]);
      setIsAddingShop(false);
      setNewShop({ 
        name: '', 
        address: '',
        phone: '',
        email: '',
        opening_time: '09:00',
        closing_time: '18:00',
        reminder_enabled: true,
        reminder_hours_before: 24,
        reminder_type: 'email'
      });
      toast.success(t('admin.shopAdded'));
      if (!selectedShopId) setSelectedShopId(data[0].id);
    } catch (error: any) {
      toast.error(t('admin.failedAddShop') + ': ' + error.message);
    } finally {
      setIsSubmittingShop(false);
    }
  };

  const handleDeleteShop = async (id: string) => {
    setConfirmConfig({
      isOpen: true,
      title: t('admin.deleteShop'),
      message: t('admin.confirmDeleteShop'),
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error } = await supabase.from('barber_shops').delete().eq('id', id);
          if (error) throw error;
          setShops(shops.filter(s => s.id !== id));
          if (selectedShopId === id) setSelectedShopId(shops.find(s => s.id !== id)?.id || null);
          toast.success(t('admin.shopDeleted'));
        } catch (error: any) {
          toast.error(t('admin.failedDeleteShop') + ': ' + error.message);
        }
      }
    });
  };

  const [isRegisteringShop, setIsRegisteringShop] = useState(false);
  const [initialShopData, setInitialShopData] = useState({
    name: '',
    address: '',
    phone: profile?.phone || '',
    email: profile?.email || ''
  });

  const handleRegisterInitialShop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      toast.error(t('booking.mustAgree'));
      return;
    }

    setLoading(true);
    try {
      const { data: shopId, error } = await supabase.rpc('register_barber_shop', {
        shop_name: initialShopData.name,
        shop_address: initialShopData.address,
        shop_phone: initialShopData.phone,
        shop_email: initialShopData.email
      });

      if (error) throw error;

      toast.success(t('admin.shopRegistered'));
      
      // Redirect to Stripe Checkout
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: profile?.id,
          email: profile?.email,
          shopId: shopId,
          agreedToTerms: true
        }),
      });

      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error: any) {
      toast.error(t('admin.failedRegisterShop') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedShopId) return;
    const shop = shops.find(s => s.id === selectedShopId);
    if (!shop) return;

    // Validation
    if (shop.opening_time && shop.closing_time) {
      if (shop.closing_time <= shop.opening_time) {
        toast.error(t('admin.invalidClosingTime'));
        return;
      }
    }

    setIsSubmittingShop(true);
    try {
      const { error } = await supabase
        .from('barber_shops')
        .update({ 
          name: shop.name, 
          address: shop.address,
          phone: shop.phone,
          email: shop.email,
          reminder_enabled: shop.reminder_enabled,
          reminder_hours_before: shop.reminder_hours_before,
          reminder_type: shop.reminder_type,
          opening_time: shop.opening_time,
          closing_time: shop.closing_time,
          latitude: shop.latitude,
          longitude: shop.longitude,
          is_active: shop.is_active !== false
        })
        .eq('id', selectedShopId);
      
      if (error) throw error;
      toast.success(t('admin.shopUpdated') || 'Shop details updated successfully');
      fetchData();
    } catch (error: any) {
      toast.error(t('admin.failedUpdateShop') + ': ' + error.message);
    } finally {
      setIsSubmittingShop(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatUser || !profile) return;
    
    const reservation = reservations.find(r => r.id === selectedChatUser);
    if (!reservation) return;

    const msg: Message = {
      id: crypto.randomUUID(),
      sender_id: profile.id,
      receiver_id: reservation.customer_name,
      content: newMessage.trim(),
      created_at: new Date().toISOString(),
      is_read: false,
      reservation_id: selectedChatUser,
    };

    setMessages(prev => [...prev, msg]);
    setNewMessage('');

    try {
      const { error } = await supabase.from('messages').insert([msg]);
      if (error) throw error;
    } catch (error) {
      console.warn('Could not save message to DB:', error);
    }
  };

  const handleSendSingleReminder = async (reservation: Reservation) => {
    const shop = shops.find(s => s.id === reservation.barber_shop_id);
    if (!shop) return;

    setLoading(true);
    try {
      const firstSlot = reservation.reservation_slots?.[0]?.slots;
      const dateStr = firstSlot ? format(parseISO(firstSlot.date), 'PPP', { locale: dateLocale }) : 'N/A';
      const timeStr = firstSlot ? firstSlot.time.substring(0, 5) : 'N/A';

      if (shop.reminder_type === 'email' || shop.reminder_type === 'both') {
        try {
          await sendConfirmationEmail(reservation.customer_email, 'reminder', {
            shopName: shop.name,
            customerName: reservation.customer_name,
            date: dateStr,
            time: timeStr
          });
        } catch (emailError) {
          console.warn('Email reminder failed:', emailError);
        }
      }
      /* 
      if (shop.reminder_type === 'sms' || shop.reminder_type === 'both') {
        // SMS reminder removed as requested
      }
      */

      const { error } = await supabase
        .from('reservations')
        .update({ reminder_sent: true })
        .eq('id', reservation.id);

      if (error) {
        console.error('Failed to update reminder_sent status:', error);
        if (!error.message.includes('Could not find the \'reminder_sent\' column')) {
          throw error;
        }
      }

      setReservations(prev => prev.map(r => r.id === reservation.id ? { ...r, reminder_sent: true } : r));
      toast.success(t('admin.reminderSent'));
    } catch (error: any) {
      toast.error(t('admin.failedSendReminders') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    if (!selectedShopId) return;
    const shop = shops.find(s => s.id === selectedShopId);
    if (!shop || !shop.reminder_enabled) {
      toast.error(t('admin.remindersNotEnabled'));
      return;
    }

    setLoading(true);
    try {
      // Fetch upcoming confirmed reservations
      const { data: upcomingReservations, error } = await supabase
        .from('reservations')
        .select('*, reservation_slots(slots(*))')
        .eq('barber_shop_id', selectedShopId)
        .eq('status', 'confirmed');

      if (error) throw error;
      
      const unremindedReservations = (upcomingReservations || []).filter(r => r.reminder_sent !== true);

      const now = new Date();
      const reminderThreshold = (shop.reminder_hours_before || 24) * 60 * 60 * 1000;
      
      let sentCount = 0;
      const remindedIds: string[] = [];

      for (const res of unremindedReservations) {
        const slots = res.reservation_slots?.map((rs: any) => rs.slots) || [];
        if (slots.length === 0) continue;
        
        // Use the first slot's date/time as the appointment start
        const firstSlot = slots[0];
        const appointmentTime = new Date(`${firstSlot.date}T${firstSlot.time}`);
        const timeUntilAppointment = appointmentTime.getTime() - now.getTime();

        // If appointment is in the future and within the threshold
        if (timeUntilAppointment > 0 && timeUntilAppointment <= reminderThreshold) {
          if (shop.reminder_type === 'email' || shop.reminder_type === 'both') {
            try {
              await sendConfirmationEmail(res.customer_email, 'reminder', {
                shopName: shop.name,
                customerName: res.customer_name,
                date: format(parseISO(firstSlot.date), 'PPP', { locale: dateLocale }),
                time: firstSlot.time.substring(0, 5)
              });
            } catch (emailError) {
              console.warn('Email reminder failed for reservation:', res.id, emailError);
            }
          }
          /*
          if (shop.reminder_type === 'sms' || shop.reminder_type === 'both') {
            // SMS reminder removed as requested
          }
          */
          remindedIds.push(res.id);
          sentCount++;
        }
      }

      if (remindedIds.length > 0) {
        // Update reservations to mark them as reminded
        const { error: updateError } = await supabase
          .from('reservations')
          .update({ reminder_sent: true })
          .in('id', remindedIds);
        
        if (updateError) {
          console.error('Failed to update reminder_sent status:', updateError);
          // Don't throw if it's just a missing column error, so the user still gets the success message
          if (!updateError.message.includes('Could not find the \'reminder_sent\' column')) {
            throw updateError;
          }
        }
        
        // Update local state
        setReservations(prev => prev.map(r => remindedIds.includes(r.id) ? { ...r, reminder_sent: true } : r));
      }

      toast.success(t('admin.remindersSent') + ' ' + sentCount);
    } catch (error: any) {
      toast.error(t('admin.failedSendReminders') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.reload();
  };

  if (!profile && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
        <div className="bg-amber-50 text-amber-600 p-6 rounded-2xl mb-6">
          <AlertCircle size={48} className="mx-auto mb-4" />
          <h2 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.profileNotFound')}</h2>
          <p className="font-bold opacity-80 text-zinc-600 dark:text-zinc-300">{t('admin.profileNotFoundDesc')}</p>
        </div>
        <button onClick={handleSignOut} className="bg-zinc-900 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-2">
          <LogOut size={18} /> {t('admin.signOut')}
        </button>
      </div>
    );
  }

  if (profile?.role === 'customer' && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
        <div className="bg-red-50 text-red-600 p-6 rounded-2xl mb-6">
          <XCircle size={48} className="mx-auto mb-4" />
          <h2 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.accessDenied')}</h2>
          <p className="font-bold opacity-80 text-zinc-600 dark:text-zinc-300">{t('admin.accessDeniedDesc')}</p>
        </div>
        <button onClick={handleSignOut} className="bg-zinc-900 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-2">
          <LogOut size={18} /> {t('admin.signOut')}
        </button>
      </div>
    );
  }

  const isShopDisabled = shops.find(s => s.id === selectedShopId)?.is_active === false;
  const isStaff = profile?.role === 'barber_staff' || isAdmin;

  // Calendar logic
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarDays = eachDayOfInterval({ 
    start: startOfWeek(monthStart), 
    end: endOfWeek(monthEnd) 
  });

  const filteredReservations = reservations.filter(res => {
    // If staff, only show their own reservations
    if (profile?.role === 'barber_staff' && currentBarberId && res.barber_id !== currentBarberId) {
      return false;
    }

    const matchesSearch = 
      (res.customer_name || '').toLowerCase().includes(reservationSearch.toLowerCase()) ||
      (res.customer_email || '').toLowerCase().includes(reservationSearch.toLowerCase()) ||
      (res.customer_phone || '').toLowerCase().includes(reservationSearch.toLowerCase());
    
    const matchesFilter = reservationFilter === 'all' || res.status === reservationFilter;
    
    const matchesDate = reservationDateFilter ? res.slots?.[0]?.date === reservationDateFilter : true;
    
    return matchesSearch && matchesFilter && matchesDate;
  });

  const filteredInventory = inventory.filter(item => 
    (item.name || '').toLowerCase().includes(inventorySearch.toLowerCase()) ||
    (item.category || '').toLowerCase().includes(inventorySearch.toLowerCase())
  );

  const uniqueCustomers = Array.from(
    reservations.reduce((acc, res) => {
      const email = (res.customer_email || '').toLowerCase();
      
      // Get the latest slot date for this reservation as the "appointment date"
      const validSlots = res.slots ? res.slots.filter(s => s && s.date) : [];
      const appointmentDate = validSlots.length > 0
        ? validSlots.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].date 
        : res.created_at;

      if (!acc.has(email)) {
        acc.set(email, {
          name: res.customer_name,
          email: res.customer_email,
          phone: res.customer_phone,
          totalBookings: 0,
          lastBooking: appointmentDate,
          reservations: [] as Reservation[]
        });
      }
      
      const customer = acc.get(email);
      customer.totalBookings += 1;
      
      if (new Date(appointmentDate) > new Date(customer.lastBooking)) {
        customer.lastBooking = appointmentDate;
      }
      
      customer.reservations.push(res);
      return acc;
    }, new Map<string, any>())
  ).map(([_, data]) => data);

  // Add registered customers who haven't booked yet
  allProfiles.filter(p => p.role === 'customer').forEach(p => {
    const email = (p.email || '').toLowerCase();
    if (!uniqueCustomers.find(c => (c.email || '').toLowerCase() === email)) {
      uniqueCustomers.push({
        name: p.full_name || (p.email || '').split('@')[0] || 'Unknown',
        email: p.email || 'N/A',
        phone: p.phone || 'N/A',
        totalBookings: 0,
        lastBooking: p.created_at,
        reservations: []
      });
    }
  });

  const filteredCustomers = uniqueCustomers.filter(c => 
    (c.name || '').toLowerCase().includes(customerSearchQuery.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(customerSearchQuery.toLowerCase()) ||
    (c.phone || '').toLowerCase().includes(customerSearchQuery.toLowerCase())
  );

  const filteredTeam = allProfiles.filter(p => {
    if (p.id === profile?.id) return false;
    
    const matchesSearch = (p.email || '').toLowerCase().includes(teamSearch.toLowerCase());
    const matchesFilter = teamFilter === 'all' || p.role === teamFilter;
    const matchesShop = teamShopFilter === 'all' || 
                        (teamShopFilter === 'none' ? !p.barber_shop_id : p.barber_shop_id === teamShopFilter);
    
    return matchesSearch && matchesFilter && matchesShop;
  });

  const filteredRoles = allProfiles.filter(p => {
    // Super Admins should only manage other Admins and Super Admins
    if (p.role !== 'barber_admin' && p.role !== 'super_admin') return false;
    
    const matchesSearch = p.email?.toLowerCase().includes(roleSearchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || p.role === roleFilter;
    const matchesShop = teamShopFilter === 'all' || 
                        (teamShopFilter === 'none' ? !p.barber_shop_id : p.barber_shop_id === teamShopFilter);
    return matchesSearch && matchesRole && matchesShop;
  });

  const defaultCategories = useMemo(() => [t('admin.haircut'), t('admin.shave'), t('admin.styling'), t('admin.treatment')], [t]);
  const uniqueCategories = useMemo(() => {
    const cats = Array.from(new Set([
      ...services.map(s => (s.category || '').trim()), 
      ...customCategories.map(c => (c.name || c || '').trim()), 
      ...defaultCategories.map(c => (c || '').trim())
    ])).filter(Boolean).sort();
    return cats;
  }, [services, customCategories, defaultCategories]);

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const matchesSearch = 
        (service.name || '').toLowerCase().includes(serviceSearch.toLowerCase()) ||
        (service.category || '').toLowerCase().includes(serviceSearch.toLowerCase()) ||
        (service.description || '').toLowerCase().includes(serviceSearch.toLowerCase());
      
      const matchesCategory = serviceCategoryFilter === 'all' || service.category === serviceCategoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  }, [services, serviceSearch, serviceCategoryFilter]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 py-8">
      <LegalModal 
        isOpen={!!legalType} 
        onClose={() => setLegalType(null)} 
        type={legalType || 'terms'} 
      />
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Mobile Sidebar Toggle */}
        <div className="lg:hidden flex items-center justify-between bg-zinc-100 dark:bg-zinc-800 p-4 rounded-2xl mb-4">
          <div className="flex items-center gap-3">
             <div className="flex flex-col items-start">
              <h1 className="text-xl font-black uppercase tracking-tighter leading-none mb-1 text-left">
                {t('admin.shop')} <span className="text-zinc-400 dark:text-zinc-300">{t('admin.control')}</span>
              </h1>
              <p className="text-zinc-500 dark:text-zinc-300 font-bold uppercase tracking-widest text-[8px] text-left">
                {isSuperAdmin ? t('admin.superAdminDashboard') : t('admin.adminDashboard')}
              </p>
            </div>
          </div>
          <button 
            onClick={() => setOpenSections(prev => ({ ...prev, mobileMenu: !prev.mobileMenu }))}
            className="p-2 bg-white dark:bg-zinc-900 rounded-xl shadow-sm text-zinc-900 dark:text-white"
          >
            <div className={`transition-transform duration-300 ${openSections.mobileMenu ? 'rotate-90' : ''}`}>
              <LayoutDashboard size={20} />
            </div>
          </button>
        </div>

        {/* Sidebar Navigation */}
        <aside className={`${openSections.mobileMenu ? 'block' : 'hidden'} lg:block w-full lg:w-56 shrink-0 transition-all duration-300`}>
          <div className="lg:sticky lg:top-36 space-y-8">
            <div className="hidden lg:flex flex-col items-start">
              <h1 className="text-3xl font-black uppercase tracking-tighter leading-none mb-1 text-left text-zinc-900 dark:text-white">
                {t('admin.shop')} <span className="text-zinc-400 dark:text-zinc-300">{t('admin.control')}</span>
              </h1>
              <p className="text-zinc-500 dark:text-zinc-300 font-bold uppercase tracking-widest text-[10px] text-left">
                {isSuperAdmin ? t('admin.superAdminDashboard') : t('admin.adminDashboard')}
              </p>
            </div>

            <nav className="flex flex-col gap-1 bg-zinc-100 dark:bg-zinc-800 p-2 rounded-[2.5rem] overflow-hidden">
              {/* Operations Group */}
              <div className="space-y-1">
                <button 
                  onClick={() => toggleSection('operations')}
                  className="w-full px-6 py-3 rounded-2xl font-black flex items-center justify-between text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-500 hover:bg-white/50 dark:hover:bg-zinc-700/50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <LayoutDashboard size={16} />
                    <span>{t('admin.operations') || 'Operations'}</span>
                  </div>
                  <motion.div
                    animate={{ rotate: openSections.operations ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={14} />
                  </motion.div>
                </button>
                
                <AnimatePresence initial={false}>
                  {openSections.operations && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden flex flex-col gap-1 px-1"
                    >
                      {!isSuperAdmin && (
                        <button 
                          onClick={() => {
                            setActiveTab('reservations');
                            setOpenSections(prev => ({ ...prev, mobileMenu: false }));
                          }}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all relative ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'reservations' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <CalendarIcon size={16} /> {t('admin.reservations') || 'Reservations'}
                          {newReservationsCount > 0 && (
                            <span className="absolute top-3 right-3 min-w-[16px] h-[16px] bg-amber-500 text-white text-[8px] flex items-center justify-center rounded-full animate-pulse px-1">
                              {newReservationsCount}
                            </span>
                          )}
                        </button>
                      )}
                      {isShopAdmin && (
                        <button 
                          onClick={() => {
                            setActiveTab('customers');
                            setOpenSections(prev => ({ ...prev, mobileMenu: false }));
                          }}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'customers' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Contact size={16} /> {t('admin.customers') || 'Customers'}
                        </button>
                      )}
                      {isStaff && !isSuperAdmin && (
                        <button 
                          onClick={() => {
                            setActiveTab('slots');
                            setOpenSections(prev => ({ ...prev, mobileMenu: false }));
                          }}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'slots' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <CalendarDays size={16} /> {t('admin.slots') || 'Slots'}
                        </button>
                      )}
                      {isStaff && !isSuperAdmin && (
                        <button 
                          onClick={() => setActiveTab('staff_dashboard')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'staff_dashboard' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <UserRound size={16} /> {t('admin.staffDashboard') || 'My Dashboard'}
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Management Group */}
              <div className="space-y-1">
                <button 
                  onClick={() => toggleSection('management')}
                  className="w-full px-6 py-3 rounded-2xl font-black flex items-center justify-between text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-500 hover:bg-white/50 dark:hover:bg-zinc-700/50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <Store size={16} />
                    <span>{t('admin.management') || 'Management'}</span>
                  </div>
                  <motion.div
                    animate={{ rotate: openSections.management ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={14} />
                  </motion.div>
                </button>
                
                <AnimatePresence initial={false}>
                  {openSections.management && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden flex flex-col gap-1 px-1"
                    >
                      {isShopAdmin && (
                        <button 
                          onClick={() => {
                            setActiveTab('services');
                            setOpenSections(prev => ({ ...prev, mobileMenu: false }));
                          }}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'services' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Scissors size={16} /> {t('admin.services') || 'Services'}
                        </button>
                      )}
                      {isShopAdmin && (
                        <button 
                          onClick={() => {
                            setActiveTab('inventory');
                            setOpenSections(prev => ({ ...prev, mobileMenu: false }));
                          }}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'inventory' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Store size={16} /> {t('admin.inventory') || 'Inventory'}
                        </button>
                      )}
                      {isShopAdmin && (
                        <button 
                          onClick={() => {
                            setActiveTab('team');
                            setOpenSections(prev => ({ ...prev, mobileMenu: false }));
                          }}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'team' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Users size={16} /> {t('admin.team') || 'Team'}
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button 
                          onClick={() => setActiveTab('shops')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'shops' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Store size={16} /> {t('admin.shops') || 'Shops'}
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button 
                          onClick={() => setActiveTab('roles')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'roles' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Shield size={16} /> {t('admin.userRoles') || 'User Roles'}
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Financials Group */}
              <div className="space-y-1">
                <button 
                  onClick={() => toggleSection('financials')}
                  className="w-full px-6 py-3 rounded-2xl font-black flex items-center justify-between text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-500 hover:bg-white/50 dark:hover:bg-zinc-700/50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <BarChart3 size={16} />
                    <span>{t('admin.financials') || 'Financials'}</span>
                  </div>
                  <motion.div
                    animate={{ rotate: openSections.financials ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={14} />
                  </motion.div>
                </button>
                
                <AnimatePresence initial={false}>
                  {openSections.financials && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden flex flex-col gap-1 px-1"
                    >
                      {isAdmin && (
                        <button 
                          onClick={() => setActiveTab('analytics')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'analytics' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <BarChart3 size={16} /> {t('admin.analytics') || 'Analytics'}
                        </button>
                      )}
                      {isShopAdmin && (
                        <button 
                          onClick={() => setActiveTab('commissions')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'commissions' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <CheckCircle size={16} /> {t('admin.commissions') || 'Commissions'}
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Marketing Group */}
              <div className="space-y-1">
                <button 
                  onClick={() => toggleSection('marketing')}
                  className="w-full px-6 py-3 rounded-2xl font-black flex items-center justify-between text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-500 hover:bg-white/50 dark:hover:bg-zinc-700/50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <Target size={16} />
                    <span>{t('admin.growth') || 'Growth'}</span>
                  </div>
                  <motion.div
                    animate={{ rotate: openSections.marketing ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={14} />
                  </motion.div>
                </button>
                
                <AnimatePresence initial={false}>
                  {openSections.marketing && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden flex flex-col gap-1 px-1"
                    >
                      {isAdmin && (
                        <button 
                          onClick={() => setActiveTab('marketing')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'marketing' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Users size={16} /> {t('admin.marketing') || 'Marketing'}
                        </button>
                      )}
                      {isAdmin && (
                        <button 
                          onClick={() => setActiveTab('email_templates')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'email_templates' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Mail size={16} /> {t('admin.emailTemplates') || 'Email Templates'}
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* System Group */}
              <div className="space-y-1">
                <button 
                  onClick={() => toggleSection('system')}
                  className="w-full px-6 py-3 rounded-2xl font-black flex items-center justify-between text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-500 hover:bg-white/50 dark:hover:bg-zinc-700/50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <Settings size={16} />
                    <span>{t('admin.system') || 'System'}</span>
                  </div>
                  <motion.div
                    animate={{ rotate: openSections.system ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={14} />
                  </motion.div>
                </button>
                
                <AnimatePresence initial={false}>
                  {openSections.system && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden flex flex-col gap-1 px-1"
                    >
                      {isStaff && !isSuperAdmin && (
                        <button 
                          onClick={() => setActiveTab('chat')}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'chat' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <MessageSquare size={16} /> {t('admin.chat') || 'Chat'}
                        </button>
                      )}
                      {isAdmin && (
                        <button 
                          onClick={() => {
                            setActiveTab('settings');
                            setOpenSections(prev => ({ ...prev, mobileMenu: false }));
                          }}
                          className={`w-full px-5 py-3 rounded-xl font-black flex items-center gap-3 transition-all ${
                            language === 'ta' ? 'text-xs' : 'uppercase tracking-widest text-[9px]'
                          } ${activeTab === 'settings' ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-md border border-zinc-100 dark:border-zinc-700/50' : 'text-zinc-400 dark:text-zinc-300 hover:bg-white/50 dark:hover:bg-zinc-700/50'}`}
                        >
                          <Settings size={16} /> {t('admin.settings') || 'Settings'}
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>

            <div className="hidden lg:block p-6 bg-white dark:bg-zinc-900 rounded-[2.5rem] border border-zinc-100 dark:border-zinc-800 shadow-xl shadow-zinc-200/50 dark:shadow-black/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-zinc-900 text-white rounded-xl flex items-center justify-center font-black shrink-0">
                  {profile?.email?.[0].toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-black text-zinc-900 dark:text-white truncate">{profile?.email}</div>
                  <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-md ${
                    isSuperAdmin ? 'bg-purple-100 text-purple-600' :
                    isAdmin ? 'bg-blue-100 text-blue-600' :
                    'bg-amber-100 text-amber-600'
                  }`}>
                    {profile?.role === 'super_admin' ? t('admin.superAdmin') || 'Super Admin' :
                     profile?.role === 'barber_admin' ? t('admin.barberAdmin') || 'Shop Admin' :
                     profile?.role === 'barber_staff' ? t('admin.barberStaff') || 'Barber Staff' :
                     t('admin.customerRole') || 'Customer'}
                  </span>
                </div>
              </div>
              
              {isSuperAdmin && shops.length > 0 && (
                <div className="space-y-2 mb-6">
                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.allShops') || 'All Shops (Overview)'}</label>
                  <select 
                    className="w-full px-4 py-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-xl font-bold text-xs outline-none focus:ring-2 ring-zinc-900 dark:ring-amber-500 text-zinc-900 dark:text-white"
                    value={selectedShopId || ''}
                    onChange={(e) => setSelectedShopId(e.target.value)}
                  >
                    <option value="">{t('admin.allShops') || 'All Shops (Overview)'}</option>
                    {shops.map(shop => (
                      <option key={shop.id} value={shop.id}>{shop.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-2 py-4 bg-zinc-50 dark:bg-zinc-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-zinc-400 dark:text-zinc-300 hover:text-red-500 rounded-2xl transition-all text-[10px] sm:text-xs font-black uppercase tracking-widest">
                <LogOut size={14} /> {t('common.logout')}
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0">
          {/* Mobile Header */}
          <div className="lg:hidden flex flex-col gap-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-900 text-white rounded-xl flex items-center justify-center font-black">
                  {profile?.email?.[0].toUpperCase()}
                </div>
              </div>
              <div className="flex flex-col items-start">
                <h1 className="text-3xl font-black uppercase tracking-tighter leading-none mb-1 text-left">
                  {t('admin.shop') || 'Shop'} <span className="text-zinc-400 dark:text-zinc-300">{t('admin.control') || 'Control'}</span>
                </h1>
                <p className="text-zinc-500 dark:text-zinc-300 font-bold uppercase tracking-widest text-[8px] text-left">
                  {isSuperAdmin ? t('admin.superAdminDashboard') || 'Super Admin Dashboard' : t('admin.adminDashboard') || 'Admin Dashboard'}
                </p>
              </div>
            </div>

            {isSuperAdmin && shops.length > 0 && (
              <div className="flex flex-col gap-1">
                <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.allShops') || 'All Shops (Overview)'}</label>
                <select 
                  className="w-full px-4 py-3 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl font-bold text-xs outline-none focus:ring-2 ring-zinc-900 dark:ring-amber-500 text-zinc-900 dark:text-white"
                  value={selectedShopId || ''}
                  onChange={(e) => setSelectedShopId(e.target.value)}
                >
                  <option value="">{t('admin.allShops') || 'All Shops (Overview)'}</option>
                  {shops.map(shop => (
                    <option key={shop.id} value={shop.id}>{shop.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {!isSuperAdmin && newReservationsCount > 0 && (
            <button 
              onClick={() => setActiveTab('reservations')}
              className="lg:hidden flex items-center gap-2 bg-amber-50 text-amber-600 px-4 py-2 rounded-2xl border border-amber-100 animate-bounce shadow-sm mb-6"
            >
              <Bell size={18} className="animate-ring" />
              <span className="text-xs font-black uppercase tracking-widest">
                {newReservationsCount} {t('admin.newReservation') || 'New Reservation'}
              </span>
            </button>
          )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-12 h-12 border-4 border-zinc-200 dark:border-zinc-800 border-t-zinc-900 dark:border-t-amber-500 rounded-full animate-spin" />
        </div>
      ) : isShopDisabled && !isSuperAdmin ? (
        <div className="max-w-2xl mx-auto py-24">
          <div className={`${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} rounded-[3rem] border shadow-2xl dark:shadow-black/50 p-12 text-center`}>
            <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <AlertCircle size={40} />
            </div>
            <h2 className={`text-3xl font-black uppercase tracking-tighter mb-4 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>Shop Disabled</h2>
            <p className="text-zinc-500 dark:text-zinc-300 font-bold mb-8">
              This barber shop has been disabled by the system administrator. This usually happens when a monthly contract is cancelled or expired.
            </p>
            <button 
              onClick={handleSignOut}
              className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all"
            >
              {t('admin.signOut')}
            </button>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <ErrorBoundary sectionName={activeTab} key={activeTab}>
          {!selectedShopId && profile?.role === 'barber_admin' && !isSuperAdmin ? (
            <div className="max-w-2xl mx-auto py-12">
              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                <div className="p-10 border-b border-zinc-50 dark:border-zinc-800 text-center">
                  <div className="w-20 h-20 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Store size={40} />
                  </div>
                  <h2 className={`text-3xl font-black uppercase tracking-tighter mb-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{t('admin.setupShop')}</h2>
                  <p className="text-zinc-500 dark:text-zinc-300 font-medium">{t('admin.setupShopDesc')}</p>
                </div>
                <form onSubmit={handleRegisterInitialShop} className="p-10 space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('common.shopName')}</label>
                    <input 
                      required
                      type="text" 
                      className={`w-full px-6 py-4 ${isDarkMode ? 'bg-zinc-800/50 text-white' : 'bg-zinc-50 text-zinc-900'} border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none`}
                      placeholder="My Barber Shop"
                      value={initialShopData.name}
                      onChange={(e) => setInitialShopData({ ...initialShopData, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('common.address')}</label>
                    <input 
                      required
                      type="text" 
                      className={`w-full px-6 py-4 ${isDarkMode ? 'bg-zinc-800/50 text-white' : 'bg-zinc-50 text-zinc-900'} border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none`}
                      placeholder="123 Street, City"
                      value={initialShopData.address}
                      onChange={(e) => setInitialShopData({ ...initialShopData, address: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('common.phone')}</label>
                    <input 
                      required
                      type="tel" 
                      className={`w-full px-6 py-4 ${isDarkMode ? 'bg-zinc-800/50 text-white' : 'bg-zinc-50 text-zinc-900'} border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none`}
                      placeholder="+41 79 123 45 67"
                      value={initialShopData.phone}
                      onChange={(e) => setInitialShopData({ ...initialShopData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('common.email')}</label>
                    <input 
                      required
                      type="email" 
                      className={`w-full px-6 py-4 ${isDarkMode ? 'bg-zinc-800/50 text-white' : 'bg-zinc-50 text-zinc-900'} border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none`}
                      placeholder="shop@example.com"
                      value={initialShopData.email}
                      onChange={(e) => setInitialShopData({ ...initialShopData, email: e.target.value })}
                    />
                  </div>
                  
                  <div className="mb-8 p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                    <label className="flex items-start gap-4 cursor-pointer group">
                      <div className="relative flex items-center mt-1">
                        <input
                          type="checkbox"
                          checked={agreed}
                          onChange={(e) => setAgreed(e.target.checked)}
                          className="peer sr-only"
                        />
                        <div className="w-6 h-6 border-2 border-zinc-200 dark:border-zinc-700 rounded-lg peer-checked:bg-zinc-900 dark:peer-checked:bg-amber-500 peer-checked:border-zinc-900 dark:peer-checked:border-amber-500 transition-all" />
                        <Check className="absolute text-white dark:text-zinc-900 opacity-0 peer-checked:opacity-100 transition-opacity left-1" size={16} />
                      </div>
                      <span className="text-xs font-medium text-zinc-500 dark:text-zinc-300 group-hover:text-zinc-700 dark:group-hover:text-zinc-200 transition-colors leading-relaxed">
                        {t('booking.agreeToTerms').split(/(\{terms\}|\{privacy\})/g).map((part, i) => {
                          if (part === '{terms}') {
                            return (
                              <button 
                                key={i} 
                                onClick={(e) => { e.preventDefault(); setLegalType('terms'); }} 
                                className="text-zinc-900 dark:text-white font-black hover:underline mx-0.5"
                              >
                                {t('common.terms')}
                              </button>
                            );
                          }
                          if (part === '{privacy}') {
                            return (
                              <button 
                                key={i} 
                                onClick={(e) => { e.preventDefault(); setLegalType('privacy'); }} 
                                className="text-zinc-900 dark:text-white font-black hover:underline mx-0.5"
                              >
                                {t('common.privacy')}
                              </button>
                            );
                          }
                          return part;
                        })}
                      </span>
                    </label>
                  </div>

                  <button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-zinc-900 text-white py-6 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-zinc-800 transition-all shadow-xl shadow-zinc-200 disabled:opacity-50 mt-4"
                  >
                    {loading ? t('common.loading') : t('admin.completeSetup')}
                  </button>
                </form>
              </div>
            </div>
          ) : !selectedShopId && profile?.role === 'barber_staff' && !isSuperAdmin ? (
            <div className="max-w-md mx-auto py-24 text-center">
              <div className="bg-white p-12 rounded-[3rem] border border-zinc-100 shadow-2xl">
                <div className="w-20 h-20 bg-amber-50 text-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-8">
                  <AlertCircle size={40} />
                </div>
                <h2 className={`text-2xl font-black uppercase tracking-tighter mb-4 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{t('admin.noShopAssigned')}</h2>
                <p className="text-zinc-500 dark:text-zinc-300 font-medium mb-8">
                  {t('admin.contactAdminToAssign')}
                </p>
                <button 
                  onClick={handleSignOut}
                  className="w-full py-4 bg-zinc-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-zinc-800 transition-all"
                >
                  {t('common.logout')}
                </button>
              </div>
            </div>
          ) : activeTab === 'reservations' && !isSuperAdmin && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-zinc-900 to-zinc-800 dark:from-zinc-100 dark:to-white text-white dark:text-zinc-900 p-8 rounded-[2.5rem] shadow-2xl shadow-zinc-900/20 dark:shadow-white/10 border border-zinc-800 dark:border-white/20 hover:scale-[1.02] transition-transform duration-300">
                  <p className={`font-black opacity-60 mb-2 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-[0.2em]'}`}>{t('admin.totalBookings') || 'Total Bookings'}</p>
                  <h3 className="text-4xl font-black tracking-tighter">{reservations.length}</h3>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-zinc-900 dark:to-zinc-800/80 border border-amber-100 dark:border-zinc-800 p-8 rounded-[2.5rem] shadow-xl shadow-amber-500/5 dark:shadow-black/50 hover:scale-[1.02] transition-transform duration-300">
                  <p className={`font-black text-amber-600/70 dark:text-amber-500 mb-2 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-[0.2em]'}`}>{t('common.pending') || 'Pending'}</p>
                  <h3 className="text-4xl font-black tracking-tighter text-amber-600 dark:text-amber-400">
                    {reservations.filter(r => r.status === 'pending').length}
                  </h3>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-zinc-900 dark:to-zinc-800/80 border border-emerald-100 dark:border-zinc-800 p-8 rounded-[2.5rem] shadow-xl shadow-emerald-500/5 dark:shadow-black/50 hover:scale-[1.02] transition-transform duration-300">
                  <p className={`font-black text-emerald-600/70 dark:text-emerald-500 mb-2 ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-[0.2em]'}`}>{t('common.confirmed') || 'Confirmed'}</p>
                  <h3 className="text-4xl font-black tracking-tighter text-emerald-600 dark:text-emerald-400">
                    {reservations.filter(r => r.status === 'confirmed').length}
                  </h3>
                </div>
              </div>

              {barbers.filter(b => profile?.role === 'barber_staff' ? b.id === currentBarberId : true).length > 0 && (
                <div className="mb-8">
                  <h2 className={`text-xl font-black uppercase tracking-tighter mb-4 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{t('common.barbers') || 'Barbers'}</h2>
                  <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar">
                    {barbers.filter(b => profile?.role === 'barber_staff' ? b.id === currentBarberId : true).map(barber => (
                      <div key={barber.id} className="flex flex-col items-center gap-2 min-w-[80px]">
                        <div className="w-16 h-16 rounded-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 shrink-0">
                          {barber.photo_url ? (
                            <img src={barber.photo_url} alt={barber.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-zinc-300 dark:text-zinc-300">
                              <UserRound size={24} />
                            </div>
                          )}
                        </div>
                        <span className="text-xs font-black text-zinc-900 dark:text-zinc-100 text-center truncate w-full">{barber.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl overflow-hidden">
                <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6">
                  <h2 className={`text-xl font-black uppercase tracking-tighter ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>{t('admin.recentReservations') || 'Recent Reservations'}</h2>
                  <div className="flex flex-row flex-wrap items-center justify-start xl:justify-end gap-4 w-full xl:w-auto mt-4 xl:mt-0">
                    <div className="relative flex-1 min-w-[200px] xl:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={16} />
                      <input 
                        type="text" 
                        placeholder={t('admin.searchReservations') || 'Search by name, email or phone...'} 
                        className="pl-10 pr-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none w-full xl:w-64 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500" 
                        value={reservationSearch}
                        onChange={(e) => setReservationSearch(e.target.value)}
                      />
                    </div>
                    <input 
                      type="date"
                      className="px-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 w-full sm:w-auto min-w-[140px]"
                      value={reservationDateFilter}
                      onChange={(e) => setReservationDateFilter(e.target.value)}
                    />
                    <select 
                      className="px-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white w-full sm:w-auto"
                      value={reservationFilter}
                      onChange={(e) => setReservationFilter(e.target.value as any)}
                    >
                      <option value="all">{t('admin.allStatus') || 'All Status'}</option>
                      <option value="pending">{t('common.pending') || 'Pending'}</option>
                      <option value="confirmed">{t('common.confirmed') || 'Confirmed'}</option>
                      <option value="cancelled">{t('common.cancelled') || 'Cancelled'}</option>
                    </select>
                    <button 
                      onClick={handleExportReservations}
                      className="px-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 text-zinc-900 dark:text-zinc-300 rounded-xl font-bold text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all flex items-center justify-center gap-2 whitespace-nowrap grow sm:grow-0"
                    >
                      <Download size={14} /> {t('admin.export') || 'CSV'}
                    </button>
                    <button 
                      onClick={() => setIsAddingReservation(true)}
                      className="px-6 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all flex items-center justify-center gap-2 whitespace-nowrap grow sm:grow-0"
                    >
                      <Plus size={14} /> New Booking
                    </button>
                  </div>
                </div>
                
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.customer') || 'Customer'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('common.barber') || 'Barber'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.services') || 'Services'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.dateTime') || 'Date & Time'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('common.total') || 'Total'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.status') || 'Status'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.reminder') || 'Reminder'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                      {filteredReservations.map((res) => (
                        <tr key={res.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                          <td className="p-6">
                            <div className="font-black text-zinc-900 dark:text-white">{res.customer_name}</div>
                            <div className="text-xs font-bold text-zinc-400 dark:text-zinc-300">{res.customer_email}</div>
                            <div className="text-xs font-bold text-zinc-400 dark:text-zinc-300">{res.customer_phone}</div>
                          </td>
                          <td className="p-6">
                            {res.barber ? (
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-zinc-100 dark:bg-zinc-800 rounded-lg overflow-hidden shrink-0">
                                  {res.barber.photo_url ? (
                                    <img src={res.barber.photo_url} alt={res.barber.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-zinc-300 dark:text-zinc-300">
                                      <UserRound size={16} />
                                    </div>
                                  )}
                                </div>
                                <span className="font-black text-zinc-900 dark:text-zinc-100 text-xs">{res.barber.name}</span>
                              </div>
                            ) : (
                              <span className="text-zinc-300 dark:text-zinc-300 font-bold text-xs uppercase tracking-widest">{t('common.none') || 'None'}</span>
                            )}
                          </td>
                          <td className="p-6">
                            <div className="flex flex-wrap gap-1">
                              {res.services?.filter(s => s).map((s, i) => (
                                <span key={i} className="px-2 py-0.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-md text-[9px] font-black uppercase">
                                  {s?.name}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="p-6">
                            <div className="flex items-center gap-2 text-xs font-black text-zinc-900 dark:text-white">
                              <CalendarIcon size={14} className="text-zinc-400 dark:text-zinc-300" />
                              {res.slots?.[0]?.date ? (
                                (() => {
                                  try {
                                    return format(parseISO(res.slots[0].date), 'MMM d, yyyy');
                                  } catch (e) {
                                    return res.slots[0].date || 'Invalid Date';
                                  }
                                })()
                              ) : 'N/A'}
                            </div>
                            <div className="flex items-center gap-2 text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">
                              <Clock size={14} />
                              {res.slots?.[0]?.time ? res.slots[0].time.substring(0, 5) : 'N/A'}
                            </div>
                          </td>
                          <td className="p-6 font-black text-zinc-900 dark:text-white">{t('common.currency')}{res.total_price}</td>
                          <td className="p-6">
                            <span className={`px-3 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest ${
                              res.status === 'confirmed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                              res.status === 'cancelled' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                              'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                            }`}>
                              {t(`common.${res.status}`)}
                            </span>
                          </td>
                          <td className="p-6">
                            {res.reminder_sent ? (
                              <span className="flex items-center gap-1 text-[10px] font-black text-emerald-500 dark:text-emerald-400 uppercase tracking-widest">
                                <CheckCircle size={12} />
                                {t('admin.sent') || 'Sent'}
                              </span>
                            ) : (
                              <span className="text-[10px] font-black text-zinc-300 dark:text-zinc-300 uppercase tracking-widest">
                                {t('admin.notSent') || 'Not Sent'}
                              </span>
                            )}
                          </td>
                          <td className="p-6">
                            <div className="flex items-center gap-2 flex-wrap">
                                <a 
                                  href={`mailto:${res.customer_email}`}
                                  className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                                  title={t('admin.sendEmail') || 'Send Email'}
                                >
                                  <Mail size={18} />
                                </a>
                                {res.customer_phone && (
                                  <a 
                                    href={`tel:${res.customer_phone}`}
                                    className="p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                                    title={t('admin.callCustomer') || 'Call Customer'}
                                  >
                                    <Phone size={18} />
                                  </a>
                                )}
                              <button 
                                onClick={() => {
                                  setSelectedReservation(res);
                                  setIsEditingReservation(true);
                                }}
                                className="p-2 bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                                title={t('admin.editReservation') || 'Edit Reservation'}
                              >
                                <Pencil size={18} />
                              </button>
                              {res.status === 'confirmed' && !res.reminder_sent && (
                                <button 
                                  onClick={() => handleSendSingleReminder(res)}
                                  className="p-2 bg-zinc-50 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-xl hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                                  title={t('admin.sendReminder') || 'Send Reminder'}
                                >
                                  <Bell size={18} />
                                </button>
                              )}
                              {res.status === 'pending' && (
                                <button 
                                  onClick={() => handleUpdateReservationStatus(res.id, 'confirmed')}
                                  className="p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
                                  title={t('common.confirm') || 'Confirm'}
                                >
                                  <CheckCircle size={18} />
                                </button>
                              )}
                              {res.status !== 'cancelled' && (
                                <button 
                                  onClick={() => handleUpdateReservationStatus(res.id, 'cancelled')}
                                  className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                  title={t('common.cancel')}
                                >
                                  <XCircle size={18} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Reservation Card View */}
                <div className="sm:hidden grid grid-cols-1 gap-4 p-4">
                  {filteredReservations.map((res) => (
                    <div key={res.id} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-5 space-y-4 border border-zinc-100 dark:border-zinc-800">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-black text-zinc-900 dark:text-white text-base">{res.customer_name}</div>
                          <div className="text-xs font-bold text-zinc-400 dark:text-zinc-300">{res.customer_email}</div>
                          <div className="text-xs font-bold text-zinc-400 dark:text-zinc-300">{res.customer_phone}</div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                          res.status === 'confirmed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                          res.status === 'cancelled' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                          'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                        }`}>
                          {t(`common.${res.status}`)}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 py-3 border-y border-zinc-100 dark:border-zinc-700/50">
                         {res.barber ? (
                          <div className="flex items-center gap-2">
                             <div className="w-8 h-8 bg-zinc-100 dark:bg-zinc-900 rounded-lg overflow-hidden shrink-0">
                              {res.barber.photo_url ? (
                                <img src={res.barber.photo_url} alt={res.barber.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-300">
                                  <UserRound size={16} />
                                </div>
                              )}
                            </div>
                            <span className="font-black text-zinc-900 dark:text-zinc-100 text-[10px] uppercase tracking-wide">{res.barber.name}</span>
                          </div>
                        ) : (
                          <span className="text-zinc-300 font-bold text-[10px] uppercase tracking-widest">{t('common.none')}</span>
                        )}
                        <div className="h-4 w-px bg-zinc-200 dark:bg-zinc-700" />
                        <div className="flex flex-wrap gap-1">
                          {res.services?.filter(s => s).map((s, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded text-[8px] font-black uppercase">
                              {s?.name}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-[10px] font-black text-zinc-900 dark:text-white">
                            <CalendarIcon size={12} className="text-zinc-400" />
                            {res.slots?.[0]?.date ? (
                              (() => {
                                try {
                                  return format(parseISO(res.slots[0].date), 'MMM d, yyyy');
                                } catch (e) {
                                  return res.slots[0].date;
                                }
                              })()
                            ) : 'N/A'}
                          </div>
                          <div className="flex items-center gap-2 text-[10px] font-bold text-zinc-400 dark:text-zinc-300">
                            <Clock size={12} />
                            {res.slots?.[0]?.time ? res.slots[0].time.substring(0, 5) : 'N/A'}
                          </div>
                        </div>
                        <div className="text-lg font-black text-amber-500">{t('common.currency')}{res.total_price}</div>
                      </div>

                      <div className="flex items-center gap-2 pt-2">
                          <a href={`mailto:${res.customer_email}`} className="flex-1 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl flex items-center justify-center">
                             <Mail size={16} />
                          </a>
                          {res.customer_phone && (
                            <a href={`tel:${res.customer_phone}`} className="flex-1 py-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl flex items-center justify-center">
                              <Phone size={16} />
                            </a>
                          )}
                          <button 
                            onClick={() => {
                              setSelectedReservation(res);
                              setIsEditingReservation(true);
                            }}
                            className="flex-1 py-2 bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded-xl flex items-center justify-center"
                          >
                            <Pencil size={16} />
                          </button>
                           {res.status === 'pending' && (
                            <button 
                              onClick={() => handleUpdateReservationStatus(res.id, 'confirmed')}
                              className="flex-2 py-2 bg-emerald-500 text-white rounded-xl flex items-center justify-center font-black text-[10px] uppercase tracking-widest gap-2"
                            >
                              <CheckCircle size={14} /> {t('common.confirm')}
                            </button>
                          )}
                          {res.status !== 'cancelled' && (
                            <button 
                              onClick={() => handleUpdateReservationStatus(res.id, 'cancelled')}
                              className="flex-1 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl flex items-center justify-center"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'customers' && isShopAdmin && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.customers') || 'Customers'}</h2>
                  <div className="flex items-center gap-4 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={16} />
                      <input 
                        type="text" 
                        placeholder={t('common.search') || 'Search...'} 
                        className="pl-10 pr-4 py-2 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none w-full sm:w-64 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400" 
                        value={customerSearchQuery}
                        onChange={(e) => setCustomerSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.customer') || 'Customer'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.totalBookings') || 'Total Bookings'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.lastBooking') || 'Last Booking'}</th>
                        <th className={`p-6 font-black text-zinc-400 dark:text-zinc-300 ${language === 'ta' ? 'text-xs' : 'text-[10px] sm:text-xs uppercase tracking-widest'}`}>{t('admin.actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                      {filteredCustomers.map((customer, idx) => (
                        <tr key={idx} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                          <td className="p-6">
                            <div className="font-black text-zinc-900 dark:text-white">{customer.name}</div>
                            <div className="text-xs font-bold text-zinc-400 dark:text-zinc-300">{customer.email}</div>
                            <div className="text-xs font-bold text-zinc-400 dark:text-zinc-300">{customer.phone}</div>
                          </td>
                          <td className="p-6">
                            <span className="px-3 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest">
                              {customer.totalBookings} {t('admin.reservations') || 'Reservations'}
                            </span>
                          </td>
                          <td className="p-6 text-xs font-bold text-zinc-400 dark:text-zinc-300">
                            {customer.lastBooking ? (
                              (() => {
                                try {
                                  return format(parseISO(customer.lastBooking), 'MMM d, yyyy');
                                } catch (e) {
                                  return 'N/A';
                                }
                              })()
                            ) : 'N/A'}
                          </td>
                          <td className="p-6">
                            <button 
                              onClick={() => {
                                setReservationSearch(customer.email);
                                setActiveTab('reservations');
                              }}
                              className="px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl text-[10px] font-black uppercase hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
                            >
                              {t('common.view') || 'View'} {t('admin.reservations') || 'Reservations'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Customers List */}
                <div className="md:hidden grid grid-cols-1 gap-4 p-4">
                  {filteredCustomers.map((customer, idx) => (
                    <div key={idx} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-800 space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-black text-zinc-900 dark:text-white text-base">{customer.name}</div>
                          <div className="text-xs font-bold text-zinc-400 dark:text-zinc-300">{customer.email}</div>
                        </div>
                        <div className="px-2 py-1 bg-amber-500/10 text-amber-500 rounded-lg text-[10px] font-black uppercase">
                          {customer.totalBookings} {t('admin.reservations') || 'Bookings'}
                        </div>
                      </div>
                      <div className="flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-zinc-400">
                        <span>{t('admin.lastBooking')}: {customer.lastBooking ? format(parseISO(customer.lastBooking), 'MMM d, yyyy') : 'N/A'}</span>
                      </div>
                      <button 
                        onClick={() => {
                          setReservationSearch(customer.email);
                          setActiveTab('reservations');
                        }}
                        className="w-full py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2"
                      >
                        <Eye size={16} /> {t('common.view')} {t('admin.reservations')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'slots' && !isSuperAdmin && (
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="min-w-[200px]">
                    <select 
                      className="w-full px-6 py-4 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-sm shadow-sm text-zinc-900 dark:text-white"
                      value={selectedBarberId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedBarberId(val);
                        setNewSlotBarberId(val);
                      }}
                      disabled={profile?.role === 'barber_staff'}
                    >
                      <option value="">{t('admin.allBarbers') || 'All Barbers'}</option>
                      {barbers.filter(b => profile?.role === 'barber_staff' ? b.id === currentBarberId : true).map(barber => (
                        <option key={barber.id} value={barber.id}>{barber.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 bg-white dark:bg-zinc-900 p-2 rounded-2xl border border-zinc-100 dark:border-zinc-800 shadow-sm transition-all hover:shadow-md">
                    <button 
                      onClick={() => {
                        const today = startOfToday();
                        setCurrentMonth(today);
                        setSelectedDate(today);
                      }}
                      className="px-4 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-500/10 rounded-xl transition-all text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-amber-500"
                    >
                      {t('common.today') || 'Today'}
                    </button>
                    <div className="w-px h-4 bg-zinc-100 dark:bg-zinc-800 mx-1" />
                    
                    <div className="flex items-center gap-1">
                      <button onClick={() => setCurrentMonth(addMonths(currentMonth, -1))} className="p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-xl transition-all text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-amber-500">
                        <ChevronLeft size={18} />
                      </button>
                      
                      <div className="flex items-center gap-2 px-2">
                        <select 
                          value={currentMonth.getMonth()} 
                          onChange={(e) => {
                            const newDate = startOfMonth(new Date(currentMonth.getFullYear(), parseInt(e.target.value), 1));
                            setCurrentMonth(newDate);
                          }}
                          className="bg-transparent border-none focus:ring-0 text-sm font-black uppercase tracking-tighter text-zinc-900 dark:text-white cursor-pointer hover:text-amber-500 transition-colors py-0 px-1"
                        >
                          {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i} className="dark:bg-zinc-900 uppercase">
                              {format(new Date(2024, i, 1), 'MMMM')}
                            </option>
                          ))}
                        </select>
                        <select 
                          value={currentMonth.getFullYear()} 
                          onChange={(e) => {
                            const newDate = startOfMonth(new Date(parseInt(e.target.value), currentMonth.getMonth(), 1));
                            setCurrentMonth(newDate);
                          }}
                          className="bg-transparent border-none focus:ring-0 text-sm font-black uppercase tracking-tighter text-zinc-900 dark:text-white cursor-pointer hover:text-amber-500 transition-colors py-0 px-1"
                        >
                          {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
                            <option key={year} value={year} className="dark:bg-zinc-900 text-zinc-900">{year}</option>
                          ))}
                        </select>
                      </div>

                      <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-xl transition-all text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-amber-500">
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.availability') || 'Availability'}</h2>
                  <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1 uppercase tracking-widest">{t('admin.manageStaffAvailability') || 'Manage staff working hours and slots'}</p>
                </div>
              </div>

              <div className="space-y-8">
                {/* Calendar View */}
                <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl relative overflow-hidden">
                  {isFetchingSlots && (
                    <div className="absolute inset-0 bg-white/60 dark:bg-zinc-950/60 backdrop-blur-[2px] z-20 flex items-center justify-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-zinc-900 dark:border-amber-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('common.loading') || 'Loading...'}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-7 gap-1 sm:gap-4">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                      <div key={d} className="text-center text-[7px] sm:text-[10px] font-black text-zinc-300 dark:text-zinc-500 uppercase tracking-widest py-4">
                        <span className="sm:hidden">{d.substring(0, 1)}</span>
                        <span className="hidden sm:inline">{t(`common.days.${d.toLowerCase()}`) || d}</span>
                      </div>
                    ))}
                    {calendarDays.map((day, i) => {
                      const isSelected = isSameDay(day, selectedDate);
                      const isCurrentMonth = isSameMonth(day, currentMonth);
                      const isPastDay = isBefore(day, startOfToday());
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const daySlots = slots.filter(s => s.date === dateStr && (selectedBarberId ? s.barber_id === selectedBarberId : true));
                      const availableCount = daySlots.filter(s => s.is_available).length;
                      const hasAvailable = availableCount > 0;
                      const isFullyBooked = daySlots.length > 0 && availableCount === 0;
                      
                      return (
                        <button
                          key={i}
                          disabled={isPastDay}
                          onClick={() => setSelectedDate(day)}
                          className={`min-h-[60px] sm:min-h-[110px] p-1.5 sm:p-4 rounded-xl sm:rounded-2xl border transition-all flex flex-col items-start justify-between relative group ${
                            isSelected ? 'border-zinc-900 dark:border-amber-500 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-2xl z-10 scale-105 sm:scale-100 border-2' : 
                            isPastDay ? 'border-transparent bg-zinc-50/50 dark:bg-zinc-950/50 text-zinc-300 dark:text-zinc-700 cursor-not-allowed opacity-40' :
                            hasAvailable && isCurrentMonth ? 'border-emerald-100 dark:border-emerald-900/20 bg-emerald-50/20 dark:bg-emerald-900/5 hover:border-emerald-400 dark:hover:border-emerald-700 text-zinc-900 dark:text-white shadow-sm' :
                            isFullyBooked && isCurrentMonth ? 'border-red-50 dark:border-red-900/10 bg-red-50/10 dark:bg-red-900/5 hover:border-red-300 dark:hover:border-red-800 text-zinc-900 dark:text-white' :
                            isCurrentMonth ? 'border-zinc-50 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-200 dark:hover:border-zinc-700 text-zinc-900 dark:text-white' : 
                            'border-transparent text-zinc-300 dark:text-zinc-700 opacity-10'
                          }`}
                        >
                          <div className="flex justify-between items-start w-full">
                            <span className={`text-[10px] sm:text-lg font-black ${isSelected ? 'text-white dark:text-zinc-900' : isPastDay ? 'text-zinc-200 dark:text-zinc-400' : 'text-zinc-900 dark:text-white'}`}>
                              {format(day, 'd')}
                            </span>
                            {isToday(day) && !isSelected && (
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="w-1 h-1 sm:w-2 sm:h-2 rounded-full bg-amber-500 shadow-lg" />
                                <span className="hidden sm:inline text-[7px] font-black uppercase text-amber-500 tracking-tighter">Today</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="w-full mt-auto">
                            {daySlots.length > 0 ? (
                              <div className="space-y-0.5 sm:space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className={`flex items-center gap-0.5 ${isSelected ? 'text-white/60 dark:text-zinc-900/60' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                    <Clock size={6} className="sm:w-2 sm:h-2 shrink-0" />
                                    <span className="text-[5px] sm:text-[9px] font-black uppercase tracking-widest leading-none">
                                      {availableCount}
                                    </span>
                                  </div>
                                </div>
                                <div className={`h-[2px] sm:h-1.5 rounded-full w-full overflow-hidden ${isSelected ? 'bg-white/20 dark:bg-zinc-900/20' : 'bg-zinc-100 dark:bg-zinc-800'}`}>
                                  <div 
                                    className={`h-full transition-all duration-700 ease-out ${isSelected ? 'bg-white dark:bg-zinc-900' : hasAvailable ? 'bg-emerald-500' : 'bg-red-500'}`}
                                    style={{ width: hasAvailable ? `${(availableCount / daySlots.length) * 100}%` : '100%' }}
                                  />
                                </div>
                              </div>
                            ) : (
                              !isPastDay && isCurrentMonth && (
                                <div className={`flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300 ${isSelected ? 'text-white/60 dark:text-zinc-900/60' : 'text-zinc-300 dark:text-zinc-600'}`}>
                                  <Plus size={8} className="sm:size-3 shrink-0" />
                                </div>
                              )
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Calendar Legend */}
                  <div className="mt-8 pt-8 border-t border-zinc-50 dark:border-zinc-800 flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.availableSlots') || 'Available Slots'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.fullyBooked') || 'Fully Booked'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-zinc-100 dark:bg-zinc-800" />
                      <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.booked') || 'Booked'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                       <span className="w-2 h-2 rounded-full bg-amber-500" />
                       <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.today') || 'Today'}</span>
                    </div>
                  </div>
                </div>

                {/* Slots for Day View */}
                <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl overflow-hidden flex flex-col">
                  <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 bg-zinc-50/30 dark:bg-zinc-800/30">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.slotsForDay') || 'Slots for Day'}</h3>
                      <div className="flex items-center gap-3">
                        {slots.filter(s => s.date === format(selectedDate, 'yyyy-MM-dd') && (selectedBarberId ? s.barber_id === selectedBarberId : true) && s.is_available).length > 0 && (
                          <button 
                            onClick={() => {
                              const bId = selectedBarberId || newSlotBarberId;
                              if (bId) handleClearDayAvailability(bId);
                              else toast.error(t('admin.selectBarberFirst'));
                            }}
                            className="p-1.5 text-zinc-300 hover:text-red-500 transition-colors"
                            title={t('admin.clearAvailability')}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                        <div className="flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-lg">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                          <span className="text-[10px] font-black uppercase text-emerald-600 dark:text-emerald-400 tracking-widest">
                            {slots.filter(s => s.date === format(selectedDate, 'yyyy-MM-dd') && (selectedBarberId ? s.barber_id === selectedBarberId : true) && s.is_available).length}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-300 uppercase tracking-widest">
                      {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                    </p>
                  </div>

                  <div className="p-8">
                    {slots.filter(s => s.date === format(selectedDate, 'yyyy-MM-dd') && (selectedBarberId ? s.barber_id === selectedBarberId : true)).length === 0 ? (
                      <div className="py-20 flex flex-col items-center justify-center text-center">
                        <div className="w-20 h-20 bg-zinc-50 dark:bg-zinc-800 rounded-full flex items-center justify-center mb-6">
                          <Clock className="text-zinc-200 dark:text-zinc-300" size={40} />
                        </div>
                        <p className="text-zinc-400 dark:text-zinc-300 font-black uppercase tracking-widest text-[10px] max-w-[160px] mx-auto leading-relaxed">
                          {t('admin.noSlotsAdded') || 'No slots added for this day'}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {slots
                          .filter(s => s.date === format(selectedDate, 'yyyy-MM-dd') && (selectedBarberId ? s.barber_id === selectedBarberId : true))
                          .sort((a, b) => a.time.localeCompare(b.time))
                          .map((slot) => (
                            <div 
                              key={slot.id}
                              onClick={() => {
                                if (slot.is_available) {
                                  setSlotToBook(slot);
                                  setIsBookSlotModalOpen(true);
                                }
                              }}
                              className={`group p-6 rounded-[2.5rem] border-2 transition-all flex items-center justify-between ${
                                slot.is_available 
                                  ? 'border-emerald-50 dark:border-emerald-900/20 bg-emerald-50/30 dark:bg-emerald-900/10 cursor-pointer hover:border-emerald-200 dark:hover:border-emerald-800 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20' 
                                  : 'border-zinc-50 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-800/50 opacity-60'
                              }`}
                            >
                              <div className="flex items-center gap-4">
                                <div className={`px-4 py-2.5 min-w-[5rem] rounded-2xl flex items-center justify-center font-black text-lg tracking-tight ${
                                  slot.is_available ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-sm transition-all group-hover:scale-105' : 'bg-zinc-100 dark:bg-zinc-800/50 text-zinc-400 dark:text-zinc-500'
                                }`}>
                                  {slot.time.substring(0, 5)}
                                </div>
                                <div className="flex flex-col justify-center">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <div className={`w-1.5 h-1.5 rounded-full ${slot.is_available ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]' : 'bg-zinc-300 dark:bg-zinc-600'}`} />
                                    <span className={`text-[9px] font-black uppercase tracking-widest ${slot.is_available ? 'text-emerald-600/70 dark:text-emerald-400/70' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                      {slot.is_available ? (t('common.available') || 'Available') : (t('common.booked') || 'Booked')}
                                    </span>
                                  </div>
                                  {!selectedBarberId && slot.barber_id && (
                                    <p className="text-xs font-bold text-zinc-900 dark:text-white">
                                      {barbers.find(b => b.id === slot.barber_id)?.name}
                                    </p>
                                  )}
                                </div>
                              </div>
                              
                              {slot.is_available && (
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSlot(slot.id);
                                  }}
                                  className="p-2.5 text-zinc-300 hover:text-red-500 hover:bg-white dark:hover:bg-zinc-800 rounded-xl transition-all opacity-0 group-hover:opacity-100 shadow-sm"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="p-8 bg-zinc-50/30 dark:bg-zinc-800/30 border-t border-zinc-50 dark:border-zinc-800">
                    <button 
                      onClick={() => {
                        setNewSlotBarberId(selectedBarberId);
                        setIsAddingSlotModalOpen(true);
                      }}
                      className="w-full sm:w-auto px-8 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50 group relative overflow-hidden active:scale-95"
                    >
                      <Plus size={18} className="group-hover:scale-110 transition-transform" /> 
                      <span>{t('admin.addSlot') || 'Add Slot'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Tools Section */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                    <div className="xl:col-span-2 bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/20 rounded-2xl flex items-center justify-center text-amber-500 dark:text-amber-400">
                      <CalendarDays size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.generateSlots') || 'Generate Slots'}</h3>
                      <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-300 uppercase tracking-widest">{t('admin.bulkCreateAvailability') || 'Bulk create availability for multiple days'}</p>
                    </div>
                  </div>
                  
                   <div className="space-y-10">
                     {/* Section 1: Personnel & Service */}
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-8 border-b border-zinc-50 dark:border-zinc-800">
                        <div className="space-y-2">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 px-2 flex items-center gap-2">
                            <User size={12} className="text-amber-500" />
                            {t('admin.selectBarber') || 'Select Barber'}
                          </label>
                          <select 
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white shadow-sm appearance-none transition-all"
                            value={newSlotBarberId}
                            onChange={(e) => setNewSlotBarberId(e.target.value)}
                            disabled={profile?.role === 'barber_staff'}
                          >
                            <option value="">{t('admin.chooseBarber') || 'Choose Barber'}</option>
                            {barbers.filter(b => profile?.role === 'barber_staff' ? b.id === currentBarberId : true).map(barber => (
                              <option key={barber.id} value={barber.id}>{barber.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 px-2 flex items-center gap-2">
                             <Scissors size={12} className="text-amber-500" />
                             {t('admin.service') || 'Service (Optional)'}
                          </label>
                          <select
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white appearance-none transition-all"
                            value={generateServiceId}
                            onChange={(e) => {
                              const serviceId = e.target.value;
                              setGenerateServiceId(serviceId);
                              if (serviceId) {
                                const service = services.find(s => s.id === serviceId);
                                if (service) {
                                  setGenerateInterval(service.duration);
                                }
                              }
                            }}
                          >
                            <option value="">{t('admin.customInterval') || 'Custom Interval'}</option>
                            {services.map(service => (
                              <option key={service.id} value={service.id}>
                                {service.name} ({service.duration} min)
                              </option>
                            ))}
                          </select>
                        </div>
                     </div>

                     {/* Section 2: Shift & Breaks */}
                     <div className="space-y-8">
                        <div className="flex items-center gap-3">
                           <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-500">
                             <Clock size={16} />
                           </div>
                           <h4 className="text-xs font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('admin.shiftAndBreaks') || 'Shift & Breaks'}</h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                           <div className="space-y-6">
                              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500 ml-2">{t('admin.workingHours') || 'Working Hours'}</p>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 px-2">{t('admin.startTime') || 'Start'}</label>
                                  <ModernTimePicker 
                                    value={generateStartTime}
                                    onChange={setGenerateStartTime}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 px-2">{t('admin.endTime') || 'End'}</label>
                                  <ModernTimePicker 
                                    value={generateEndTime}
                                    onChange={setGenerateEndTime}
                                  />
                                </div>
                              </div>
                           </div>

                           <div className="space-y-6">
                              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-500 ml-2">{t('admin.breakTime') || 'Break Time'}</p>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 px-2">{t('admin.breakStart') || 'Start'}</label>
                                  <ModernTimePicker 
                                    value={generateBreakStart}
                                    onChange={setGenerateBreakStart}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 px-2">{t('admin.breakEnd') || 'End'}</label>
                                  <ModernTimePicker 
                                    value={generateBreakEnd}
                                    onChange={setGenerateBreakEnd}
                                  />
                                </div>
                              </div>
                           </div>
                        </div>
                     </div>

                     {/* Section 3: Schedule Details */}
                     <div className="space-y-8 pt-8 border-t border-zinc-50 dark:border-zinc-800">
                        <div className="flex items-center gap-3">
                           <div className="w-8 h-8 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center text-amber-500">
                             <CalendarIcon size={16} />
                           </div>
                           <h4 className="text-xs font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('admin.scheduleDetails') || 'Schedule Details'}</h4>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                           <div className="space-y-6">
                             <div className="space-y-3">
                               <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 px-2">{t('admin.workingDays') || 'Working Days'}</label>
                               <div className="flex flex-wrap gap-2">
                                 {WORKING_DAYS_LIST.map((dayObj) => (
                                   <button
                                     key={dayObj.value}
                                     onClick={() => {
                                       if (generateWorkingDays.includes(dayObj.value)) {
                                         setGenerateWorkingDays(generateWorkingDays.filter(d => d !== dayObj.value));
                                       } else {
                                         setGenerateWorkingDays([...generateWorkingDays, dayObj.value].sort());
                                       }
                                     }}
                                     className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                                       generateWorkingDays.includes(dayObj.value)
                                         ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-lg shadow-zinc-200 dark:shadow-black/50 scale-110 z-10'
                                         : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                                     }`}
                                   >
                                     {(t(`common.days.${dayObj.label}`) || dayObj.label.charAt(0)).charAt(0).toUpperCase()}
                                   </button>
                                 ))}
                               </div>
                             </div>

                             <div className="flex items-end">
                                <label className="flex items-center gap-3 cursor-pointer group p-5 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100 dark:border-zinc-700 rounded-2xl w-full transition-all hover:bg-zinc-100 dark:hover:bg-zinc-800">
                                  <div className="relative flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={skipWeekends}
                                      onChange={(e) => setSkipWeekends(e.target.checked)}
                                      className="peer sr-only"
                                    />
                                    <div className="w-5 h-5 border-2 border-zinc-200 dark:border-zinc-700 rounded-lg peer-checked:bg-zinc-900 dark:peer-checked:bg-amber-500 peer-checked:border-zinc-900 dark:peer-checked:border-amber-500 transition-all" />
                                    <Check className="absolute text-white dark:text-zinc-900 opacity-0 peer-checked:opacity-100 transition-opacity left-0.5" size={14} />
                                  </div>
                                  <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 group-hover:text-zinc-600 dark:group-hover:text-zinc-200 transition-colors">
                                    {t('admin.skipWeekends') || 'Skip Weekends'}
                                  </span>
                                </label>
                              </div>
                           </div>

                           <div className="space-y-6">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 px-2">{t('admin.startDate') || 'Start Date'}</label>
                                  <input 
                                    type="date" 
                                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white transition-all shadow-sm"
                                    value={format(selectedDate, 'yyyy-MM-dd')}
                                    onChange={(e) => setSelectedDate(parseISO(e.target.value))}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 px-2">{t('admin.endDate') || 'End Date'}</label>
                                  <input 
                                    type="date" 
                                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white transition-all shadow-sm"
                                    value={format(generateEndDate, 'yyyy-MM-dd')}
                                    onChange={(e) => setGenerateEndDate(parseISO(e.target.value))}
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 px-2">{t('admin.intervalMins') || 'Interval (min)'}</label>
                                <div className="relative group">
                                  <Clock className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-zinc-900 dark:group-focus-within:text-amber-500 transition-colors" size={18} />
                                  <input 
                                    type="number" 
                                    min="5"
                                    step="5"
                                    className="w-full pl-16 pr-8 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white transition-all"
                                    value={generateInterval}
                                    onChange={(e) => {
                                      setGenerateInterval(parseInt(e.target.value));
                                      setGenerateServiceId(''); // Reset service selection if manually changed
                                    }}
                                  />
                                </div>
                              </div>
                           </div>
                        </div>
                     </div>
                   </div>

                  <div className="mt-10 flex flex-col sm:flex-row gap-4">
                    <button 
                      onClick={handleGenerateSlots}
                      disabled={!selectedShopId || !newSlotBarberId || isGeneratingSlots}
                      className="flex-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-6 py-5 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-2xl shadow-zinc-200 dark:shadow-black/50 disabled:opacity-50 group relative overflow-hidden hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap"
                    >
                      {isGeneratingSlots ? (
                        <div className="w-5 h-5 border-2 border-white dark:border-zinc-900 border-t-transparent rounded-full animate-spin shrink-0" />
                      ) : (
                        <CalendarDays size={18} className="group-hover:scale-110 transition-transform shrink-0" />
                      )}
                      <span>{t('admin.generateSlots') || 'Generate Slots'}</span>
                      <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    </button>
                    <button 
                      onClick={handleClearAvailability}
                      disabled={!selectedShopId || !newSlotBarberId || isGeneratingSlots}
                      className="px-6 py-5 bg-red-50 dark:bg-red-900/10 text-red-500 dark:text-red-400 rounded-2xl font-black uppercase text-xs flex items-center justify-center gap-2 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all disabled:opacity-50 border border-red-100 dark:border-red-900/20 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-red-200/20 dark:shadow-none whitespace-nowrap"
                    >
                      <Trash2 size={18} className="shrink-0" />
                      <span>{t('admin.clearAvailability') || 'Clear Availability'}</span>
                    </button>
                  </div>
                </div>

                <div className="bg-zinc-900 p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:bg-white/10 transition-all duration-700" />
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white mb-8">
                      <Clock size={24} />
                    </div>
                    <h3 className="text-2xl font-black uppercase tracking-tighter text-white mb-4">{t('admin.quickTips') || 'Quick Tips'}</h3>
                    <div className="space-y-6">
                      <div className="flex gap-4">
                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white text-[10px] font-black shrink-0">1</div>
                        <p className="text-zinc-300 text-xs font-bold leading-relaxed">
                          {t('admin.tip1') || 'Select a barber first to manage their specific availability.'}
                        </p>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white text-[10px] font-black shrink-0">2</div>
                        <p className="text-zinc-300 text-xs font-bold leading-relaxed">
                          {t('admin.tip2') || 'Use the bulk generator to quickly set up a full month of slots.'}
                        </p>
                      </div>
                      <div className="flex gap-4">
                        <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-white text-[10px] font-black shrink-0">3</div>
                        <p className="text-zinc-300 text-xs font-bold leading-relaxed">
                          {t('admin.tip3') || 'Click on a booked slot to view appointment details.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Add Slot Modal */}
              <AnimatePresence>
                {isAddingSlotModalOpen && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setIsAddingSlotModalOpen(false)}
                      className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
                    />
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: 20 }}
                      className="relative w-full max-w-xl bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl overflow-hidden border border-zinc-100 dark:border-zinc-800"
                    >
                      <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.addSlot')}</h2>
                        <button 
                          onClick={() => setIsAddingSlotModalOpen(false)}
                          className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 dark:text-zinc-300"
                        >
                          <X size={20} />
                        </button>
                      </div>

                      <div className="p-6 sm:p-8 space-y-8">
                        <div className="p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-[2rem] border border-zinc-100 dark:border-zinc-800">
                          <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-300 uppercase tracking-widest mb-1">{t('common.date')}</p>
                          <p className="text-lg font-black text-zinc-900 dark:text-white">{format(selectedDate, 'MMMM d, yyyy')}</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <label htmlFor="slot-barber" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 block">{t('common.barber')}</label>
                            <select 
                              id="slot-barber"
                              className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white transition-all appearance-none"
                              value={newSlotBarberId}
                              onChange={(e) => setNewSlotBarberId(e.target.value)}
                              disabled={profile?.role === 'barber_staff'}
                            >
                              <option value="" disabled className="dark:bg-zinc-900">{t('booking.selectBarber')}</option>
                              {barbers.filter(b => profile?.role === 'barber_staff' ? b.id === currentBarberId : true).map(barber => (
                                <option key={barber.id} value={barber.id} className="dark:bg-zinc-900">{barber.name}</option>
                              ))}
                            </select>
                          </div>

                          <div className="space-y-3">
                            <label htmlFor="slot-time" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 block">{t('admin.timeSlot')}</label>
                            <ModernTimePicker 
                              value={newSlotTime}
                              onChange={setNewSlotTime}
                            />
                          </div>
                        </div>

                        <button 
                          onClick={async () => {
                            await handleAddSlot();
                            setIsAddingSlotModalOpen(false);
                          }}
                          disabled={!selectedShopId || !newSlotBarberId || isSubmittingSlot}
                          className="w-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-6 rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-2xl shadow-zinc-200 dark:shadow-black/50 disabled:opacity-50 group relative overflow-hidden hover:scale-[1.02] active:scale-[0.98]"
                        >
                          {isSubmittingSlot ? <LoadingSpinner size={24} /> : <Plus size={24} className="group-hover:scale-110 transition-transform" />}
                          <span>{t('admin.addSlot')}</span>
                          <div className="absolute inset-0 bg-zinc-900/10 dark:bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                        </button>
                      </div>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </div>
          )}



          {activeTab === 'team' && isShopAdmin && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6">
                  <div>
                    <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.teamManagement') || 'Team Management'}</h2>
                    <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">{t('admin.manageStaffRoles') || 'Manage staff roles and access'}</p>
                  </div>
                  <div className="flex flex-row flex-wrap items-center gap-4 w-full xl:w-auto">
                    <button 
                      onClick={() => setIsAddingStaffModalOpen(true)}
                      className="px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-lg shadow-zinc-200 dark:shadow-black/50 flex items-center justify-center gap-2"
                    >
                      <UserPlus size={20} />
                      <span className="text-xs font-black uppercase tracking-widest">{t('admin.addStaff') || 'Add Staff'}</span>
                    </button>
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={16} />
                      <input 
                        type="text" 
                        placeholder={t('admin.searchTeam') || 'Search team...'} 
                        className="pl-10 pr-4 py-2 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none w-full sm:w-64 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400" 
                        value={teamSearch}
                        onChange={(e) => setTeamSearch(e.target.value)}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                      <select 
                        className="flex-1 sm:flex-none px-4 py-2 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                        value={teamFilter}
                        onChange={(e) => setTeamFilter(e.target.value as any)}
                      >
                        <option value="all" className="dark:bg-zinc-900">{t('admin.allRoles') || 'All Roles'}</option>
                        <option value="super_admin" className="dark:bg-zinc-900">{t('admin.superAdmin') || 'Super Admin'}</option>
                        <option value="barber_admin" className="dark:bg-zinc-900">{t('admin.barberAdmin') || 'Barber Admin'}</option>
                        <option value="barber_staff" className="dark:bg-zinc-900">{t('admin.barberStaff') || 'Barber Staff'}</option>
                        <option value="customer" className="dark:bg-zinc-900">{t('admin.customer') || 'Customer'}</option>
                      </select>
                      {isSuperAdmin && (
                        <select 
                          className="flex-1 sm:flex-none px-4 py-2 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                          value={teamShopFilter}
                          onChange={(e) => setTeamShopFilter(e.target.value)}
                        >
                          <option value="all" className="dark:bg-zinc-900">{t('admin.allShops') || 'All Shops'}</option>
                          {shops.map(shop => (
                            <option key={shop.id} value={shop.id} className="dark:bg-zinc-900">{shop.name}</option>
                          ))}
                        </select>
                      )}
                      <div className="hidden sm:block p-3 bg-zinc-50 dark:bg-zinc-800 rounded-2xl text-zinc-400 dark:text-zinc-300">
                        <Shield size={20} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.user') || 'User'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.joined') || 'Joined'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.currentRole') || 'Current Role'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.shop') || 'Shop'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                      {filteredTeam.map((p) => (
                        <tr key={p.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                          <td className="p-6">
                            <div className="font-black text-zinc-900 dark:text-white">{p.email}</div>
                            {barbers.find(b => b.user_id === p.id) && (
                              <div className="flex items-center gap-1 mt-1">
                                <Scissors size={10} className="text-amber-500" />
                                <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest">
                                  {t('admin.linkedBarber') || 'Linked Barber'}: {barbers.find(b => b.user_id === p.id)?.name}
                                </span>
                              </div>
                            )}
                          </td>
                          <td className="p-6 text-xs font-bold text-zinc-400 dark:text-zinc-300">
                            {format(parseISO(p.created_at), 'MMM d, yyyy')}
                          </td>
                          <td className="p-6">
                            <span className={`px-3 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest ${
                              p.role === 'super_admin' ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' :
                              p.role === 'barber_admin' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                              p.role === 'barber_staff' ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' :
                              'bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-300'
                            }`}>
                              {p.role === 'super_admin' ? (t('admin.superAdmin') || 'Super Admin') :
                               p.role === 'barber_admin' ? (t('admin.barberAdmin') || 'Barber Admin') :
                               p.role === 'barber_staff' ? (t('admin.barberStaff') || 'Barber Staff') :
                               (t('admin.customerRole') || 'Customer')}
                            </span>
                          </td>
                          <td className="p-6 text-xs font-bold text-zinc-500 dark:text-zinc-300">
                            {isSuperAdmin ? (
                              <select 
                                className="bg-transparent border-none focus:ring-0 text-xs font-bold text-zinc-500 dark:text-zinc-300 cursor-pointer outline-none"
                                value={p.barber_shop_id || ''}
                                onChange={(e) => handleUpdateRole(p.id, p.role, e.target.value || null)}
                              >
                                <option value="" className="dark:bg-zinc-900">{t('common.none') || 'None'}</option>
                                {shops.map(shop => (
                                  <option key={shop.id} value={shop.id} className="dark:bg-zinc-900">{shop.name}</option>
                                ))}
                              </select>
                            ) : (
                              shops.find(s => s.id === p.barber_shop_id)?.name || (t('common.none') || 'None')
                            )}
                          </td>
                          <td className="p-6">
                            <select 
                              className="px-4 py-2 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-xl text-[10px] font-black uppercase focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none cursor-pointer text-zinc-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              value={p.role}
                              disabled={!isSuperAdmin && (p.role === 'super_admin' || p.role === 'barber_admin')}
                              onChange={(e) => {
                                const newRole = e.target.value;
                                const shopId = newRole === 'customer' || newRole === 'super_admin' ? null : (p.barber_shop_id || selectedShopId);
                                handleUpdateRole(p.id, newRole, shopId);
                              }}
                            >
                              {isSuperAdmin && <option value="super_admin" className="dark:bg-zinc-900">{t('admin.superAdmin') || 'Super Admin'}</option>}
                              {(isSuperAdmin || p.role === 'barber_admin') && (
                                <option value="barber_admin" className="dark:bg-zinc-900">{t('admin.barberAdmin') || 'Barber Admin'}</option>
                              )}
                              <option value="barber_staff" className="dark:bg-zinc-900">{t('admin.barberStaff') || 'Barber Staff'}</option>
                              <option value="customer" className="dark:bg-zinc-900">{t('admin.customerRole') || 'Customer'}</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl overflow-hidden mt-8">
                <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('common.barbers') || 'Barbers'}</h2>
                    <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">{t('admin.manageStaff') || 'Manage your team members'}</p>
                  </div>
                  <button 
                    onClick={() => setIsAddingBarber(true)}
                    className="px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-lg shadow-zinc-200 dark:shadow-black/50 flex items-center gap-2"
                  >
                    <Plus size={20} />
                    <span className="text-xs font-black uppercase tracking-widest">{t('admin.addBarber') || 'Add Barber'}</span>
                  </button>
                </div>

                <div className="p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {barbers.length === 0 ? (
                      <div className="col-span-full py-12 flex flex-col items-center justify-center text-zinc-400 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border-2 border-dashed border-zinc-200 dark:border-zinc-700">
                        <UserRound size={48} className="mb-4 opacity-20" />
                        <p className="font-black uppercase tracking-widest text-xs">{t('booking.noBarbers') || 'No barbers found'}</p>
                      </div>
                    ) : (
                      barbers.map((barber) => (
                        <div key={barber.id} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-6 relative group border border-transparent hover:border-zinc-200 dark:hover:border-zinc-700 transition-all">
                          <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                            <button 
                              onClick={() => setEditingBarber(barber)}
                              className="p-2 text-zinc-400 dark:text-zinc-300 hover:text-amber-500 hover:bg-white dark:hover:bg-zinc-800 rounded-xl transition-all"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button 
                              onClick={() => handleDeleteBarber(barber.id)}
                              className="p-2 text-zinc-400 dark:text-zinc-300 hover:text-red-500 hover:bg-white dark:hover:bg-zinc-800 rounded-xl transition-all"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 bg-zinc-200 dark:bg-zinc-700 rounded-2xl overflow-hidden shrink-0">
                              {barber.photo_url ? (
                                <img src={barber.photo_url} alt={barber.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-zinc-400 dark:text-zinc-300">
                                  <UserRound size={32} />
                                </div>
                              )}
                            </div>
                            <div>
                              <h3 className="font-black text-lg text-zinc-900 dark:text-white">{barber.name}</h3>
                              <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 uppercase tracking-widest">
                                {shops.find(s => s.id === barber.barber_shop_id)?.name}
                              </p>
                              {barber.user_id && (
                                <div className="flex items-center gap-1.5 mt-1">
                                  <User size={10} className="text-amber-500" />
                                  <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest">
                                    {allProfiles.find(p => p.id === barber.user_id)?.email || 'Linked User'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-zinc-500 dark:text-zinc-300 font-medium line-clamp-2 mb-4">{barber.bio}</p>
                          
                          <div className="pt-4 border-t border-zinc-100 dark:border-zinc-800 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.workingDays')}</span>
                              <div className="flex gap-1">
                                {WORKING_DAYS_LIST.map((dayObj) => (
                                  <span 
                                    key={dayObj.value}
                                    className={`w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-black ${
                                      (barber.working_days || []).includes(dayObj.value)
                                        ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900'
                                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-300 dark:text-zinc-300'
                                    }`}
                                  >
                                    {dayObj.short}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.hours')}</span>
                              <span className="text-[10px] font-black text-zinc-900 dark:text-white">
                                {barber.start_time?.substring(0, 5)} - {barber.end_time?.substring(0, 5)}
                              </span>
                            </div>
                            {(barber.break_start || barber.break_end) && (
                              <div className="flex items-center justify-between">
                                <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.break')}</span>
                                <span className="text-[10px] font-black text-zinc-400 dark:text-zinc-300">
                                  {barber.break_start?.substring(0, 5)} - {barber.break_end?.substring(0, 5)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {isAddingBarber && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                  >
                    <motion.div 
                      initial={{ scale: 0.9, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0.9, y: 20 }}
                      className="bg-white dark:bg-zinc-950 rounded-[3rem] w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden shadow-2xl border border-zinc-100 dark:border-zinc-800"
                    >
                      <div className="p-6 sm:p-7 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                        <h2 className="text-lg sm:text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.addBarber')}</h2>
                        <button onClick={() => {
                          setIsAddingBarber(false);
                          setBarberPhotoFile(null);
                        }} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors">
                          <X size={20} className="text-zinc-400 dark:text-zinc-300" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-6 sm:p-7 space-y-5 custom-scrollbar scroll-smooth">
                        <div className="space-y-2">
                          <label htmlFor="barber-name" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">
                            {t('common.name')} <span className="text-red-500">*</span>
                          </label>
                          <input 
                            id="barber-name"
                            type="text" 
                            required
                            className="w-full px-5 py-3.5 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                            placeholder={t('common.name')}
                            value={newBarber.name}
                            onChange={(e) => setNewBarber({...newBarber, name: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="barber-bio" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.bio')}</label>
                          <textarea 
                            id="barber-bio"
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none h-32 resize-none text-zinc-900 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                            placeholder={t('admin.description')}
                            value={newBarber.bio}
                            onChange={(e) => setNewBarber({...newBarber, bio: e.target.value})}
                          />
                        </div>
                        <div className="space-y-4">
                          <label htmlFor="barber-photo" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.photoUrl')}</label>
                          
                          <div 
                            className={`relative border-2 border-dashed rounded-[2.5rem] p-8 transition-all flex flex-col items-center justify-center text-center overflow-hidden min-h-[220px]
                              ${isDraggingPhoto 
                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/10' 
                                : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-900'
                              }
                            `}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setIsDraggingPhoto(true);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              setIsDraggingPhoto(false);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              setIsDraggingPhoto(false);
                              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                setBarberPhotoFile(e.dataTransfer.files[0]);
                              }
                            }}
                          >
                            <input 
                              id="barber-photo-file"
                              type="file" 
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => setBarberPhotoFile(e.target.files?.[0] || null)}
                            />

                            {(barberPhotoFile || newBarber.photo_url) ? (
                              <div className="relative w-full h-48 sm:h-56 border-2 border-white dark:border-zinc-800 rounded-[1.5rem] overflow-hidden shadow-2xl z-10 group">
                                <img 
                                  src={barberPhotoFile ? URL.createObjectURL(barberPhotoFile) : newBarber.photo_url} 
                                  alt="Preview" 
                                  className="w-full h-full object-cover" 
                                  referrerPolicy="no-referrer"
                                />
                                {!isUploading && (
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setBarberPhotoFile(null);
                                      setNewBarber({...newBarber, photo_url: ''});
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white shadow-lg backdrop-blur-md rounded-xl p-2 transition-all flex items-center gap-2"
                                  >
                                    <X size={16} /> <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest hidden sm:inline">Remove</span>
                                  </button>
                                )}
                                {isUploading && uploadProgress > 0 && (
                                  <div className="absolute inset-0 bg-zinc-900/80 backdrop-blur-md flex flex-col items-center justify-center text-white p-6 z-20">
                                    <div className="font-black text-3xl mb-4 text-amber-500">
                                      {Math.round(uploadProgress)}%
                                    </div>
                                    <div className="w-full max-w-[200px] bg-zinc-800 rounded-full h-2 overflow-hidden shadow-inner">
                                      <div 
                                        className="bg-amber-500 h-2 rounded-full transition-all duration-300 ease-out shadow-[0_0_12px_rgba(245,158,11,0.6)]" 
                                        style={{ width: `${uploadProgress}%` }}
                                      />
                                    </div>
                                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mt-4 animate-pulse">Uploading Image</div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center pointer-events-none z-10">
                                <div className="w-16 h-16 bg-white dark:bg-zinc-800 rounded-2xl shadow-sm border border-zinc-100 dark:border-zinc-700 flex items-center justify-center text-zinc-400 dark:text-zinc-500 mb-4 transition-transform group-hover:scale-110">
                                  <Upload size={24} />
                                </div>
                                <h4 className="text-xs font-black text-zinc-900 dark:text-white mb-1 uppercase tracking-widest">
                                  {t('admin.uploadPhoto') || 'Upload Photo'}
                                </h4>
                                <p className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 mb-6">
                                  Drag and drop your image here, or
                                </p>
                              </div>
                            )}

                            {!(barberPhotoFile || newBarber.photo_url) && (
                              <button
                                type="button"
                                onClick={() => document.getElementById('barber-photo-file')?.click()}
                                className="relative z-20 px-8 py-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-zinc-800 dark:hover:bg-white transition-all shadow-md"
                              >
                                Browse Files
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-4 p-6 bg-zinc-50 dark:bg-zinc-900/50 rounded-[2rem] border border-zinc-100 dark:border-zinc-800">
                          <h3 className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.availability') || 'Availability & Working Hours'}</h3>
                          
                          <div className="space-y-2">
                            <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.workingDays') || 'Working Days'}</label>
                            <div className="flex flex-wrap gap-2">
                              {WORKING_DAYS_LIST.map((dayObj) => (
                                <button
                                  key={dayObj.value}
                                  type="button"
                                  onClick={() => {
                                    const currentDays = newBarber.working_days || [];
                                    const nextDays = currentDays.includes(dayObj.value)
                                      ? currentDays.filter(d => d !== dayObj.value)
                                      : [...currentDays, dayObj.value].sort();
                                    setNewBarber({...newBarber, working_days: nextDays});
                                  }}
                                  className={`px-4 py-2 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all ${
                                    (newBarber.working_days || []).includes(dayObj.value)
                                      ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-lg shadow-zinc-200 dark:shadow-black/50'
                                      : 'bg-white dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 border border-zinc-100 dark:border-zinc-700'
                                  }`}
                                >
                                  {t(`common.days.${dayObj.label}`)?.substring(0, 3) || dayObj.label.charAt(0).toUpperCase() + dayObj.label.slice(1)}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.startTime')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={newBarber.start_time}
                                onChange={(value) => setNewBarber({...newBarber, start_time: value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.endTime')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={newBarber.end_time}
                                onChange={(value) => setNewBarber({...newBarber, end_time: value})}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.breakStart')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={newBarber.break_start}
                                onChange={(value) => setNewBarber({...newBarber, break_start: value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.breakEnd')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={newBarber.break_end}
                                onChange={(value) => setNewBarber({...newBarber, break_end: value})}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="barber-user" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.linkUser')}</label>
                          <select 
                            id="barber-user"
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                            value={newBarber.user_id}
                            onChange={(e) => setNewBarber({...newBarber, user_id: e.target.value})}
                          >
                            <option value="" className="dark:bg-zinc-900">{t('admin.noUser') || 'No User Linked'}</option>
                            {allProfiles.filter(p => p.role !== 'customer').map(p => (
                              <option key={p.id} value={p.id} className="dark:bg-zinc-900">{p.email}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="p-8 border-t border-zinc-50 dark:border-zinc-800 flex gap-4 shrink-0">
                        <button 
                          onClick={handleAddBarber}
                          disabled={isUploading || isSubmittingBarber}
                          className="flex-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {(isUploading || isSubmittingBarber) ? <LoadingSpinner /> : t('admin.addBarber')}
                        </button>
                        <button 
                          onClick={() => setIsAddingBarber(false)}
                          className="px-6 py-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}

                {isAddingStaffModalOpen && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                  >
                    <motion.div 
                      initial={{ scale: 0.9, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0.9, y: 20 }}
                      className="bg-white dark:bg-zinc-950 rounded-[3rem] w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden shadow-2xl border border-zinc-100 dark:border-zinc-800"
                    >
                      <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.addStaff')}</h2>
                        <button onClick={() => {
                          setIsAddingStaffModalOpen(false);
                          setNewStaffEmail('');
                          setNewStaffPassword('');
                          setNewStaffConfirmPassword('');
                          setNewStaffFullName('');
                        }} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors">
                          <X size={20} className="text-zinc-400 dark:text-zinc-300" />
                        </button>
                      </div>
                      <form onSubmit={handleAddStaffAccount} className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar scroll-smooth">
                        <div className="space-y-2 text-left">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('common.fullName') || 'Full Name'}</label>
                          <div className="relative">
                            <UserRound className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={20} />
                            <input 
                              required
                              type="text" 
                              className="w-full pl-14 pr-8 py-5 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                              placeholder="John Doe"
                              value={newStaffFullName}
                              onChange={(e) => setNewStaffFullName(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="space-y-2 text-left">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('auth.email')}</label>
                          <div className="relative">
                            <Mail className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={20} />
                            <input 
                              required
                              type="email" 
                              className="w-full pl-14 pr-8 py-5 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                              placeholder="staff@example.com"
                              value={newStaffEmail}
                              onChange={(e) => setNewStaffEmail(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="space-y-2 text-left">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('auth.password')}</label>
                          <div className="relative">
                            <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={20} />
                            <input 
                              required
                              type="password" 
                              className="w-full pl-14 pr-8 py-5 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                              placeholder="••••••••"
                              value={newStaffPassword}
                              onChange={(e) => setNewStaffPassword(e.target.value)}
                              minLength={6}
                            />
                          </div>
                        </div>
                        <div className="space-y-2 text-left">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('auth.confirmPassword') || 'Confirm Password'}</label>
                          <div className="relative">
                            <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={20} />
                            <input 
                              required
                              type="password" 
                              className="w-full pl-14 pr-8 py-5 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                              placeholder="••••••••"
                              value={newStaffConfirmPassword}
                              onChange={(e) => setNewStaffConfirmPassword(e.target.value)}
                              minLength={6}
                            />
                          </div>
                        </div>
                        <div className="p-6 bg-amber-50 dark:bg-amber-900/20 rounded-[2rem] border border-amber-100 dark:border-amber-900/30">
                          <p className="text-xs font-bold text-amber-700 dark:text-amber-400 leading-relaxed">
                            {t('admin.staffCreationNotice')}
                          </p>
                        </div>
                        <div className="flex gap-4 pt-4">
                          <button 
                            type="submit"
                            disabled={loading || isSubmittingStaff}
                            className="flex-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50 disabled:opacity-50 flex items-center justify-center gap-2"
                          >
                            {(loading || isSubmittingStaff) ? <LoadingSpinner /> : t('admin.createAccount')}
                          </button>
                          <button 
                            type="button"
                            onClick={() => {
                              setIsAddingStaffModalOpen(false);
                              setNewStaffEmail('');
                              setNewStaffPassword('');
                              setNewStaffConfirmPassword('');
                              setNewStaffFullName('');
                            }}
                            className="px-6 py-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </form>
                    </motion.div>
                  </motion.div>
                )}

                {editingBarber && (
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                  >
                    <motion.div 
                      initial={{ scale: 0.9, y: 20 }}
                      animate={{ scale: 1, y: 0 }}
                      exit={{ scale: 0.9, y: 20 }}
                      className="bg-white dark:bg-zinc-950 rounded-[3rem] w-full max-w-xl flex flex-col max-h-[90vh] overflow-hidden shadow-2xl border border-zinc-100 dark:border-zinc-800"
                    >
                      <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.editBarber') || 'Edit Barber'}</h2>
                        <button onClick={() => {
                          setEditingBarber(null);
                          setBarberPhotoFile(null);
                        }} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors">
                          <X size={20} className="text-zinc-400 dark:text-zinc-300" />
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar scroll-smooth">
                        <div className="space-y-2">
                          <label htmlFor="edit-barber-name" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">
                            {t('common.name')} <span className="text-red-500">*</span>
                          </label>
                          <input 
                            id="edit-barber-name"
                            type="text" 
                            required
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                            placeholder={t('common.name')}
                            value={editingBarber.name}
                            onChange={(e) => setEditingBarber({...editingBarber, name: e.target.value})}
                          />
                        </div>
                        <div className="space-y-2">
                          <label htmlFor="edit-barber-bio" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.bio')}</label>
                          <textarea 
                            id="edit-barber-bio"
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none h-32 resize-none text-zinc-900 dark:text-zinc-300 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                            placeholder={t('admin.description')}
                            value={editingBarber.bio || ''}
                            onChange={(e) => setEditingBarber({...editingBarber, bio: e.target.value})}
                          />
                        </div>
                        <div className="space-y-4">
                          <label htmlFor="edit-barber-photo" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.photoUrl')}</label>
                          
                          <div 
                            className={`relative border-2 border-dashed rounded-[2.5rem] p-8 transition-all flex flex-col items-center justify-center text-center overflow-hidden min-h-[220px]
                              ${isDraggingPhoto 
                                ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/10' 
                                : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-900'
                              }
                            `}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setIsDraggingPhoto(true);
                            }}
                            onDragLeave={(e) => {
                              e.preventDefault();
                              setIsDraggingPhoto(false);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              setIsDraggingPhoto(false);
                              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                                setBarberPhotoFile(e.dataTransfer.files[0]);
                              }
                            }}
                          >
                            <input 
                              id="edit-barber-photo-file"
                              type="file" 
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => setBarberPhotoFile(e.target.files?.[0] || null)}
                            />

                            {(barberPhotoFile || editingBarber.photo_url) ? (
                              <div className="relative w-full h-48 sm:h-56 border-2 border-white dark:border-zinc-800 rounded-[1.5rem] overflow-hidden shadow-2xl z-10 group">
                                <img 
                                  src={barberPhotoFile ? URL.createObjectURL(barberPhotoFile) : editingBarber.photo_url} 
                                  alt="Preview" 
                                  className="w-full h-full object-cover" 
                                  referrerPolicy="no-referrer"
                                />
                                {!isUploading && (
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setBarberPhotoFile(null);
                                      setEditingBarber({...editingBarber, photo_url: ''});
                                    }}
                                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white shadow-lg backdrop-blur-md rounded-xl p-2 transition-all flex items-center gap-2"
                                  >
                                    <X size={16} /> <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest hidden sm:inline">Remove</span>
                                  </button>
                                )}
                                {isUploading && uploadProgress > 0 && (
                                  <div className="absolute inset-0 bg-zinc-900/80 backdrop-blur-md flex flex-col items-center justify-center text-white p-6 z-20">
                                    <div className="font-black text-3xl mb-4 text-amber-500">
                                      {Math.round(uploadProgress)}%
                                    </div>
                                    <div className="w-full max-w-[200px] bg-zinc-800 rounded-full h-2 overflow-hidden shadow-inner">
                                      <div 
                                        className="bg-amber-500 h-2 rounded-full transition-all duration-300 ease-out shadow-[0_0_12px_rgba(245,158,11,0.6)]" 
                                        style={{ width: `${uploadProgress}%` }}
                                      />
                                    </div>
                                    <div className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mt-4 animate-pulse">Uploading Image</div>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center pointer-events-none z-10">
                                <div className="w-16 h-16 bg-white dark:bg-zinc-800 rounded-2xl shadow-sm border border-zinc-100 dark:border-zinc-700 flex items-center justify-center text-zinc-400 dark:text-zinc-500 mb-4 transition-transform group-hover:scale-110">
                                  <Upload size={24} />
                                </div>
                                <h4 className="text-xs font-black text-zinc-900 dark:text-white mb-1 uppercase tracking-widest">
                                  {t('admin.uploadPhoto') || 'Upload Photo'}
                                </h4>
                                <p className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400 mb-6">
                                  Drag and drop your image here, or
                                </p>
                              </div>
                            )}

                            {!(barberPhotoFile || editingBarber.photo_url) && (
                              <button
                                type="button"
                                onClick={() => document.getElementById('edit-barber-photo-file')?.click()}
                                className="relative z-20 px-8 py-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-zinc-800 dark:hover:bg-white transition-all shadow-md"
                              >
                                Browse Files
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-4 p-6 bg-zinc-50 dark:bg-zinc-900/50 rounded-[2rem]">
                          <h3 className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.availability') || 'Availability & Working Hours'}</h3>
                          
                          <div className="space-y-2">
                            <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.workingDays') || 'Working Days'}</label>
                            <div className="flex flex-wrap gap-2">
                              {WORKING_DAYS_LIST.map((dayObj) => (
                                <button
                                  key={dayObj.value}
                                  type="button"
                                  onClick={() => {
                                    const currentDays = editingBarber.working_days || [];
                                    const nextDays = currentDays.includes(dayObj.value)
                                      ? currentDays.filter(d => d !== dayObj.value)
                                      : [...currentDays, dayObj.value].sort();
                                    setEditingBarber({...editingBarber, working_days: nextDays});
                                  }}
                                  className={`px-4 py-2 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all ${
                                    (editingBarber.working_days || []).includes(dayObj.value)
                                      ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-lg shadow-zinc-200 dark:shadow-black/50'
                                      : 'bg-white dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 border border-zinc-100 dark:border-zinc-700'
                                  }`}
                                >
                                  {t(`common.days.${dayObj.label}`)?.substring(0, 3) || dayObj.label.charAt(0).toUpperCase() + dayObj.label.slice(1)}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.startTime')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={editingBarber.start_time || '09:00'}
                                onChange={(value) => setEditingBarber({...editingBarber, start_time: value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.endTime')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={editingBarber.end_time || '18:00'}
                                onChange={(value) => setEditingBarber({...editingBarber, end_time: value})}
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.breakStart')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={editingBarber.break_start || '12:00'}
                                onChange={(value) => setEditingBarber({...editingBarber, break_start: value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-2">{t('admin.breakEnd')}</label>
                              <ModernTimePicker 
                                className="w-full px-4 py-3 bg-white dark:bg-zinc-800 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                value={editingBarber.break_end || '13:00'}
                                onChange={(value) => setEditingBarber({...editingBarber, break_end: value})}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.portfolio')}</label>
                          <div className="space-y-4">
                            <div className="flex gap-2">
                              <input 
                                type="text" 
                                className="flex-1 px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-sm text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                                placeholder="Image URL (e.g. from Unsplash or Picsum)"
                                id="new-portfolio-url"
                              />
                              <button 
                                type="button"
                                onClick={() => {
                                  const input = document.getElementById('new-portfolio-url') as HTMLInputElement;
                                  if (input.value) {
                                    const currentUrls = editingBarber.portfolio_urls || [];
                                    setEditingBarber({...editingBarber, portfolio_urls: [...currentUrls, input.value]});
                                    input.value = '';
                                  }
                                }}
                                className="px-6 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all"
                              >
                                {t('common.add')}
                              </button>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              {(editingBarber.portfolio_urls || []).map((url, idx) => (
                                <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800">
                                  <img src={url} alt="Portfolio" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                  <button 
                                    onClick={() => {
                                      const nextUrls = (editingBarber.portfolio_urls || []).filter((_, i) => i !== idx);
                                      setEditingBarber({...editingBarber, portfolio_urls: nextUrls});
                                    }}
                                    className="absolute inset-0 bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="edit-barber-user" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.linkUser')}</label>
                          <select 
                            id="edit-barber-user"
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                            value={editingBarber.user_id || ''}
                            onChange={(e) => setEditingBarber({...editingBarber, user_id: e.target.value})}
                          >
                            <option value="" className="dark:bg-zinc-900">{t('admin.noUser') || 'No User Linked'}</option>
                            {allProfiles.filter(p => p.role !== 'customer').map(p => (
                              <option key={p.id} value={p.id} className="dark:bg-zinc-900">{p.email}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="p-8 border-t border-zinc-50 dark:border-zinc-800 flex gap-4 shrink-0">
                        <button 
                          onClick={handleUpdateBarber}
                          disabled={isUploading || isSubmittingBarber}
                          className="flex-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {(isUploading || isSubmittingBarber) ? <LoadingSpinner /> : t('common.save')}
                        </button>
                        <button 
                          onClick={() => {
                            setEditingBarber(null);
                            setBarberPhotoFile(null);
                          }}
                          disabled={isSubmittingBarber}
                          className="px-6 py-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 rounded-2xl font-black uppercase tracking-widest text-sm hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all disabled:opacity-50"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 p-8 rounded-[2.5rem]">
                <h4 className="text-sm font-black uppercase tracking-tighter text-amber-900 dark:text-amber-400 mb-2">{t('admin.instructions')}</h4>
                <p className="text-xs text-amber-800 dark:text-amber-300/80 font-medium leading-relaxed">
                  {t('admin.teamInstructions')}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'services' && isAdmin && (
            <div className="space-y-8">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="w-full lg:w-auto">
                  <h2 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.menu') || 'Menu'} <span className="text-zinc-400 dark:text-zinc-300">{t('admin.items') || 'Items'}</span></h2>
                  <p className="text-zinc-400 dark:text-zinc-500 text-xs font-bold uppercase tracking-widest mt-1">
                    {filteredServices.length} {t('admin.services') || 'Services'} {serviceSearch || serviceCategoryFilter !== 'all' ? t('admin.filtered') || 'filtered' : ''}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 sm:flex-none w-full sm:w-auto lg:w-72">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={18} />
                    <input 
                      type="text" 
                      placeholder={t('admin.searchServices') || 'Search services...'} 
                      className="w-full pl-12 pr-12 py-4 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl font-bold text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none shadow-sm text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                      value={serviceSearch}
                      onChange={(e) => setServiceSearch(e.target.value)}
                    />
                    {serviceSearch && (
                      <button 
                        onClick={() => setServiceSearch('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  
                  <div className="relative flex-1 sm:flex-none">
                    <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300 pointer-events-none" size={16} />
                    <select 
                      className="w-full pl-12 pr-10 py-4 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl font-bold text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none shadow-sm text-zinc-900 dark:text-white appearance-none"
                      value={serviceCategoryFilter}
                      onChange={(e) => setServiceCategoryFilter(e.target.value)}
                    >
                      <option value="all" className="dark:bg-zinc-900">{t('admin.allCategories') || 'All Categories'}</option>
                      {uniqueCategories.map(category => (
                        <option key={category} value={category} className="dark:bg-zinc-900">{category}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                  </div>

                  {(serviceSearch || serviceCategoryFilter !== 'all') && (
                    <button 
                      onClick={() => {
                        setServiceSearch('');
                        setServiceCategoryFilter('all');
                      }}
                      className="px-4 py-4 text-zinc-400 hover:text-zinc-900 dark:hover:text-white font-bold text-xs uppercase tracking-widest transition-colors flex items-center gap-2"
                    >
                      <X size={14} /> {t('common.clear') || 'Clear'}
                    </button>
                  )}

                  <button 
                    onClick={() => setIsAddingService(true)}
                    className="flex-1 sm:flex-none bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-6 sm:px-8 py-4 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50 whitespace-nowrap"
                  >
                    <Plus size={20} /> <span className="hidden sm:inline">{t('admin.addService') || 'Add Service'}</span>
                  </button>
                </div>
              </div>

              {/* Categories Management Section */}
              <div className="bg-white dark:bg-zinc-900 p-8 rounded-[2.5rem] border border-zinc-100 dark:border-zinc-800 shadow-xl shadow-zinc-200/50 dark:shadow-black/50 mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                  
                  {!isAddingCategory ? (
                    <button 
                      onClick={() => setIsAddingCategory(true)}
                      className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-6 py-3 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all text-[10px] sm:text-xs uppercase tracking-widest"
                    >
                      <Plus size={16} /> {t('admin.addCategory') || 'Add Category'}
                    </button>
                  ) : (
                    <form onSubmit={handleAddCategory} className="flex items-center gap-2 w-full md:w-auto">
                      <input 
                        autoFocus
                        type="text"
                        placeholder={t('admin.category') || 'Category Name'}
                        className="flex-1 md:w-64 px-6 py-3 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-xl text-xs font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                      />
                      <button type="submit" className="p-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all">
                        <CheckCircle size={18} />
                      </button>
                      <button type="button" onClick={() => setIsAddingCategory(false)} className="p-3 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 rounded-xl hover:text-zinc-600 dark:hover:text-zinc-200 transition-all">
                        <X size={18} />
                      </button>
                    </form>
                  )}
                  <div className="text-right w-full md:w-auto mt-4 md:mt-0">
                    <h3 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
                      {t('admin.categories') || 'Categories'}
                    </h3>
                    <p className="text-zinc-400 dark:text-zinc-300 text-xs font-bold uppercase tracking-widest mt-1">
                      {t('admin.manageServiceGroups') || 'Manage your service groups'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  {uniqueCategories.map(category => {
                    const isDefault = defaultCategories.includes(category);
                    const isUsed = services.some(s => s.category === category);
                    const isEditing = editingCategory?.old === category;

                    if (isEditing) {
                      return (
                        <form 
                          key={category}
                          onSubmit={handleUpdateCategory}
                          className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-800 p-2 rounded-2xl border border-zinc-200 dark:border-zinc-700"
                        >
                          <input 
                            autoFocus
                            className="bg-transparent border-none outline-none font-black text-[10px] sm:text-xs uppercase tracking-widest px-2 w-32 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                            value={editingCategory.new}
                            onChange={(e) => setEditingCategory({ ...editingCategory, new: e.target.value })}
                          />
                          <button type="submit" className="p-2 text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors">
                            <CheckCircle size={14} />
                          </button>
                          <button type="button" onClick={() => setEditingCategory(null)} className="p-2 text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg transition-colors">
                            <X size={14} />
                          </button>
                        </form>
                      );
                    }

                    return (
                      <div key={category} className="group relative">
                        <span className={`px-5 py-3 rounded-2xl border flex items-center gap-3 transition-all ${
                          isUsed ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-white border-zinc-100 dark:border-zinc-700'
                        }`}>
                          <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest">{category}</span>
                          {!isDefault && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button 
                                onClick={() => setEditingCategory({ old: category, new: category })}
                                className="text-zinc-400 hover:text-amber-500 transition-colors"
                              >
                                <Edit2 size={12} />
                              </button>
                              <button 
                                onClick={() => handleDeleteCategory(category)}
                                className={`transition-colors ${isUsed ? 'text-zinc-500 hover:text-red-400 cursor-not-allowed' : 'text-zinc-400 hover:text-red-500'}`}
                                title={isUsed ? t('admin.categoryInUse') : t('common.delete')}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          )}
                          {isUsed && !isDefault && (
                            <div className="w-2 h-2 bg-amber-500 rounded-full" />
                          )}
                        </span>
                      </div>
                    );
                  })}
                  {uniqueCategories.length === 0 && (
                    <div className="w-full py-12 text-center bg-zinc-50 dark:bg-zinc-800 rounded-[2rem] border-2 border-dashed border-zinc-200 dark:border-zinc-700">
                      <p className="text-xs text-zinc-400 dark:text-zinc-300 font-bold uppercase tracking-widest">No categories yet</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl overflow-hidden">
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                        <th className="p-6 font-black text-zinc-400 dark:text-zinc-300 text-[10px] sm:text-xs uppercase tracking-widest">{t('common.name') || 'Name'}</th>
                        <th className="p-6 font-black text-zinc-400 dark:text-zinc-300 text-[10px] sm:text-xs uppercase tracking-widest">{t('common.price') || 'Price'}</th>
                        <th className="p-6 font-black text-zinc-400 dark:text-zinc-300 text-[10px] sm:text-xs uppercase tracking-widest">{t('common.duration') || 'Duration'}</th>
                        <th className="p-6 font-black text-zinc-400 dark:text-zinc-300 text-[10px] sm:text-xs uppercase tracking-widest">{t('common.category') || 'Category'}</th>
                        <th className="p-6 font-black text-zinc-400 dark:text-zinc-300 text-[10px] sm:text-xs uppercase tracking-widest text-right">{t('common.actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                      <AnimatePresence mode="popLayout">
                        {filteredServices.map((service) => (
                          <motion.tr 
                            layout
                            key={service.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="hover:bg-zinc-50/50 dark:hover:bg-zinc-800/50 transition-colors group"
                          >
                            <td className="p-6">
                              <div className="flex items-center gap-4">
                                <div className="p-3 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-xl">
                                  <Scissors size={18} />
                                </div>
                                <div>
                                  <div className="font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{service.name}</div>
                                  <div className="text-xs text-zinc-400 dark:text-zinc-300 font-medium line-clamp-1 max-w-xs">{service.description}</div>
                                </div>
                              </div>
                            </td>
                            <td className="p-6 font-black text-zinc-900 dark:text-white">{t('common.currency') || 'CHF'}{service.price}</td>
                            <td className="p-6 font-black text-zinc-400 dark:text-zinc-300 text-xs">{service.duration} {t('common.min') || 'min'}</td>
                            <td className="p-6">
                              <span className="px-3 py-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-[9px] font-black uppercase tracking-widest rounded-full">
                                {service.category}
                              </span>
                            </td>
                            <td className="p-6 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button 
                                  onClick={() => setEditingService(service)}
                                  className="p-3 text-zinc-300 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-all"
                                >
                                  <Edit2 size={18} />
                                </button>
                                <button 
                                  onClick={() => handleDeleteService(service.id)}
                                  className="p-3 text-zinc-300 dark:text-zinc-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                  {filteredServices.length === 0 && (
                    <div className="p-20 text-center">
                      <div className="w-20 h-20 bg-zinc-50 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Scissors className="text-zinc-200 dark:text-zinc-300" size={40} />
                      </div>
                      <h3 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white mb-2">No Services Found</h3>
                      <p className="text-zinc-400 dark:text-zinc-300 font-medium">Try adjusting your search or filters.</p>
                    </div>
                  )}
                </div>

                {/* Mobile Services List */}
                <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                  {filteredServices.map((service) => (
                    <div key={service.id} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-800 space-y-4">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-4">
                          <div className="p-3 bg-white dark:bg-zinc-900 rounded-xl shadow-sm h-fit">
                            <Scissors size={18} className="text-zinc-900 dark:text-white" />
                          </div>
                          <div>
                            <div className="font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{service.name}</div>
                            <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{service.category}</div>
                          </div>
                        </div>
                        <div className="text-lg font-black text-emerald-500">{t('common.currency')}{service.price}</div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-zinc-100 dark:border-zinc-800">
                        <div className="flex items-center gap-2 text-xs font-bold text-zinc-500">
                          <Clock size={14} />
                          {service.duration} min
                        </div>
                        <div className="flex gap-2">
                           <button 
                            onClick={() => setEditingService(service)}
                            className="p-2 bg-white dark:bg-zinc-900 text-zinc-500 rounded-lg shadow-sm"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            onClick={() => handleDeleteService(service.id)}
                            className="p-2 bg-white dark:bg-zinc-900 text-red-500 rounded-lg shadow-sm"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Edit Service Modal */}
              <AnimatePresence>
                {editingService && (
                  <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => setEditingService(null)}
                      className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
                    />
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: 20 }}
                      className="relative w-full max-w-xl bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-zinc-100 dark:border-zinc-800 transition-colors"
                    >
                      <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                        <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('common.edit')} <span className="text-zinc-400 dark:text-zinc-300">{t('admin.services')}</span></h2>
                        <button onClick={() => setEditingService(null)} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors">
                          <X size={20} className="text-zinc-400 dark:text-zinc-300" />
                        </button>
                      </div>
                      <form onSubmit={handleUpdateService} className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar scroll-smooth">
                        <div className="space-y-4">
                          <div className="space-y-1">
                            <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.serviceName')}</label>
                            <input 
                              required
                              className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                              value={editingService.name}
                              onChange={(e) => setEditingService({...editingService, name: e.target.value})}
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.description')}</label>
                            <textarea 
                              className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-bold text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none h-24 resize-none text-zinc-900 dark:text-zinc-300 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                              value={editingService.description}
                              onChange={(e) => setEditingService({...editingService, description: e.target.value})}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.price')}</label>
                              <div className="relative">
                                <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-zinc-400 dark:text-zinc-300">{t('common.currency')}</span>
                                <input 
                                  required
                                  type="number"
                                  className="w-full pl-20 pr-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                                  value={editingService.price}
                                  onChange={(e) => setEditingService({...editingService, price: Number(e.target.value)})}
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('common.duration')}</label>
                              <input 
                                required
                                type="number"
                                className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                                value={editingService.duration}
                                onChange={(e) => setEditingService({...editingService, duration: Number(e.target.value)})}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.category')}</label>
                            <div className="flex gap-2">
                              {!isCreatingNewCategoryInEditForm ? (
                                <select 
                                  className="flex-1 px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none appearance-none text-zinc-900 dark:text-white"
                                  value={editingService.category}
                                  onChange={(e) => {
                                    if (e.target.value === 'ADD_NEW') {
                                      setIsCreatingNewCategoryInEditForm(true);
                                      setEditingService({...editingService, category: ''});
                                    } else {
                                      setEditingService({...editingService, category: e.target.value});
                                    }
                                  }}
                                >
                                  {uniqueCategories.map(cat => (
                                    <option key={cat} value={cat} className="dark:bg-zinc-900">{cat}</option>
                                  ))}
                                  <option value="ADD_NEW" className="text-zinc-400 dark:text-zinc-300 font-bold italic dark:bg-zinc-900">+ {t('admin.addNewCategory')}</option>
                                </select>
                              ) : (
                                <div className="flex-1 flex gap-2">
                                  <input 
                                    autoFocus
                                    placeholder={t('admin.category')}
                                    className="flex-1 px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                                    value={editingService.category}
                                    onChange={(e) => setEditingService({...editingService, category: e.target.value})}
                                  />
                                  <button 
                                    type="button"
                                    onClick={() => {
                                      setIsCreatingNewCategoryInEditForm(false);
                                      setEditingService({...editingService, category: uniqueCategories[0] || ''});
                                    }}
                                    className="px-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 rounded-2xl hover:text-zinc-600 dark:hover:text-zinc-400 transition-colors"
                                  >
                                    <X size={18} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-4 pt-4">
                          <button 
                            type="submit"
                            disabled={isSubmittingService}
                            className="flex-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-4 rounded-2xl font-black text-sm shadow-xl shadow-zinc-200 dark:shadow-black/50 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all flex items-center justify-center gap-2"
                          >
                            {isSubmittingService && <LoadingSpinner />}
                            {t('common.save')}
                          </button>
                          <button 
                            type="button"
                            onClick={() => setEditingService(null)}
                            disabled={isSubmittingService}
                            className="flex-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 py-4 rounded-2xl font-black text-sm hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all disabled:opacity-50"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </form>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>

            </div>
          )}

          {activeTab === 'shops' && isSuperAdmin && (
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.barber') || 'Barber'} <span className="text-zinc-400 dark:text-zinc-300">{t('admin.shops') || 'Shops'}</span></h2>
                <button 
                  onClick={() => setIsAddingShop(true)}
                  className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-8 py-4 rounded-2xl font-black flex items-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50"
                >
                  <Plus size={20} /> {t('admin.addShop') || 'Add Shop'}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">


                {shops.map((shop) => (
                  <motion.div 
                    layout
                    key={shop.id}
                    className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl relative group"
                  >
                    <div className="flex justify-between items-start mb-6">
                      <div className="p-4 bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-2xl">
                        <Store size={24} />
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-black uppercase tracking-tighter mb-2 text-zinc-900 dark:text-white">{shop.name}</h3>
                    <p className="text-zinc-500 dark:text-zinc-300 text-sm font-medium leading-relaxed mb-4">{shop.address}</p>
                    
                    <div className="flex items-center gap-4 mb-6">
                      <div className="flex items-center gap-1.5">
                        <Clock size={14} className="text-zinc-400 dark:text-zinc-300" />
                        <span className="text-xs font-black text-zinc-900 dark:text-white">{shop.opening_time || '09:00'}</span>
                      </div>
                      <div className="w-1 h-1 bg-zinc-200 dark:bg-zinc-800 rounded-full" />
                      <div className="flex items-center gap-1.5">
                        <Clock size={14} className="text-zinc-400 dark:text-zinc-300" />
                        <span className="text-xs font-black text-zinc-900 dark:text-white">{shop.closing_time || '18:00'}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="px-3 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-300 text-[9px] font-black uppercase tracking-widest rounded-full">
                        {format(parseISO(shop.created_at), 'MMM yyyy')}
                      </span>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => {
                            setSelectedShopId(shop.id);
                            setActiveTab('settings');
                          }}
                          className="p-3 text-zinc-300 dark:text-zinc-300 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                        >
                          <Pencil size={18} />
                        </button>
                        <button 
                          onClick={() => handleDeleteShop(shop.id)}
                          className="p-3 text-zinc-300 dark:text-zinc-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'roles' && isSuperAdmin && (
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={18} />
                    <input 
                      type="text" 
                      placeholder={t('common.search')}
                      className="w-full pl-12 pr-6 py-3 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-sm shadow-sm text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500"
                      value={roleSearchQuery}
                      onChange={(e) => setRoleSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex w-full sm:w-auto gap-4">
                    <select 
                      className="flex-1 sm:w-auto px-6 py-3 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-sm shadow-sm text-zinc-900 dark:text-white appearance-none pr-10 relative"
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value)}
                    >
                      <option value="all" className="dark:bg-zinc-900">{t('common.all')}</option>
                      <option value="super_admin" className="dark:bg-zinc-900">{t('admin.superAdmin')}</option>
                      <option value="barber_admin" className="dark:bg-zinc-900">{t('admin.barberAdmin')}</option>
                    </select>
                    <select 
                      className="flex-1 sm:w-auto px-6 py-3 bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-sm shadow-sm text-zinc-900 dark:text-white appearance-none pr-10 relative"
                      value={teamShopFilter}
                      onChange={(e) => setTeamShopFilter(e.target.value)}
                    >
                      <option value="all" className="dark:bg-zinc-900">{t('admin.allShops')}</option>
                      <option value="none" className="dark:bg-zinc-900">{t('admin.noShopAssigned')}</option>
                      {shops.map(shop => (
                        <option key={shop.id} value={shop.id} className="dark:bg-zinc-900">{shop.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="text-right w-full md:w-auto">
                  <h2 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.user')} <span className="text-amber-500">{t('admin.roles')}</span></h2>
                  <p className="text-zinc-500 dark:text-zinc-300 font-bold uppercase tracking-widest text-xs">{t('admin.manageSystemRoles')}</p>
                </div>
              </div>

              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50 border-b border-zinc-100 dark:border-zinc-800">
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.user')}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.currentRole')}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.barberShop')}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 text-right">{t('admin.actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                      {filteredRoles.map((p) => (
                        <tr key={p.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                          <td className="p-6">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-xl flex items-center justify-center font-black">
                                {p.email?.[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="text-sm font-black text-zinc-900 dark:text-white">{p.email}</div>
                                <div className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300 uppercase tracking-widest">
                                  {t('admin.joined')} {format(parseISO(p.created_at), 'MMM yyyy')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="p-6">
                            <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                              p.role === 'super_admin' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' :
                              p.role === 'barber_admin' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                              p.role === 'barber_staff' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                              'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-300'
                            }`}>
                              {p.role === 'super_admin' ? t('admin.superAdmin') :
                               p.role === 'barber_admin' ? t('admin.barberAdmin') :
                               p.role === 'barber_staff' ? t('admin.barberStaff') :
                               t('admin.customerRole')}
                            </span>
                          </td>
                          <td className="p-6">
                            <div className="flex items-center gap-2 text-sm font-bold text-zinc-500 dark:text-zinc-300">
                              <Store size={14} className="text-zinc-300 dark:text-zinc-300" />
                              {shops.find(s => s.id === p.barber_shop_id)?.name || t('admin.noShopAssigned')}
                            </div>
                          </td>
                          <td className="p-6">
                            <div className="flex items-center justify-end gap-3">
                              {p.id !== profile?.id ? (
                                <>
                                  <select 
                                    className="bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-xl px-4 py-2 text-xs font-bold outline-none focus:ring-2 ring-zinc-900 dark:ring-amber-500 text-zinc-900 dark:text-white"
                                    value={p.role}
                                    onChange={(e) => handleUpdateRole(p.id, e.target.value, p.barber_shop_id || undefined)}
                                  >
                                    <option value="barber_admin" className="dark:bg-zinc-800">{t('admin.barberAdmin')}</option>
                                    <option value="super_admin" className="dark:bg-zinc-800">{t('admin.superAdmin')}</option>
                                  </select>
                                  <select 
                                    className="bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-xl px-4 py-2 text-xs font-bold outline-none focus:ring-2 ring-zinc-900 dark:ring-amber-500 text-zinc-900 dark:text-white max-w-[150px]"
                                    value={p.barber_shop_id || ''}
                                    onChange={(e) => handleUpdateRole(p.id, p.role, e.target.value || null)}
                                  >
                                    <option value="" className="dark:bg-zinc-800">{t('admin.noShop')}</option>
                                    {shops.map(shop => (
                                      <option key={shop.id} value={shop.id} className="dark:bg-zinc-800">{shop.name}</option>
                                    ))}
                                  </select>
                                </>
                              ) : (
                                <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-300 dark:text-zinc-300 italic">{t('admin.currentUser')}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'settings' && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white dark:bg-zinc-900 p-12 rounded-[3.5rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl shadow-zinc-200/50 dark:shadow-black/50">
                <div className="flex items-center gap-4 mb-10">
                  <div className="p-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl">
                    <Store size={24} />
                  </div>
                  <div>
                    <h2 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
                      {shops.find(s => s.id === selectedShopId)?.name || t('admin.shop')}{' '}
                      <span className="text-zinc-400 dark:text-zinc-300">{t('admin.settings') || 'Settings'}</span>
                    </h2>
                    <p className="text-zinc-400 dark:text-zinc-300 font-bold text-xs uppercase tracking-widest">{t('admin.manageShopDetails') || 'Manage your shop details and preferences'}</p>
                  </div>
                </div>

                {selectedShopId ? (
                  <form onSubmit={handleUpdateShop} className="space-y-6">
                    {/* Status Section for Shop Admins */}
                    {isAdmin && (
                      <div className="flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800 mb-6">
                        <div>
                          <p className="font-black text-sm uppercase tracking-tight text-zinc-900 dark:text-white">{t('admin.shopStatus') || 'Shop Status'}</p>
                          <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300 uppercase tracking-widest">{t('admin.activateDeactivateShop') || 'Enable or disable this shop'}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newShops = shops.map(s => s.id === selectedShopId ? {...s, is_active: !s.is_active} : s);
                            setShops(newShops);
                          }}
                          className={`w-14 h-8 rounded-full transition-all relative ${shops.find(s => s.id === selectedShopId)?.is_active !== false ? 'bg-emerald-500' : 'bg-red-500'}`}
                        >
                          <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${shops.find(s => s.id === selectedShopId)?.is_active !== false ? 'left-7' : 'left-1'}`} />
                        </button>
                      </div>
                    )}

                    {/* General Info Section */}
                    <div className="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setExpandedSettings(prev => ({ ...prev, general: !prev.general }))}
                        className="w-full flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Store size={18} className="text-zinc-900 dark:text-white" />
                          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('admin.general') || 'General Info'}</h3>
                        </div>
                        {expandedSettings.general ? <ChevronUp size={18} className="text-zinc-400 dark:text-zinc-300" /> : <ChevronDown size={18} className="text-zinc-400 dark:text-zinc-300" />}
                      </button>
                      
                      <AnimatePresence>
                        {expandedSettings.general && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 space-y-6 bg-white dark:bg-zinc-900">
                              <div className="space-y-2">
                                <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.shopName') || 'Shop Name'}</label>
                                <input 
                                  disabled={!isAdmin}
                                  required
                                  className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none disabled:opacity-50 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                                  value={shops.find(s => s.id === selectedShopId)?.name || ''}
                                  onChange={(e) => {
                                    const newShops = shops.map(s => s.id === selectedShopId ? {...s, name: e.target.value} : s);
                                    setShops(newShops);
                                  }}
                                />
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('booking.phoneNumber') || 'Phone Number'}</label>
                                  <input 
                                    disabled={!isAdmin}
                                    required
                                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none disabled:opacity-50 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                                    value={shops.find(s => s.id === selectedShopId)?.phone || ''}
                                    onChange={(e) => {
                                      const newShops = shops.map(s => s.id === selectedShopId ? {...s, phone: e.target.value} : s);
                                      setShops(newShops);
                                    }}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('booking.emailAddress') || 'Email Address'}</label>
                                  <input 
                                    disabled={!isAdmin}
                                    type="email"
                                    required
                                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none disabled:opacity-50 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                                    value={shops.find(s => s.id === selectedShopId)?.email || ''}
                                    onChange={(e) => {
                                      const newShops = shops.map(s => s.id === selectedShopId ? {...s, email: e.target.value} : s);
                                      setShops(newShops);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Address & Location Section */}
                    <div className="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden mt-4">
                      <button
                        type="button"
                        onClick={() => setExpandedSettings(prev => ({ ...prev, location: !prev.location }))}
                        className="w-full flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <MapPin size={18} className="text-zinc-900 dark:text-white" />
                          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('admin.addressLocation') || 'Address & Location'}</h3>
                        </div>
                        {expandedSettings.location ? <ChevronUp size={18} className="text-zinc-400 dark:text-zinc-300" /> : <ChevronDown size={18} className="text-zinc-400 dark:text-zinc-300" />}
                      </button>
                      
                      <AnimatePresence>
                        {expandedSettings.location && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 space-y-6 bg-white dark:bg-zinc-900">
                              <div className="space-y-2">
                                <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.address') || 'Address'}</label>
                                <textarea 
                                  disabled={!isAdmin}
                                  required
                                  className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none h-32 resize-none disabled:opacity-50 text-zinc-900 dark:text-zinc-300"
                                  value={shops.find(s => s.id === selectedShopId)?.address || ''}
                                  onChange={(e) => {
                                    const newShops = shops.map(s => s.id === selectedShopId ? {...s, address: e.target.value} : s);
                                    setShops(newShops);
                                  }}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.latitude') || 'Latitude'}</label>
                                  <input 
                                    disabled={!isAdmin}
                                    type="number"
                                    step="any"
                                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none disabled:opacity-50 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                                    value={shops.find(s => s.id === selectedShopId)?.latitude ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                      const newShops = shops.map(s => s.id === selectedShopId ? {...s, latitude: val} : s);
                                      setShops(newShops);
                                    }}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.longitude') || 'Longitude'}</label>
                                  <input 
                                    disabled={!isAdmin}
                                    type="number"
                                    step="any"
                                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none disabled:opacity-50 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                                    value={shops.find(s => s.id === selectedShopId)?.longitude ?? ''}
                                    onChange={(e) => {
                                      const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                                      const newShops = shops.map(s => s.id === selectedShopId ? {...s, longitude: val} : s);
                                      setShops(newShops);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Operating Hours Section */}
                    <div className="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden mt-4">
                      <button
                        type="button"
                        onClick={() => setExpandedSettings(prev => ({ ...prev, hours: !prev.hours }))}
                        className="w-full flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Clock size={18} className="text-zinc-900 dark:text-white" />
                          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('admin.operatingHours') || 'Operating Hours'}</h3>
                        </div>
                        {expandedSettings.hours ? <ChevronUp size={18} className="text-zinc-400 dark:text-zinc-300" /> : <ChevronDown size={18} className="text-zinc-400 dark:text-zinc-300" />}
                      </button>
                      
                      <AnimatePresence>
                        {expandedSettings.hours && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 bg-white dark:bg-zinc-900">
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.openingTime') || 'Opening Time'}</label>
                                  <div className="relative">
                                    <Clock className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={18} />
                                    <ModernTimePicker 
                                      className={`w-full pl-16 pr-8 py-5 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white ${!isAdmin ? 'opacity-50 pointer-events-none' : ''}`}
                                      value={shops.find(s => s.id === selectedShopId)?.opening_time || '09:00'}
                                      onChange={(value) => {
                                        const newShops = shops.map(s => s.id === selectedShopId ? {...s, opening_time: value} : s);
                                        setShops(newShops);
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.closingTime') || 'Closing Time'}</label>
                                  <div className="relative">
                                    <Clock className="absolute left-6 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300" size={18} />
                                    <ModernTimePicker 
                                      className={`w-full pl-16 pr-8 py-5 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white ${!isAdmin ? 'opacity-50 pointer-events-none' : ''}`}
                                      value={shops.find(s => s.id === selectedShopId)?.closing_time || '18:00'}
                                      onChange={(value) => {
                                        const newShops = shops.map(s => s.id === selectedShopId ? {...s, closing_time: value} : s);
                                        setShops(newShops);
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Booking & Reminders Section */}
                    <div className="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden mt-4">
                      <button
                        type="button"
                        onClick={() => setExpandedSettings(prev => ({ ...prev, reminders: !prev.reminders }))}
                        className="w-full flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Bell size={18} className="text-zinc-900 dark:text-white" />
                          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('admin.bookingReminders') || 'Booking & Reminders'}</h3>
                        </div>
                        {expandedSettings.reminders ? <ChevronUp size={18} className="text-zinc-400 dark:text-zinc-300" /> : <ChevronDown size={18} className="text-zinc-400 dark:text-zinc-300" />}
                      </button>
                      
                      <AnimatePresence>
                        {expandedSettings.reminders && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 space-y-6 bg-white dark:bg-zinc-900">
                              <div className="flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl">
                                <div>
                                  <p className="font-black text-sm uppercase tracking-tight text-zinc-900 dark:text-white">{t('admin.enableReminders') || 'Enable Reminders'}</p>
                                  <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-300 uppercase tracking-widest">{t('admin.enableRemindersDesc') || 'Send automatic reminders to customers'}</p>
                                </div>
                                <button
                                  type="button"
                                  disabled={!isAdmin}
                                  onClick={() => {
                                    const shop = shops.find(s => s.id === selectedShopId);
                                    if (!shop) return;
                                    const newShops = shops.map(s => s.id === selectedShopId ? {...s, reminder_enabled: !s.reminder_enabled} : s);
                                    setShops(newShops);
                                  }}
                                  className={`w-14 h-8 rounded-full transition-all relative ${shops.find(s => s.id === selectedShopId)?.reminder_enabled ? 'bg-zinc-900 dark:bg-white' : 'bg-zinc-200 dark:bg-zinc-800'}`}
                                >
                                  <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${shops.find(s => s.id === selectedShopId)?.reminder_enabled ? 'left-7' : 'left-1'}`} />
                                </button>
                              </div>

                              {shops.find(s => s.id === selectedShopId)?.reminder_enabled && (
                                <motion.div 
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  className="space-y-6"
                                >
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.reminderType') || 'Reminder Type'}</label>
                                      <select
                                        disabled={!isAdmin}
                                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-bold text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none disabled:opacity-50 text-zinc-900 dark:text-white"
                                        value={shops.find(s => s.id === selectedShopId)?.reminder_type || 'email'}
                                        onChange={(e) => {
                                          const newShops = shops.map(s => s.id === selectedShopId ? {...s, reminder_type: e.target.value as any} : s);
                                          setShops(newShops);
                                        }}
                                      >
                                        <option value="email" className="dark:bg-zinc-900">{t('admin.emailOnly') || 'Email Only'}</option>
                                      </select>
                                      <p className="text-[9px] font-bold text-zinc-400 dark:text-zinc-300 uppercase tracking-widest ml-4 mt-1">
                                        {t('admin.reminderTypeDesc') || 'Choose how customers receive reminders'}
                                      </p>
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.hoursBefore') || 'Hours Before'}</label>
                                      <input
                                        disabled={!isAdmin}
                                        type="number"
                                        min="1"
                                        max="72"
                                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-bold text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none disabled:opacity-50 text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400"
                                        value={shops.find(s => s.id === selectedShopId)?.reminder_hours_before || 24}
                                        onChange={(e) => {
                                          const newShops = shops.map(s => s.id === selectedShopId ? {...s, reminder_hours_before: parseInt(e.target.value)} : s);
                                          setShops(newShops);
                                        }}
                                      />
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={handleSendReminders}
                                    disabled={loading}
                                    className="w-full py-4 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all disabled:opacity-50"
                                  >
                                    <Send size={14} />
                                    {loading ? (t('admin.sendingReminders') || 'Sending...') : (t('admin.sendRemindersManual') || 'Send Reminders Now')}
                                  </button>
                                </motion.div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Subscription Section */}
                    <div className="border border-zinc-100 dark:border-zinc-800 rounded-2xl overflow-hidden mt-6">
                      <button
                        type="button"
                        onClick={() => setExpandedSettings(prev => ({ ...prev, subscription: !prev.subscription }))}
                        className="w-full flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <CreditCard size={18} className="text-zinc-900 dark:text-white" />
                          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900 dark:text-white">{t('admin.subscriptionManagement') || 'Subscription Management'}</h3>
                        </div>
                        {expandedSettings.subscription ? <ChevronUp size={18} className="text-zinc-400 dark:text-zinc-300" /> : <ChevronDown size={18} className="text-zinc-400 dark:text-zinc-300" />}
                      </button>
                      
                      <AnimatePresence>
                        {expandedSettings.subscription && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-8 space-y-6 bg-white dark:bg-zinc-900 border-t border-zinc-100 dark:border-zinc-800">
                              {(() => {
                                const activeSub = allSubscriptions.find(s => s.shop_id === selectedShopId) || (selectedShopId === profile?.barber_shop_id ? subscription : null);
                                
                                if (!activeSub) {
                                  return (
                                    <div className="text-center py-8">
                                      <p className="text-zinc-400 font-bold uppercase tracking-widest text-xs mb-4">No active subscription for this shop</p>
                                      <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8">
                                        <button
                                          onClick={() => handleCreateCheckoutSession(selectedShopId!, 'basic')}
                                          className="w-full md:w-auto px-8 py-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all border border-zinc-200 dark:border-zinc-700 shadow-sm"
                                        >
                                          Subscribe to Basic
                                        </button>
                                        <button
                                          onClick={() => handleCreateCheckoutSession(selectedShopId!, 'monthly')}
                                          className="w-full md:w-auto px-8 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-amber-600 transition-all shadow-lg"
                                        >
                                          Subscribe to Pro
                                        </button>
                                      </div>
                                      
                                      {isSuperAdmin && (
                                        <div className="mt-8 pt-8 border-t border-zinc-100 dark:border-zinc-800">
                                          <p className="text-zinc-400 font-bold uppercase tracking-widest text-[9px] mb-4">Super Admin Manual Override (No Billing)</p>
                                          <div className="flex flex-col gap-2 max-w-xs mx-auto">
                                            <button
                                              onClick={() => handleUpdateSubscription(selectedShopId!, 'update_plan', 'basic')}
                                              className="px-6 py-3 bg-zinc-50 dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 rounded-xl font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all border border-zinc-200 dark:border-zinc-800"
                                            >
                                              Force Basic Activate
                                            </button>
                                            <button
                                              onClick={() => handleUpdateSubscription(selectedShopId!, 'update_plan', 'monthly')}
                                              className="px-6 py-3 bg-zinc-50 dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 rounded-xl font-bold text-[10px] sm:text-xs uppercase tracking-widest hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all border border-zinc-200 dark:border-zinc-800"
                                            >
                                              Force Pro Activate
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }

                                return (
                                  <div className="space-y-6">
                                    <div className="flex items-center justify-between p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                                      <div>
                                        <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-1">{t('admin.currentPlan') || 'Current Plan'}</p>
                                        <h4 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
                                          {activeSub.status === 'trialing' ? 'Pro Trial' : ((activeSub.plan_id === 'basic' || activeSub.plan_id === 'basic_annual') ? 'Basic Barber Shop' : 'Pro Barber Shop')}
                                        </h4>
                                        <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-amber-500 mt-0.5">
                                          {(activeSub.plan_id === 'annual' || activeSub.plan_id === 'basic_annual') ? (t('pricing.annually') || 'Annually') : (t('pricing.monthly') || 'Monthly')}
                                        </p>
                                        <p className="text-xs font-bold text-zinc-500 dark:text-zinc-300 mt-1">
                                          {activeSub.current_period_end && (
                                            activeSub.status === 'trialing' 
                                              ? `${t('admin.trialEndsOn') || 'Trial ends on'} ${format(new Date(activeSub.current_period_end), 'PPP', { locale: dateLocale })}`
                                              : `${t('admin.renewsOn') || 'Renews on'} ${format(new Date(activeSub.current_period_end), 'PPP', { locale: dateLocale })}`
                                          )}
                                        </p>
                                      </div>
                                      <div className={`px-4 py-2 rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest ${
                                        activeSub.status === 'active' || activeSub.status === 'trialing'
                                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30'
                                          : 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900/30'
                                      }`}>
                                        {activeSub.status}
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {activeSub.stripe_customer_id && (
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            try {
                                              const response = await fetch('/api/create-portal-session', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ customerId: activeSub.stripe_customer_id })
                                              });
                                              const data = await response.json();
                                              if (data.url) {
                                                window.open(data.url, '_blank');
                                              } else {
                                                throw new Error(data.error || 'Failed to create portal session');
                                              }
                                            } catch (error: any) {
                                              toast.error(error.message);
                                            }
                                          }}
                                          className="w-full py-4 bg-zinc-900 dark:bg-zinc-800 text-white dark:text-zinc-300 rounded-2xl font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-700 transition-all border border-zinc-800 dark:border-zinc-700"
                                        >
                                          <ExternalLink size={16} />
                                          {t('admin.manageSubscription') || 'Stripe Portal'}
                                        </button>
                                      )}

                                      {isSuperAdmin && (
                                        <>
                                          <select
                                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-none rounded-2xl font-bold text-xs uppercase tracking-widest focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                                            value={activeSub.plan_id || ''}
                                            onChange={(e) => handleUpdateSubscription(selectedShopId!, 'update_plan', e.target.value)}
                                          >
                                            <option value="basic">Basic Monthly</option>
                                            <option value="basic_annual">Basic Annual</option>
                                            <option value="monthly">Pro Monthly</option>
                                            <option value="annual">Pro Annual</option>
                                          </select>

                                          <button
                                            onClick={() => handleUpdateSubscription(selectedShopId!, 'cancel')}
                                            className="w-full py-4 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-red-100 dark:hover:bg-red-900/20 transition-all border border-red-100 dark:border-red-900/20 col-span-full"
                                          >
                                            {t('admin.cancelShopSubscription') || 'Cancel Subscription (Manual)'}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                    
                                    <p className="text-[10px] text-center text-zinc-400 dark:text-zinc-300 font-bold uppercase tracking-widest">
                                      {activeSub.stripe_subscription_id ? 'Managed via Stripe' : 'Manually Managed'}
                                    </p>
                                  </div>
                                );
                              })()}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {isAdmin && (
                      <button 
                        type="submit"
                        disabled={isSubmittingShop}
                        className="w-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-5 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-zinc-200 dark:shadow-black/50 hover:bg-zinc-800 dark:hover:bg-amber-600 transition-all mt-8 flex items-center justify-center gap-2"
                      >
                        {isSubmittingShop && <LoadingSpinner />}
                        {t('admin.updateShopDetails')}
                      </button>
                    )}
                    
                    {!isAdmin && (
                      <div className="p-6 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800 mt-8">
                        <p className="text-xs font-bold text-zinc-500 dark:text-zinc-300 text-center">
                          {t('admin.onlyAdminsModify')}
                        </p>
                      </div>
                    )}
                  </form>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-zinc-400 dark:text-zinc-300 font-bold">{t('admin.noShopSelected')}</p>
                  </div>
                )}


              </div>
            </div>
          )}

          {/* Duplicate Staff Dashboard block removed */}

          {activeTab === 'analytics' && isAdmin && (() => {
            if (isSuperAdmin) {
              const activeSubscriptions = allSubscriptions.filter(s => s.status === 'active');
              
              const getPlanPrice = (plan: string | undefined): number => {
                if (!plan) return 0;
                if (plan === 'basic' || plan === 'monthly') return 99; // $99/mo standard
                if (plan === 'annual' || plan === 'basic_annual') return 990; // $990/yr standard
                return 0;
              };

              const calculateMRR = () => {
                return activeSubscriptions.reduce((sum, sub) => {
                  const price = getPlanPrice(sub.plan_id);
                  if (sub.plan_id?.includes('annual')) {
                    return sum + (price / 12);
                  }
                  return sum + price;
                }, 0);
              };

              const mrr = calculateMRR();
              const totalRevenue = allSubscriptions.reduce((sum, sub) => sum + getPlanPrice(sub.plan_id), 0);

              const last6Months = Array.from({ length: 6 }).map((_, i) => {
                const start = startOfMonth(subMonths(new Date(), 5 - i));
                const monthStr = format(start, 'MMM yyyy');
                
                // Active shops that month (roughly based on subscriptions created before end of that month)
                const monthEnd = endOfMonth(start);
                const activeInMonth = allSubscriptions.filter(s => 
                  new Date(s.created_at) <= monthEnd && 
                  (s.status === 'active' || (s.current_period_end && new Date(s.current_period_end) > monthEnd))
                );

                const revenueInMonth = activeInMonth.reduce((sum, sub) => {
                  const p = getPlanPrice(sub.plan_id);
                  if (sub.plan_id?.includes('annual')) return sum + (p / 12);
                  return sum + p;
                }, 0);

                return {
                  month: monthStr,
                  revenue: revenueInMonth,
                  shops: activeInMonth.length,
                };
              });

              return (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-8 rounded-[3rem] shadow-2xl shadow-amber-500/20 text-white relative overflow-hidden">
                      <div className="relative z-10">
                        <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-amber-100 mb-2">{t('admin.mrr') || 'Monthly Recurring Revenue'}</p>
                        <h3 className="text-4xl font-black">{t('common.currency') || 'CHF'}{Math.round(mrr).toLocaleString()}</h3>
                      </div>
                      <TrendingUp size={120} className="absolute -right-4 -bottom-4 text-white opacity-10" />
                    </div>
                    <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                      <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-2">{t('admin.activeShops') || 'Active Subscribed Shops'}</p>
                      <h3 className="text-4xl font-black text-zinc-900 dark:text-white">{activeSubscriptions.length}</h3>
                    </div>
                    <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                      <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-2">{t('admin.totalSales') || 'Total Platform Revenue'}</p>
                      <h3 className="text-4xl font-black text-zinc-900 dark:text-white">{t('common.currency') || 'CHF'}{Math.round(totalRevenue).toLocaleString()}</h3>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Platform Revenue Chart */}
                    <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                      <h3 className="text-xl font-black uppercase tracking-tighter mb-6 flex items-center gap-2 text-zinc-900 dark:text-white">
                        <TrendingUp size={20} className="text-amber-500" /> {t('admin.platformRevenue') || 'Platform Revenue (Last 6 Months)'}
                      </h3>
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={last6Months}>
                            <defs>
                              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#27272a' : '#f4f4f5'} />
                            <XAxis 
                              dataKey="month" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 10, fontWeight: 700, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                              dy={10}
                            />
                            <YAxis 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 10, fontWeight: 700, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                            />
                            <Tooltip 
                              contentStyle={{ 
                                borderRadius: '1rem', 
                                border: 'none', 
                                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                                backgroundColor: isDarkMode ? '#18181b' : '#fff',
                                color: isDarkMode ? '#fff' : '#000'
                              }}
                              labelStyle={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.05em', color: isDarkMode ? '#71717a' : '#a1a1aa' }}
                              itemStyle={{ color: isDarkMode ? '#fff' : '#000' }}
                            />
                            <Area type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={4} fillOpacity={1} fill="url(#colorRevenue)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Active Shops Chart */}
                    <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                      <h3 className="text-xl font-black uppercase tracking-tighter mb-6 flex items-center gap-2 text-zinc-900 dark:text-white">
                        <Store size={20} className="text-blue-500" /> {t('admin.shopGrowth') || 'Shop Growth (Last 6 Months)'}
                      </h3>
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={last6Months}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#27272a' : '#f4f4f5'} />
                            <XAxis 
                              dataKey="month" 
                              axisLine={false} 
                              tickLine={false} 
                              tick={{ fontSize: 10, fontWeight: 700, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                              dy={10}
                            />
                            <YAxis 
                              axisLine={false} 
                              tickLine={false} 
                              allowDecimals={false}
                              tick={{ fontSize: 10, fontWeight: 700, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                            />
                            <Tooltip 
                              cursor={{ fill: isDarkMode ? '#27272a' : '#f8fafc' }}
                              contentStyle={{ 
                                borderRadius: '1rem', 
                                border: 'none', 
                                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                                backgroundColor: isDarkMode ? '#18181b' : '#fff',
                                color: isDarkMode ? '#fff' : '#000'
                              }}
                              labelStyle={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.05em', color: isDarkMode ? '#71717a' : '#a1a1aa' }}
                              itemStyle={{ color: isDarkMode ? '#fff' : '#000' }}
                            />
                            <Bar dataKey="shops" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={40} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            if (!shopAnalytics) return null;

            const { revenueData, dayOfWeekData, servicePopularity, barberPopularity, totalRevenue, totalBookings, avgRating, activeCustomers, hasData } = shopAnalytics;

            const COLORS = ['#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

            return (
              <div className="space-y-8 animate-in fade-in duration-700">
                {!hasData && (
                  <div className="p-6 bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-200/20 dark:border-amber-900/30 rounded-[2rem] flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-500 shrink-0">
                      <AlertCircle size={24} />
                    </div>
                    <div>
                      <h4 className="font-black text-amber-900 dark:text-amber-200 text-sm uppercase tracking-tight">{t('admin.previewMode') || 'Preview Mode'}</h4>
                      <p className="text-xs font-bold text-amber-700/80 dark:text-amber-300/80">
                        {t('admin.usingSampleData') || 'No reservation data available yet. Showing sample analytics to illustrate the dashboard functionality.'}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 group hover:scale-[1.02] transition-all duration-500">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-2">{t('admin.totalRevenue') || 'Total Revenue'}</p>
                    <div className="flex items-end gap-2">
                      <h3 className="text-4xl font-black text-zinc-900 dark:text-white leading-none tracking-tighter">{t('common.currency') || 'CHF'}{totalRevenue.toLocaleString()}</h3>
                      <div className="h-6 w-px bg-zinc-100 dark:bg-zinc-800 mb-2" />
                      <span className="text-[10px] font-black text-emerald-500 mb-2 uppercase tracking-widest">+12%</span>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 group hover:scale-[1.02] transition-all duration-500">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-2">{t('admin.totalBookings') || 'Total Bookings'}</p>
                    <div className="flex items-end gap-2">
                      <h3 className="text-4xl font-black text-zinc-900 dark:text-white leading-none tracking-tighter">{totalBookings}</h3>
                      <div className="h-6 w-px bg-zinc-100 dark:bg-zinc-800 mb-2" />
                      <span className="text-[10px] font-black text-amber-500 mb-2 uppercase tracking-widest">+5%</span>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 group hover:scale-[1.02] transition-all duration-500">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-2">{t('admin.avgRating') || 'Avg Rating'}</p>
                    <div className="flex items-center gap-2">
                       <h3 className="text-4xl font-black text-zinc-900 dark:text-white leading-none tracking-tighter">{avgRating.toFixed(1)}</h3>
                       <div className="flex gap-0.5 ml-1">
                         {[1, 2, 3, 4, 5].map(i => (
                           <Star key={i} size={12} fill={i <= Math.round(avgRating) ? '#f59e0b' : 'none'} className={i <= Math.round(avgRating) ? 'text-amber-500' : 'text-zinc-200 dark:text-zinc-700'} />
                         ))}
                       </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 p-8 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 group hover:scale-[1.02] transition-all duration-500">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-2">{t('admin.activeCustomers') || 'Active Customers'}</p>
                    <h3 className="text-4xl font-black text-zinc-900 dark:text-white leading-none tracking-tighter">{activeCustomers}</h3>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Revenue Growth Chart */}
                  <div className="lg:col-span-2 bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
                       <div>
                         <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-3 text-zinc-900 dark:text-white">
                           <TrendingUp size={24} className="text-amber-500" /> {t('admin.revenueTrend') || 'Revenue & Performance Trend'}
                         </h3>
                         <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.2em] mt-1 ml-9">{t('common.last30Days') || 'Snapshot of the last 30 days'}</p>
                       </div>
                       <div className="flex items-center gap-6 px-6 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                          <div className="flex items-center gap-2">
                             <div className="w-2.5 h-2.5 rounded-full bg-amber-500 shadow-sm shadow-amber-500/50" />
                             <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400">{t('admin.revenue') || 'Revenue'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                             <div className="w-2.5 h-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
                             <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400">{t('admin.bookings') || 'Bookings'}</span>
                          </div>
                       </div>
                    </div>
                    <div className="h-[350px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="date" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fontWeight: 900, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                            dy={10}
                          />
                          <YAxis 
                            yAxisId="left"
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fontWeight: 900, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                          />
                          <YAxis 
                            yAxisId="right"
                            orientation="right"
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fontWeight: 900, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              borderRadius: '1.5rem', 
                              border: 'none', 
                              boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.5)',
                              backgroundColor: isDarkMode ? '#18181b' : '#fff',
                              color: isDarkMode ? '#fff' : '#000',
                              padding: '1.25rem'
                            }}
                            labelStyle={{ fontWeight: 900, textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.1em', color: isDarkMode ? '#a1a1aa' : '#71717a', marginBottom: '0.5rem' }}
                            itemStyle={{ fontWeight: 900, fontSize: '13px' }}
                            cursor={{ stroke: isDarkMode ? '#3f3f46' : '#e4e4e7', strokeWidth: 2 }}
                          />
                          <Area 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="revenue" 
                            stroke="#f59e0b" 
                            strokeWidth={4} 
                            fillOpacity={1} 
                            fill="url(#colorRevenue)" 
                            animationDuration={2000}
                          />
                          <Area 
                            yAxisId="right"
                            type="monotone" 
                            dataKey="bookings" 
                            stroke="#3b82f6" 
                            strokeWidth={3} 
                            strokeDasharray="5 5"
                            fillOpacity={1} 
                            fill="url(#colorBookings)" 
                            animationDuration={2000}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Bookings by Day Chart */}
                  <div className="bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                    <h3 className="text-xl font-black uppercase tracking-tighter mb-1 flex items-center gap-3 text-zinc-900 dark:text-white">
                      <CalendarIcon size={24} className="text-blue-500" /> {t('admin.weeklyActivity') || 'Weekly Activity'}
                    </h3>
                    <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.2em] mb-10 ml-9">{t('admin.busyHours') || 'Busiest times of the week'}</p>
                    
                    <div className="h-[350px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dayOfWeekData} margin={{ top: 0, right: 0, left: -25, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDarkMode ? '#27272a' : '#f4f4f5'} />
                          <XAxis 
                            dataKey="name" 
                            axisLine={false} 
                            tickLine={false} 
                            tick={({ x, y, payload }) => (
                              <text x={x} y={Number(y) + 15} textAnchor="middle" className="fill-zinc-400 dark:fill-zinc-500 text-[10px] font-black uppercase tracking-tighter">
                                {payload.value.substring(0, 3)}
                              </text>
                            )}
                          />
                          <YAxis 
                            axisLine={false} 
                            tickLine={false} 
                            tick={{ fontSize: 10, fontWeight: 900, fill: isDarkMode ? '#71717a' : '#a1a1aa' }}
                          />
                          <Tooltip 
                            cursor={{ fill: isDarkMode ? '#27272a' : '#f8fafc', opacity: 0.5 }}
                            contentStyle={{ 
                              borderRadius: '1.25rem', 
                              border: 'none', 
                              boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                              backgroundColor: isDarkMode ? '#000' : '#fff',
                            }}
                          />
                          <Bar dataKey="bookings" radius={[6, 6, 0, 0]} barSize={24} animationDuration={2000}>
                            {dayOfWeekData.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.bookings === Math.max(...dayOfWeekData.map(d => d.bookings)) ? '#3b82f6' : isDarkMode ? '#27272a' : '#f4f4f5'} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Service Popularity */}
                  <div className="bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                    <h3 className="text-xl font-black uppercase tracking-tighter mb-1 flex items-center gap-3 text-zinc-900 dark:text-white">
                      <Scissors size={24} className="text-purple-500" /> {t('admin.popularServices') || 'Popular Services'}
                    </h3>
                    <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.2em] mb-10 ml-9">{t('admin.serviceMix') || 'Breakdown by booking volume'}</p>
                    
                    <div className="flex flex-col md:flex-row items-center gap-10">
                      <div className="h-[280px] w-full max-w-[280px] shrink-0">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={servicePopularity}
                              cx="50%"
                              cy="50%"
                              innerRadius={70}
                              outerRadius={100}
                              paddingAngle={8}
                              dataKey="value"
                              stroke="none"
                              animationDuration={2000}
                            >
                              {servicePopularity.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ 
                                borderRadius: '1rem', 
                                border: 'none', 
                                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)',
                                backgroundColor: isDarkMode ? '#18181b' : '#fff',
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 space-y-4 w-full">
                         {servicePopularity.map((service, index) => {
                           const total = servicePopularity.reduce((sum, s) => sum + s.value, 0);
                           const percent = total > 0 ? (service.value / total) * 100 : 0;
                           return (
                             <div key={`${service.name}-${index}`} className="flex flex-col gap-1.5">
                                <div className="flex justify-between items-center px-1">
                                   <div className="flex items-center gap-2">
                                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                                      <span className="text-xs font-black text-zinc-900 dark:text-white uppercase tracking-tight">{service.name}</span>
                                   </div>
                                   <span className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 tracking-widest">{percent.toFixed(0)}%</span>
                                </div>
                                <div className="h-2 bg-zinc-50 dark:bg-zinc-800 rounded-full overflow-hidden">
                                  <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: `${percent}%` }}
                                    transition={{ duration: 1.5, delay: index * 0.1 }}
                                    className="h-full rounded-full"
                                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                  />
                                </div>
                             </div>
                           );
                         })}
                      </div>
                    </div>
                  </div>

                  {/* Barber Performance Leaderboard */}
                  <div className="bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                    <h3 className="text-xl font-black uppercase tracking-tighter mb-1 flex items-center gap-3 text-zinc-900 dark:text-white">
                      <Users size={24} className="text-emerald-500" /> {t('admin.barberPerformance') || 'Performance Leaderboard'}
                    </h3>
                    <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.2em] mb-10 ml-9">{t('admin.basedOnBookings') || 'Ranked by booking volume'}</p>
                    
                    <div className="space-y-6">
                      {barberPopularity.map((barber, index) => {
                        const maxBookings = Math.max(...barberPopularity.map(b => b.bookings));
                        const percentage = (barber.bookings / maxBookings) * 100;
                        const realBarber = barbers.find(b => b.name === barber.name);
                        
                        return (
                          <div key={`${barber.name}-${index}`} className="flex items-center gap-6 group">
                            <div className="relative">
                               <div className="w-14 h-14 rounded-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 border-2 border-zinc-50 dark:border-zinc-700 shadow-sm shrink-0 group-hover:scale-110 transition-transform">
                                 {realBarber?.photo_url ? (
                                   <img src={realBarber.photo_url} alt={barber.name} className="w-full h-full object-cover" />
                                 ) : (
                                   <div className="w-full h-full flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300">
                                     <User size={24} />
                                   </div>
                                 )}
                               </div>
                               <div className="absolute -top-2 -left-2 w-6 h-6 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 flex items-center justify-center text-[10px] font-black shadow-lg">
                                 #{index + 1}
                               </div>
                            </div>
                            
                            <div className="flex-1 space-y-2">
                               <div className="flex justify-between items-end">
                                  <div>
                                    <div className="font-black text-zinc-900 dark:text-white text-sm uppercase tracking-tight">{barber.name}</div>
                                    <div className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.2em]">
                                      {barber.bookings} {t('admin.totalBookings') || 'Bookings'}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                     <div className="font-black text-zinc-900 dark:text-white text-sm">{t('common.currency') || 'CHF'}{barber.revenue.toLocaleString()}</div>
                                     <div className="text-[9px] font-black text-emerald-500 uppercase tracking-widest leading-none">
                                        {t('common.currency') || 'CHF'}{Math.round(barber.avgBookingValue)} avg
                                     </div>
                                  </div>
                               </div>
                               <div className="h-2.5 bg-zinc-50 dark:bg-zinc-800 rounded-full overflow-hidden">
                                  <motion.div 
                                    initial={{ width: 0 }}
                                    animate={{ width: `${percentage}%` }}
                                    transition={{ duration: 1.5, delay: index * 0.1 }}
                                    className="h-full bg-zinc-900 dark:bg-amber-500 rounded-full shadow-lg shadow-zinc-200 dark:shadow-amber-500/20"
                                  />
                               </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Detailed Revenue List */}
                <div className="bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50">
                  <div className="flex items-center justify-between mb-10">
                    <div>
                      <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-3 text-zinc-900 dark:text-white">
                        <Users size={24} className="text-emerald-500" /> {t('admin.teamContribution') || 'Team Revenue Contribution'}
                      </h3>
                      <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-[0.2em] mt-1 ml-9">{t('admin.revenueByBarber') || 'Detailed breakdown of earnings per barber'}</p>
                    </div>
                    <button className="px-6 py-3 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-2xl transition-all text-[10px] font-black uppercase tracking-widest text-zinc-600 dark:text-zinc-300 border border-zinc-100 dark:border-zinc-700">
                       {t('common.viewDetails') || 'View Details'}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {barbers.map(barber => {
                      const bData = barberPopularity.find(b => b.name === barber.name) || { revenue: 0, bookings: 0 };
                      const totalShopRevenue = totalRevenue || 1;
                      const percentage = (bData.revenue / totalShopRevenue) * 100;
                      
                      return (
                        <div key={barber.id} className="p-6 bg-zinc-50/50 dark:bg-zinc-800/30 rounded-3xl border border-zinc-50 dark:border-zinc-800/50 flex flex-col gap-4">
                           <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-xl overflow-hidden bg-white dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 shrink-0">
                                {barber.photo_url ? (
                                  <img src={barber.photo_url} alt={barber.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300">
                                    <User size={20} />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1">
                                 <h4 className="font-black text-zinc-900 dark:text-white text-sm tracking-tight">{barber.name}</h4>
                                 <span className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">{t('admin.barber') || 'Barber'}</span>
                              </div>
                              <div className="text-right">
                                 <div className="text-[10px] font-black text-amber-500 uppercase tracking-[0.2em]">{percentage.toFixed(1)}%</div>
                              </div>
                           </div>
                           <div className="grid grid-cols-2 gap-4">
                              <div className="bg-white dark:bg-zinc-900 p-3 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                                 <div className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">{t('admin.revenue') || 'Revenue'}</div>
                                 <div className="text-sm font-black text-zinc-900 dark:text-white leading-none">{t('common.currency') || 'CHF'}{bData.revenue.toLocaleString()}</div>
                              </div>
                              <div className="bg-white dark:bg-zinc-900 p-3 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                                 <div className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-1">{t('admin.bookings') || 'Bookings'}</div>
                                 <div className="text-sm font-black text-zinc-900 dark:text-white leading-none">{bData.bookings}</div>
                              </div>
                           </div>
                           <div className="h-1.5 w-full bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: `${percentage}%` }}
                                transition={{ duration: 1.5 }}
                                className="h-full bg-emerald-500 rounded-full"
                              />
                           </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}

          {activeTab === 'inventory' && isShopAdmin && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.inventory') || 'Inventory'}</h2>
                    <p className="text-[10px] font-black text-zinc-400 dark:text-zinc-500 uppercase tracking-widest">{inventory.length} {t('admin.totalItems') || 'Items Total'}</p>
                  </div>
                  
                  <div className="flex flex-1 max-w-xl items-center gap-4">
                    <div className="relative flex-1 group">
                      <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-300 dark:text-zinc-600 group-focus-within:text-amber-500 transition-colors" size={18} />
                      <input 
                        type="text"
                        placeholder={t('admin.searchItem') || "Search items..."}
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                        className="w-full pl-14 pr-6 py-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-100 dark:border-zinc-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition-all font-medium text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                      />
                    </div>

                    <div className="flex gap-2">
                      <button 
                        onClick={handleExportInventory}
                        className="p-4 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200/50 dark:border-zinc-800 text-zinc-900 dark:text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-all flex items-center justify-center gap-2 group"
                        title="Export CSV"
                      >
                        <Download size={18} className="group-hover:scale-110 transition-transform" />
                      </button>
                      <button 
                        onClick={() => setIsAddItemModalOpen(true)}
                        className="px-8 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-none flex items-center justify-center gap-2"
                      >
                        <Plus size={18} /> {t('admin.addItem') || 'Add Item'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.item') || 'Item'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.stock') || 'Stock'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.minStock') || 'Min Stock'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.status') || 'Status'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                       {filteredInventory.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-12 text-center text-zinc-400 dark:text-zinc-500 font-bold uppercase tracking-widest text-[10px]">
                            {inventorySearch ? (t('admin.noSearchResults') || 'No items match your search') : (t('common.noData') || 'No inventory items found')}
                          </td>
                        </tr>
                      ) : filteredInventory.map((item) => (
                        <tr key={item.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors group">
                          <td className="p-6">
                            <div className="font-black text-zinc-900 dark:text-white">{item.name}</div>
                            <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{item.category}</div>
                          </td>
                          <td className="p-6 font-bold text-zinc-600 dark:text-zinc-300">{item.quantity} {item.unit}</td>
                          <td className="p-6 font-bold text-zinc-400 dark:text-zinc-300">{item.min_stock_level} {item.unit}</td>
                          <td className="p-6">
                            <span className={`px-3 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest ${
                              item.quantity <= item.min_stock_level 
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' 
                                : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                            }`}>
                              {item.quantity <= item.min_stock_level ? (t('admin.lowStock') || 'Low Stock') : (t('admin.inStock') || 'In Stock')}
                            </span>
                          </td>
                          <td className="p-6 text-right opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="flex justify-end gap-2">
                              <button 
                                onClick={() => {
                                  setSelectedInventoryItem(item);
                                  setIsUsingItem(true);
                                  setInventoryActionAmount(1);
                                }}
                                className="p-2 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl hover:bg-amber-100 transition-colors"
                                title="Use Item"
                              >
                                <Minus size={14} />
                              </button>
                              <button 
                                onClick={() => {
                                  setSelectedInventoryItem(item);
                                  setIsRestockingItem(true);
                                  setInventoryActionAmount(1);
                                }}
                                className="p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 transition-colors"
                                title="Restock"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Inventory List */}
                <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                   {filteredInventory.map((item) => (
                    <div key={item.id} className="bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-800 space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-black text-zinc-900 dark:text-white">{item.name}</div>
                          <div className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">{item.category}</div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${
                          item.quantity <= item.min_stock_level 
                            ? 'bg-red-500 text-white' 
                            : 'bg-emerald-500 text-white'
                        }`}>
                          {item.quantity} {item.unit}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-zinc-100 dark:border-zinc-800">
                        <div className="text-[10px] font-bold text-zinc-400">Min: {item.min_stock_level} {item.unit}</div>
                        <div className="flex gap-2">
                           <button 
                            onClick={() => {
                              setSelectedInventoryItem(item);
                              setIsUsingItem(true);
                              setInventoryActionAmount(1);
                            }}
                            className="p-2 bg-white dark:bg-zinc-900 text-amber-500 rounded-lg shadow-sm"
                          >
                            <Minus size={14} />
                          </button>
                          <button 
                            onClick={() => {
                              setSelectedInventoryItem(item);
                              setIsRestockingItem(true);
                              setInventoryActionAmount(1);
                            }}
                            className="p-2 bg-white dark:bg-zinc-900 text-emerald-500 rounded-lg shadow-sm"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'commissions' && isAdmin && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                  <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.commissions') || 'Commissions'}</h2>
                  <div className="flex gap-2">
                    <button 
                      onClick={handleExportCommissions}
                      className="px-6 py-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200/50 dark:border-zinc-800 text-zinc-900 dark:text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-all flex items-center justify-center gap-2"
                    >
                      <Download size={14} /> CSV
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.barber') || 'Barber'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.amount') || 'Amount'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.status') || 'Status'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('common.date') || 'Date'}</th>
                        <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                      {commissions.map((comm) => (
                        <tr key={comm.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                          <td className="p-6">
                            <div className="font-black text-zinc-900 dark:text-white">{comm.barbers?.name}</div>
                          </td>
                          <td className="p-6 font-black text-zinc-900 dark:text-white">{t('common.currency') || 'CHF'}{comm.amount}</td>
                          <td className="p-6">
                            <span className={`px-3 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest ${
                              comm.status === 'paid' 
                                ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' 
                                : comm.status === 'cancelled'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                            }`}>
                              {t(`admin.${comm.status}`) || comm.status}
                            </span>
                          </td>
                          <td className="p-6 text-xs font-bold text-zinc-400 dark:text-zinc-300">
                            {format(parseISO(comm.created_at), 'MMM d, yyyy')}
                          </td>
                          <td className="p-6 text-right">
                            {comm.status === 'pending' && (
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleUpdateCommissionStatus(comm.id, 'paid')}
                                  className="p-2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 transition-colors"
                                  title="Mark as Paid"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={() => handleUpdateCommissionStatus(comm.id, 'cancelled')}
                                  className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-100 transition-colors"
                                  title="Cancel Commission"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'marketing' && isAdmin && (
            <div className="space-y-8">
              {isSuperAdmin ? (
                <>
                  {/* Acquisition Management Modals */}
                  <AnimatePresence>
                    {isConfiguringRewards && (
                      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
                        <motion.div 
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          onClick={() => setIsConfiguringRewards(false)}
                          className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
                        />
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }}
                          className="relative w-full max-w-md bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl overflow-hidden border border-zinc-100 dark:border-zinc-800"
                        >
                          <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                            <h2 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Referral Rewards</h2>
                            <button onClick={() => setIsConfiguringRewards(false)} className="p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-full transition-colors">
                              <X size={24} className="text-zinc-400 dark:text-zinc-300" />
                            </button>
                          </div>
                          <div className="p-8 space-y-6">
                            <div>
                              <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-2 block ml-4">Reward Amount (CHF)</label>
                              <div className="relative">
                                <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-zinc-400">CHF</span>
                                <input 
                                  type="number"
                                  value={referralRewardAmount}
                                  onChange={(e) => setReferralRewardAmount(Number(e.target.value))}
                                  className="w-full pl-16 pr-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-2 border-transparent focus:border-amber-500 rounded-2xl outline-none font-bold text-zinc-900 dark:text-white transition-all shadow-inner"
                                />
                              </div>
                              <p className="text-[10px] font-bold text-zinc-400 mt-2 ml-4 italic">Platform credit awarded for each shop that joins via referral.</p>
                            </div>
                            
                            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-800/30 flex items-center gap-4">
                              <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl flex items-center justify-center shrink-0">
                                <Check size={20} />
                              </div>
                              <div>
                                <p className="text-xs font-black text-emerald-900 dark:text-emerald-200">System Ready</p>
                                <p className="text-[10px] font-medium text-emerald-700/70 dark:text-emerald-400/70">Rewards will be automatically tracked.</p>
                              </div>
                            </div>

                            <button 
                              onClick={() => {
                                toast.success('Referral rewards configuration updated');
                                setIsConfiguringRewards(false);
                              }}
                              className="w-full py-5 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl shadow-zinc-200 dark:shadow-black/50"
                            >
                              Update Config
                            </button>
                          </div>
                        </motion.div>
                      </div>
                    )}
                  </AnimatePresence>

                  <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                    <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                      <div className="text-left">
                        <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Platform Acquisition</h2>
                        <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">Marketing tools to acquire new barber shops</p>
                      </div>
                    </div>
                    <div className="p-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-8 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-[2.5rem] border border-blue-100/50 dark:border-blue-800/30 flex flex-col justify-between hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 group">
                          <div>
                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                              <Globe size={24} />
                            </div>
                            <h3 className="text-lg font-black uppercase tracking-tighter mb-2 text-zinc-900 dark:text-white">Platform Promo Codes</h3>
                            <p className="text-sm font-bold text-zinc-500 dark:text-zinc-300 mb-6 group-hover:text-zinc-600 dark:group-hover:text-zinc-200 transition-colors">Generate global discounts for new shop subscriptions to incentivize platform onboarding.</p>
                          </div>
                          <div className="space-y-4">
                            <button 
                              onClick={() => setIsManagingPlatformPromos(!isManagingPlatformPromos)}
                              className={`w-full py-4 text-[10px] font-black uppercase tracking-widest rounded-2xl border transition-all shadow-sm ${isManagingPlatformPromos ? 'bg-blue-600 text-white border-transparent' : 'bg-white/50 dark:bg-zinc-900/50 backdrop-blur text-blue-600 dark:text-blue-400 border-blue-200/50 dark:border-blue-800/50 hover:bg-blue-600 hover:text-white dark:hover:bg-blue-500 dark:hover:text-zinc-900'}`}
                            >
                              {isManagingPlatformPromos ? 'Hide Promos' : 'Manage Promo Codes'}
                            </button>
                          </div>
                        </div>

                        <div className="p-8 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 dark:from-emerald-900/10 dark:to-teal-900/10 rounded-[2.5rem] border border-emerald-100/50 dark:border-emerald-800/30 flex flex-col justify-between hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300 group">
                          <div>
                            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                              <Target size={24} />
                            </div>
                            <h3 className="text-lg font-black uppercase tracking-tighter mb-2 text-zinc-900 dark:text-white">Referral Program</h3>
                            <p className="text-sm font-bold text-zinc-500 dark:text-zinc-300 mb-6 group-hover:text-zinc-600 dark:group-hover:text-zinc-200 transition-colors">Allow existing shops to refer other barbers. Currently rewarding {referralRewardAmount} CHF platform credit per successful onboarding.</p>
                          </div>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-white/50 dark:bg-zinc-900/50 backdrop-blur rounded-2xl border border-emerald-200/50 dark:border-emerald-800/50">
                              <span className="text-xs font-black uppercase tracking-widest text-emerald-700/70 dark:text-emerald-300/70">Status</span>
                              <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest shadow-sm">Active</span>
                            </div>
                            <button 
                              onClick={() => setIsConfiguringRewards(true)}
                              className="w-full py-4 bg-emerald-600 dark:bg-emerald-500 text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-500 dark:hover:bg-emerald-400 hover:shadow-lg hover:shadow-emerald-500/20 transition-all"
                            >
                              Configure Rewards
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Platform Promo Management Table */}
                  <AnimatePresence>
                    {isManagingPlatformPromos && (
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden"
                      >
                        <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                          <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Platform Promos</h2>
                          <button 
                            onClick={() => setIsAddingPromoCode(true)}
                            className="px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all"
                          >
                            <Plus size={14} className="inline mr-1" /> New Platform Code
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                                <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400">Code</th>
                                <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400">Discount</th>
                                <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400">Expires</th>
                                <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400">Status</th>
                                <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zinc-400 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                              {promoCodes.filter(p => !p.shop_id).map((promo) => (
                                <tr key={promo.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                                  <td className="p-6 font-black text-zinc-900 dark:text-white">{promo.code}</td>
                                  <td className="p-6 font-bold text-zinc-600 dark:text-zinc-300">
                                    {promo.discount_type === 'percent' ? `${promo.value}%` : `${promo.value} CHF`}
                                  </td>
                                  <td className="p-6 text-xs font-bold text-zinc-400 dark:text-zinc-300">
                                    {promo.expires_at ? format(parseISO(promo.expires_at), 'MMM d, yyyy') : 'Never'}
                                  </td>
                                  <td className="p-6">
                                    <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                      promo.active ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                                    }`}>
                                      {promo.active ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className="p-6 flex items-center justify-end gap-2">
                                    <button 
                                      onClick={() => handleTogglePromoCode(promo.id, promo.active)}
                                      className="p-2 bg-zinc-50 dark:bg-zinc-800 rounded-xl hover:bg-zinc-100 transition-colors text-zinc-400"
                                    >
                                      {promo.active ? <X size={16} /> : <Check size={16} />}
                                    </button>
                                    <button 
                                      onClick={() => handleDeletePromoCode(promo.id)}
                                      className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              {promoCodes.filter(p => !p.shop_id).length === 0 && (
                                <tr>
                                  <td colSpan={5} className="p-12 text-center text-zinc-400 font-bold italic">No platform promo codes created yet.</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                <>
                  <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                    <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                      <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.promoCodes') || 'Promo Codes'}</h2>
                      <button 
                        onClick={() => setIsAddingPromoCode(true)}
                        className="px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-lg shadow-zinc-200 dark:shadow-black/50"
                      >
                        <Plus size={14} className="inline mr-1" /> {t('admin.addPromo') || 'Add Promo'}
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-zinc-50/50 dark:bg-zinc-800/50">
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.code') || 'Code'}</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.discount') || 'Discount'}</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.expires') || 'Expires'}</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">{t('admin.status') || 'Status'}</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                          {promoCodes.map((promo) => (
                            <tr key={promo.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                              <td className="p-6 font-black text-zinc-900 dark:text-white">{promo.code}</td>
                              <td className="p-6 font-bold text-zinc-600 dark:text-zinc-300">
                                {promo.discount_type === 'percent' ? `${promo.value}%` : `${promo.value} CHF`}
                              </td>
                              <td className="p-6 text-xs font-bold text-zinc-400 dark:text-zinc-300">
                                {promo.expires_at ? format(parseISO(promo.expires_at), 'MMM d, yyyy') : 'Never'}
                              </td>
                              <td className="p-6">
                                <span className={`px-3 py-1 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest ${
                                  promo.active ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                }`}>
                                  {promo.active ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                                </span>
                              </td>
                              <td className="p-6 flex items-center justify-end gap-2">
                                <button 
                                  onClick={() => handleTogglePromoCode(promo.id, promo.active)}
                                  className={`p-2 rounded-xl transition-colors ${promo.active ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}
                                  title={promo.active ? 'Deactivate' : 'Activate'}
                                >
                                  {promo.active ? <X size={16} /> : <Check size={16} />}
                                </button>
                                <button 
                                  onClick={() => handleDeletePromoCode(promo.id)}
                                  className="p-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden mt-8">
                    <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Customer Reviews & Ratings</h2>
                        <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">See what customers are saying</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300">Avg Rating</p>
                          <p className="text-2xl font-black text-amber-500">
                            {(reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0).toFixed(1)} <Star size={20} className="inline fill-amber-500 mb-1" />
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto hidden-scrollbar">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-zinc-50/50 dark:bg-zinc-800/50 sticky top-0 backdrop-blur-md">
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Customer</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Rating</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Comment</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Barber</th>
                            <th className="p-6 text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-50 dark:divide-zinc-800">
                          {reviews.map((review) => {
                            const barber = barbers.find(b => b.id === review.barber_id);
                            return (
                              <tr key={review.id} className="hover:bg-zinc-50/30 dark:hover:bg-zinc-800/30 transition-colors">
                                <td className="p-6 font-bold text-zinc-900 dark:text-white">Customer</td>
                                <td className="p-6">
                                  <div className="flex gap-1">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                      <Star key={i} size={14} className={i < review.rating ? "text-amber-500 fill-amber-500" : "text-zinc-300 dark:text-zinc-700"} />
                                    ))}
                                  </div>
                                </td>
                                <td className="p-6 text-sm font-bold text-zinc-600 dark:text-zinc-300 max-w-xs truncate">
                                  {review.comment || <span className="italic text-zinc-400">No comment</span>}
                                </td>
                                <td className="p-6 font-black text-zinc-900 dark:text-white">{barber?.name || 'Unknown'}</td>
                                <td className="p-6 text-xs font-bold text-zinc-400 dark:text-zinc-300">
                                  {format(parseISO(review.created_at), 'MMM d, yyyy')}
                                </td>
                              </tr>
                            );
                          })}
                          {reviews.length === 0 && (
                            <tr>
                              <td colSpan={5} className="p-8 text-center text-zinc-400 dark:text-zinc-500 font-bold">
                                No reviews yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden mt-8">
                    <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.automatedCampaigns') || 'Automated Campaigns'}</h2>
                        <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">{t('admin.campaignsDesc') || 'Set up automatic marketing messages'}</p>
                      </div>
                    </div>
                    <div className="p-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-8 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10 rounded-[2.5rem] border border-amber-100/50 dark:border-amber-800/30 flex flex-col justify-between hover:shadow-xl hover:shadow-amber-500/5 transition-all duration-300 group">
                          <div>
                            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                              <Users size={24} />
                            </div>
                            <h3 className="text-lg font-black uppercase tracking-tighter mb-2 text-zinc-900 dark:text-white">"We Miss You" Campaign</h3>
                            <p className="text-sm font-bold text-zinc-500 dark:text-zinc-300 mb-6 group-hover:text-zinc-600 dark:group-hover:text-zinc-200 transition-colors">Automatically send a discount code to customers who haven't booked in over 6 weeks.</p>
                          </div>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-white/50 dark:bg-zinc-900/50 backdrop-blur rounded-2xl border border-amber-200/50 dark:border-amber-800/50">
                              <span className="text-xs font-black uppercase tracking-widest text-amber-700/70 dark:text-amber-300/70">{t('admin.status') || 'Status'}</span>
                              <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest shadow-sm">Active</span>
                            </div>
                            <button className="w-full py-4 bg-amber-500 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/20 transition-all">
                              Configure Campaign
                            </button>
                          </div>
                        </div>

                        <div className="p-8 bg-gradient-to-br from-rose-50/50 to-pink-50/50 dark:from-rose-900/10 dark:to-pink-900/10 rounded-[2.5rem] border border-rose-100/50 dark:border-rose-800/30 flex flex-col justify-between opacity-60 hover:opacity-100 transition-all duration-500 group relative overflow-hidden">
                          <div className="absolute inset-0 bg-white/30 dark:bg-black/30 backdrop-blur-[2px] z-10 hidden" />
                          <div>
                            <div className="w-12 h-12 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-sm relative z-20">
                              <Star size={24} />
                            </div>
                            <h3 className="text-lg font-black uppercase tracking-tighter mb-2 text-zinc-900 dark:text-white relative z-20">Birthday Special</h3>
                            <p className="text-sm font-bold text-zinc-500 dark:text-zinc-300 mb-6 group-hover:text-zinc-600 dark:group-hover:text-zinc-200 transition-colors relative z-20">Send a personalized gift or discount on your customers' birthdays.</p>
                          </div>
                          <div className="space-y-4 relative z-20">
                            <button disabled className="w-full py-4 bg-white/40 dark:bg-zinc-800/40 text-rose-600/50 dark:text-rose-400/50 rounded-2xl font-black uppercase tracking-widest text-[10px] cursor-not-allowed border border-rose-200/30 dark:border-rose-800/30">
                              Coming Soon
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden mt-8">
                    <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center">
                          <Target size={24} />
                        </div>
                        <div>
                          <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Refer & Earn Program</h2>
                          <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">Refer other barber shops to our platform and earn rewards!</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900 dark:text-white mb-4">Your Unique Referral Link</h3>
                          <div className="flex bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800 p-2 items-center">
                            <input 
                              type="text" 
                              readOnly 
                              value={`https://ais-pre-lnbtnchmrhztrw6mymyhob-704502632193.europe-west2.run.app/register?ref=${profile?.barber_shop_id || profile?.id}`}
                              className="flex-1 bg-transparent text-sm font-bold text-zinc-500 px-4 outline-none truncate"
                            />
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(`https://ais-pre-lnbtnchmrhztrw6mymyhob-704502632193.europe-west2.run.app/register?ref=${profile?.barber_shop_id || profile?.id}`);
                                toast.success("Referral link copied!");
                              }}
                              className="px-6 py-3 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all ml-2 whitespace-nowrap shadow-md"
                            >
                              Copy
                            </button>
                          </div>
                          <p className="text-[10px] font-bold text-emerald-500 mt-4 uppercase tracking-widest">* Earn 50 CHF platform credit for every shop that subscribes using your link.</p>
                        </div>
                        <div className="bg-zinc-50 dark:bg-zinc-800/50 rounded-[2rem] p-8 border border-zinc-100 dark:border-zinc-800">
                          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-900 dark:text-white mb-6">Your Referral Stats</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">Total Referred</p>
                                <p className="text-3xl font-black text-zinc-900 dark:text-white">0</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">Total Earned</p>
                                <p className="text-3xl font-black text-emerald-500">0.00 CHF</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'email_templates' && isAdmin && (
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <h2 className="text-3xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Email Templates</h2>
                  <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1 uppercase tracking-widest">Manage email communications & Resend integration</p>
                </div>
                {!isAddingTemplate && !isEditingTemplate && (
                  <button 
                    onClick={() => {
                      setNewTemplate({ id: '', name: '', scenario: 'custom', subject: '', body: '' });
                      setIsAddingTemplate(true);
                    }}
                    className="px-6 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-lg flex items-center gap-2"
                  >
                    <Plus size={18} /> New Template
                  </button>
                )}
              </div>

              {isAddingTemplate || isEditingTemplate ? (
                <div className="bg-white dark:bg-zinc-900 p-10 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
                      {isEditingTemplate ? 'Edit Template' : 'Create Template'}
                    </h3>
                    <button 
                      onClick={() => {
                        setIsAddingTemplate(false);
                        setIsEditingTemplate(null);
                      }}
                      className="p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-xl transition-all"
                    >
                      <XCircle size={24} className="text-zinc-400" />
                    </button>
                  </div>
                  
                    <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 ml-2">Template Name</label>
                        <input 
                          type="text" 
                          value={isEditingTemplate ? isEditingTemplate.name : newTemplate.name}
                          onChange={e => isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, name: e.target.value}) : setNewTemplate({...newTemplate, name: e.target.value})}
                          className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                          placeholder="e.g. Booking Confirmed"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 ml-2">Scenario</label>
                        <select 
                          value={isEditingTemplate ? isEditingTemplate.scenario : newTemplate.scenario}
                          onChange={(e: any) => isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, scenario: e.target.value}) : setNewTemplate({...newTemplate, scenario: e.target.value})}
                          className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white appearance-none"
                        >
                          <option value="booking_confirmation">Booking Confirmation</option>
                          <option value="reminder">Appointment Reminder</option>
                          <option value="password_reset">Password Reset</option>
                          <option value="welcome">Welcome Email</option>
                          <option value="custom">Custom Marketing</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="space-y-2 pb-2">
                      <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 ml-2">Prebuilt Content Options (Barber Focus)</label>
                      <div className="flex gap-2 flex-wrap">
                        <button 
                          onClick={() => {
                            const prebuilt = { subject: "Booking Confirmed! ✂️", body: "<h1 style=\"font-family: sans-serif; color: #18181b;\">You're All Set, {{name}}!</h1>\n<p style=\"font-family: sans-serif; color: #52525b;\">Your appointment on <strong>{{date}}</strong> at <strong>{{time}}</strong> is confirmed. We look forward to seeing you!</p>" };
                            isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, ...prebuilt}) : setNewTemplate({...newTemplate, ...prebuilt});
                          }}
                          className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
                        >
                          Standard Confirmation
                        </button>
                        <button 
                          onClick={() => {
                            const prebuilt = { subject: "Don't forget your haircut tomorrow!", body: "<h1 style=\"font-family: sans-serif; color: #18181b;\">Hi {{name}},</h1>\n<p style=\"font-family: sans-serif; color: #52525b;\">Just a quick reminder about your appointment tomorrow at <strong>{{time}}</strong>. Need to reschedule? Let us know!</p>" };
                            isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, ...prebuilt}) : setNewTemplate({...newTemplate, ...prebuilt});
                          }}
                          className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
                        >
                          Friendly Reminder
                        </button>
                        <button 
                          onClick={() => {
                            const prebuilt = { subject: "It's been a while! Time for a fresh cut? 💈", body: "<h1 style=\"font-family: sans-serif; color: #18181b;\">Hey {{name}},</h1>\n<p style=\"font-family: sans-serif; color: #52525b;\">We noticed you haven't been in for a while. Come back and get a fresh fade! Click here to book your next slot.</p>" };
                            isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, ...prebuilt}) : setNewTemplate({...newTemplate, ...prebuilt});
                          }}
                          className="px-4 py-2 bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 text-amber-900 dark:text-amber-400 text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
                        >
                          Win-back (Promo)
                        </button>
                        <button 
                          onClick={() => {
                            const prebuilt = { subject: "Cancellation Confirmed 📅", body: "<h1 style=\"font-family: sans-serif; color: #18181b;\">Hi {{name}},</h1>\n<p style=\"font-family: sans-serif; color: #52525b;\">Your appointment for <strong>{{date}}</strong> at <strong>{{time}}</strong> has been successfully cancelled. Whenever you're ready to get back in the chair, just book via our app!</p>" };
                            isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, ...prebuilt}) : setNewTemplate({...newTemplate, ...prebuilt});
                          }}
                          className="px-4 py-2 bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/30 dark:hover:bg-rose-900/50 text-rose-900 dark:text-rose-400 text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
                        >
                          Cancellation
                        </button>
                        <button 
                          onClick={() => {
                            const prebuilt = { subject: "Looking sharp! We'd love your feedback 🌟", body: "<h1 style=\"font-family: sans-serif; color: #18181b;\">Hey {{name}}, looking good!</h1>\n<p style=\"font-family: sans-serif; color: #52525b;\">We hope you're loving the fresh cut from your visit on <strong>{{date}}</strong>. If you have a minute, we'd greatly appreciate a quick review to let us know how we did.</p>" };
                            isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, ...prebuilt}) : setNewTemplate({...newTemplate, ...prebuilt});
                          }}
                          className="px-4 py-2 bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 text-emerald-900 dark:text-emerald-400 text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
                        >
                          Review Request
                        </button>
                        <button 
                          onClick={() => {
                            const prebuilt = { subject: "Holiday/Event Special! Look your best 🎉", body: "<h1 style=\"font-family: sans-serif; color: #18181b;\">Get Ready, {{name}}!</h1>\n<p style=\"font-family: sans-serif; color: #52525b;\">Our schedule is filling up fast for the upcoming season! Make sure you lock in your preferred slot early so you can stay looking fresh.</p>" };
                            isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, ...prebuilt}) : setNewTemplate({...newTemplate, ...prebuilt});
                          }}
                          className="px-4 py-2 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/30 dark:hover:bg-indigo-900/50 text-indigo-900 dark:text-indigo-400 text-xs font-black uppercase tracking-widest rounded-xl transition-colors"
                        >
                          Event Promo
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 ml-2">Email Subject</label>
                      <input 
                        type="text" 
                        value={isEditingTemplate ? isEditingTemplate.subject : newTemplate.subject}
                        onChange={e => isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, subject: e.target.value}) : setNewTemplate({...newTemplate, subject: e.target.value})}
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                        placeholder="Your appointment is confirmed!"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between ml-2">
                        <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400">Email Body (HTML)</label>
                        <div className="flex gap-2">
                          <span className="text-[8px] font-black px-2 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 rounded uppercase tracking-widest">{"{{name}}"}</span>
                          <span className="text-[8px] font-black px-2 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 rounded uppercase tracking-widest">{"{{date}}"}</span>
                          <span className="text-[8px] font-black px-2 py-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 rounded uppercase tracking-widest">{"{{time}}"}</span>
                        </div>
                      </div>
                      <textarea 
                        rows={8}
                        value={isEditingTemplate ? isEditingTemplate.body : newTemplate.body}
                        onChange={e => isEditingTemplate ? setIsEditingTemplate({...isEditingTemplate, body: e.target.value}) : setNewTemplate({...newTemplate, body: e.target.value})}
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-mono text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white resize-y"
                        placeholder="<h1>Hi {{name}},</h1><p>Your booking for {{date}} at {{time}} is confirmed.</p>"
                      />
                    </div>

                    <div className="flex items-center gap-4 pt-6">
                      <button 
                        onClick={() => {
                          const tpl = isEditingTemplate || { ...newTemplate, id: Date.now().toString() };
                          if (isEditingTemplate) {
                            setEmailTemplates(prev => prev.map(t => t.id === tpl.id ? tpl : t));
                          } else {
                            setEmailTemplates(prev => [...prev, tpl]);
                          }
                          setIsAddingTemplate(false);
                          setIsEditingTemplate(null);
                          toast.success("Template saved!");
                        }}
                        className="flex-1 px-6 py-5 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl"
                      >
                        Save Template
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                  <div className="xl:col-span-2 space-y-6">
                    {emailTemplates.length === 0 ? (
                      <div className="p-12 bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 text-center flex flex-col items-center shadow-xl">
                        <div className="w-20 h-20 bg-zinc-50 dark:bg-zinc-800 rounded-2xl flex items-center justify-center mb-6 border border-zinc-100 dark:border-zinc-700">
                          <Mail size={32} className="text-zinc-300 dark:text-zinc-600" />
                        </div>
                        <h3 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white mb-2">No Templates Yet</h3>
                        <p className="text-sm font-bold text-zinc-500 dark:text-zinc-400 mb-8 max-w-sm">Create your first responsive email template to start sending branded communications to your customers.</p>
                        <button 
                          onClick={() => {
                            setNewTemplate({ id: '', name: '', scenario: 'custom', subject: '', body: '' });
                            setIsAddingTemplate(true);
                          }}
                          className="px-8 py-4 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-lg hover:scale-105 transition-transform"
                        >
                          Create Template
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {emailTemplates.map(template => (
                          <div key={template.id} className="p-8 bg-white dark:bg-zinc-900 rounded-[2.5rem] border border-zinc-100 dark:border-zinc-800 flex flex-col group relative overflow-hidden transition-all hover:shadow-xl dark:shadow-none hover:border-zinc-200 dark:hover:border-zinc-700 shadow-sm">
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="px-3 py-1.5 bg-amber-50 dark:bg-amber-900/10 text-amber-600 dark:text-amber-400 border border-amber-100 dark:border-amber-900/30 rounded-lg text-[8px] font-black uppercase tracking-widest">
                                  {template.scenario.replace('_', ' ')}
                                </span>
                              </div>
                              <h3 className="text-lg font-black uppercase tracking-tighter text-zinc-900 dark:text-white truncate">{template.name}</h3>
                              <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 truncate mt-1">Subj: {template.subject}</p>
                            </div>
                            <div className="flex items-center gap-2 mt-auto pt-6 border-t border-zinc-50 dark:border-zinc-800">
                              <button 
                                onClick={() => setIsEditingTemplate(template)}
                                className="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-xl text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors hover:shadow-sm"
                                title="Edit"
                              >
                                <Pencil size={16} />
                              </button>
                              <button 
                                onClick={() => {
                                  setEmailTemplates(prev => prev.filter(t => t.id !== template.id));
                                  toast.success("Template deleted");
                                }}
                                className="p-3 bg-red-50 dark:bg-red-900/10 rounded-xl text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors ml-auto hover:shadow-sm"
                                title="Delete"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-zinc-900 p-10 rounded-[3rem] shadow-2xl relative overflow-hidden group h-fit border border-zinc-800">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:bg-white/10 transition-all duration-700" />
                    <div className="relative z-10">
                      <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-white mb-8 border border-white/10">
                        <Send size={24} />
                      </div>
                      <h3 className="text-2xl font-black uppercase tracking-tighter text-white mb-2">Resend API</h3>
                      <p className="text-xs font-bold text-zinc-400 mb-8 leading-relaxed">Connect your Resend account to dispatch transactional emails to customers automatically.</p>
                      
                      <div className="space-y-6">
                        <div className="space-y-2">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-500 ml-2">Secret API Key</label>
                          <input 
                            type="password"
                            value={resendApiKey}
                            onChange={(e) => setResendApiKey(e.target.value)}
                            placeholder="re_xxxxxxxxxxxxxx"
                            className="w-full bg-black/50 border border-white/10 rounded-2xl px-6 py-4 text-white text-sm focus:ring-2 ring-amber-500 outline-none font-mono placeholder:text-zinc-700"
                          />
                        </div>
                        <button 
                          onClick={() => {
                            if (!resendApiKey) {
                              toast.error("Please enter a Resend API Key");
                              return;
                            }
                            toast.success("Credentials saved locally.");
                          }}
                          className="w-full py-4 bg-white text-black rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-zinc-200 transition-colors shadow-lg shadow-white/10"
                        >
                          Save Credentials
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'staff_dashboard' && isStaff && !isSuperAdmin && (() => {
            const currentBarber = barbers.find(b => b.user_id === profile?.id);
            const barberReservations = reservations.filter(r => r.barber_id === currentBarber?.id);
            const confirmedReservations = barberReservations.filter(r => r.status === 'confirmed');
            const totalRevenue = confirmedReservations.reduce((sum, r) => sum + Number(r.total_price), 0);
            const avgRating = reviews.filter(rev => rev.barber_id === currentBarber?.id).length > 0
              ? reviews.filter(rev => rev.barber_id === currentBarber?.id).reduce((sum, r) => sum + r.rating, 0) / reviews.filter(rev => rev.barber_id === currentBarber?.id).length
              : 0;

            return (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 p-8 rounded-[2.5rem] shadow-xl shadow-zinc-200 dark:shadow-black/50">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest opacity-50 mb-2">My Total Revenue</p>
                    <h3 className="text-4xl font-black tracking-tighter">{t('common.currency')}{totalRevenue.toLocaleString()}</h3>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 p-8 rounded-[2.5rem] shadow-xl shadow-zinc-50 dark:shadow-black/50">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-2">My Bookings</p>
                    <h3 className="text-4xl font-black tracking-tighter text-zinc-900 dark:text-white">{barberReservations.length}</h3>
                  </div>
                  <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 p-8 rounded-[2.5rem] shadow-xl shadow-zinc-50 dark:shadow-black/50">
                    <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mb-2">My Rating</p>
                    <div className="flex items-center gap-2">
                      <h3 className="text-4xl font-black tracking-tighter text-amber-500">{avgRating.toFixed(1)}</h3>
                      <Star className="text-amber-500 fill-amber-500" size={24} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                    <div className="p-8 border-b border-zinc-50 dark:border-zinc-800">
                      <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">My Portfolio</h2>
                    </div>
                    <div className="p-8">
                      {currentBarber?.portfolio_urls && currentBarber.portfolio_urls.length > 0 ? (
                        <div className="grid grid-cols-3 gap-4">
                          {currentBarber.portfolio_urls.map((url, idx) => (
                            <div key={idx} className="aspect-square rounded-2xl overflow-hidden bg-zinc-100 dark:bg-zinc-800">
                              <img src={url} alt="Work" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 bg-zinc-50 dark:bg-zinc-800/50 rounded-[2rem] border border-dashed border-zinc-200 dark:border-zinc-700">
                          <p className="text-zinc-400 dark:text-zinc-300 font-bold">No portfolio images yet.</p>
                          <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 mt-2">Add them in your profile settings</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden">
                    <div className="p-8 border-b border-zinc-50 dark:border-zinc-800">
                      <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Upcoming Schedule</h2>
                    </div>
                    <div className="p-8 space-y-4">
                      {barberReservations
                        .filter(r => r.status === 'confirmed' && isBefore(new Date(), new Date(r.created_at)))
                        .slice(0, 5)
                        .map(res => (
                          <div key={res.id} className="flex items-center justify-between p-4 bg-zinc-50 dark:bg-zinc-800/50 rounded-2xl border border-zinc-100 dark:border-zinc-800">
                            <div>
                              <p className="font-black text-zinc-900 dark:text-white">{res.customer_name}</p>
                              <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300">
                                {res.services?.map(s => s.name).join(', ')}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-black text-zinc-900 dark:text-white">{format(new Date(res.created_at), 'MMM d')}</p>
                              <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-amber-500">Confirmed</p>
                            </div>
                          </div>
                        ))}
                      {barberReservations.length === 0 && (
                        <p className="text-center py-8 text-zinc-400 dark:text-zinc-300 font-bold">No upcoming bookings.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {activeTab === 'chat' && isStaff && (() => {
            const selectedRes = reservations.find(r => r.id === selectedChatUser);
            const chatMessages = messages.filter(m => m.reservation_id === selectedChatUser);
            
            return (
            <div className="bg-white dark:bg-zinc-900 rounded-[3rem] border border-zinc-100 dark:border-zinc-800 shadow-2xl dark:shadow-black/50 overflow-hidden h-[70vh] flex">
              <div className="w-80 border-r border-zinc-100 dark:border-zinc-800 flex flex-col">
                <div className="p-6 border-b border-zinc-50 dark:border-zinc-800">
                  <h2 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Messages</h2>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {reservations.map(res => (
                    <button 
                      key={res.id}
                      onClick={() => setSelectedChatUser(res.id)}
                      className={`w-full p-4 rounded-2xl text-left transition-all flex flex-col gap-1 ${
                        selectedChatUser === res.id 
                          ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900' 
                          : 'hover:bg-zinc-50 dark:hover:bg-zinc-800 text-zinc-900 dark:text-white'
                      }`}
                    >
                      <p className="font-black text-sm">{res.customer_name}</p>
                      <p className={`text-[10px] sm:text-xs font-black uppercase tracking-widest ${
                        selectedChatUser === res.id ? 'text-zinc-400 dark:text-zinc-700' : 'text-zinc-400 dark:text-zinc-500'
                      }`}>Booking #{res.id.slice(0, 8)}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 flex flex-col">
                {selectedChatUser && selectedRes ? (
                  <>
                    <div className="p-6 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-100 dark:bg-zinc-800 rounded-xl flex items-center justify-center text-zinc-400 dark:text-zinc-300">
                          <User size={20} />
                        </div>
                        <div>
                          <p className="font-black text-zinc-900 dark:text-white">{selectedRes.customer_name}</p>
                          <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-emerald-500">Live chat</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-8 space-y-6">
                      {chatMessages.length === 0 && (
                        <div className="text-center py-10 opacity-50">
                          <MessageSquare className="mx-auto mb-4" size={32} />
                          <p className="text-sm font-bold opacity-80">No messages yet.</p>
                        </div>
                      )}
                      {chatMessages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender_id === profile?.id ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] p-4 rounded-2xl ${
                            msg.sender_id === profile?.id 
                              ? 'bg-zinc-900 dark:bg-white rounded-tr-none text-white dark:text-zinc-900' 
                              : 'bg-zinc-100 dark:bg-zinc-800 rounded-tl-none text-zinc-900 dark:text-white'
                          }`}>
                            <p className="text-sm font-bold whitespace-pre-wrap">{msg.content}</p>
                            <p className={`text-[10px] sm:text-xs font-black uppercase tracking-widest mt-2 ${
                              msg.sender_id === profile?.id ? 'opacity-50' : 'text-zinc-400 dark:text-zinc-500'
                            }`}>
                              {format(parseISO(msg.created_at), 'h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-6 border-t border-zinc-50 dark:border-zinc-800">
                      <div className="flex gap-4">
                        <input 
                          type="text" 
                          placeholder="Type your message..."
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
                          className="flex-1 px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                        />
                        <button 
                          onClick={handleSendMessage}
                          className="p-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all flex items-center justify-center min-w-[56px]"
                        >
                          <Send size={20} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                    <div className="w-20 h-20 bg-zinc-50 dark:bg-zinc-800 text-zinc-200 dark:text-zinc-300 rounded-[2rem] flex items-center justify-center mb-6">
                      <MessageSquare size={40} />
                    </div>
                    <div className="text-left w-full">
                      <h3 className="text-xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Select a conversation</h3>
                      <p className="text-sm font-bold text-zinc-400 dark:text-zinc-300 mt-2">Choose a customer from the list to start chatting.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
          </ErrorBoundary>
        </motion.div>
      )}
      </main>
      </div>
      {/* Edit Reservation Modal */}
      <AnimatePresence>
        {isEditingReservation && selectedReservation && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => {
                setIsEditingReservation(false);
                setSelectedReservation(null);
              }}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-xl bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-zinc-100 dark:border-zinc-800 transition-colors"
            >
              <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="text-xl sm:text-2xl font-black text-zinc-900 dark:text-white tracking-tighter uppercase italic">
                    {t('admin.editReservation')}
                  </h2>
                  <p className="text-zinc-400 dark:text-zinc-300 text-[10px] font-black uppercase tracking-widest mt-1">
                    {t('admin.customerDetails')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsEditingReservation(false);
                    setSelectedReservation(null);
                  }}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 dark:text-zinc-300"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar scroll-smooth">

                <form onSubmit={handleUpdateReservation} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-zinc-400 dark:text-zinc-300 uppercase tracking-widest ml-1">
                      {t('booking.fullName')}
                    </label>
                    <div className="relative group">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300 group-focus-within:text-zinc-900 dark:group-focus-within:text-white transition-colors">
                        <User size={18} />
                      </div>
                      <input
                        type="text"
                        required
                        value={selectedReservation.customer_name}
                        onChange={(e) => setSelectedReservation({ ...selectedReservation, customer_name: e.target.value })}
                        className="w-full pl-14 pr-6 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 border-transparent rounded-2xl text-sm font-bold text-zinc-900 dark:text-white focus:border-zinc-900 dark:focus:border-amber-500 transition-all outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-zinc-400 dark:text-zinc-300 uppercase tracking-widest ml-1">
                      {t('booking.emailAddress')}
                    </label>
                    <div className="relative group">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300 group-focus-within:text-zinc-900 dark:group-focus-within:text-white transition-colors">
                        <Mail size={18} />
                      </div>
                      <input
                        type="email"
                        required
                        value={selectedReservation.customer_email}
                        onChange={(e) => setSelectedReservation({ ...selectedReservation, customer_email: e.target.value })}
                        className="w-full pl-14 pr-6 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 border-transparent rounded-2xl text-sm font-bold text-zinc-900 dark:text-white focus:border-zinc-900 dark:focus:border-amber-500 transition-all outline-none"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-zinc-400 dark:text-zinc-300 uppercase tracking-widest ml-1">
                      {t('booking.phoneNumber')}
                    </label>
                    <div className="relative group">
                      <div className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-300 group-focus-within:text-zinc-900 dark:group-focus-within:text-white transition-colors">
                        <Phone size={18} />
                      </div>
                      <input
                        type="tel"
                        required
                        value={selectedReservation.customer_phone}
                        onChange={(e) => setSelectedReservation({ ...selectedReservation, customer_phone: e.target.value })}
                        className="w-full pl-14 pr-6 py-4 bg-zinc-50 dark:bg-zinc-800 border-2 border-transparent rounded-2xl text-sm font-bold text-zinc-900 dark:text-white focus:border-zinc-900 dark:focus:border-amber-500 transition-all outline-none"
                        placeholder="079 123 45 67"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingReservation(false);
                        setSelectedReservation(null);
                      }}
                      className="flex-1 py-4 bg-zinc-50 text-zinc-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-100 transition-all"
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingReservation}
                      className="flex-1 py-4 bg-zinc-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 transition-all disabled:opacity-50 shadow-lg shadow-zinc-200 flex items-center justify-center gap-2"
                    >
                      {isSubmittingReservation && <LoadingSpinner />}
                      {t('common.save')}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmConfig.isOpen}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        variant={confirmConfig.variant}
      />

      {/* Delete Slot Confirmation Modal */}
      <ConfirmDialog
        isOpen={!!slotToDelete}
        onClose={() => setSlotToDelete(null)}
        onConfirm={confirmDeleteSlot}
        title={t('common.confirm')}
        message={t('admin.confirmDeleteSlot')}
        variant="danger"
      />

      {/* Global Modals for Services and Promo Codes */}
      <AnimatePresence>
        {isAddingPromoCode && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsAddingPromoCode(false)}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-xl bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl overflow-hidden border border-zinc-100 dark:border-zinc-800"
            >
              <div className="p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between">
                <h2 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">Add Promo Code</h2>
                <button onClick={() => setIsAddingPromoCode(false)} className="p-2 hover:bg-zinc-50 dark:hover:bg-zinc-800 rounded-full transition-colors">
                  <X size={24} className="text-zinc-400 dark:text-zinc-300" />
                </button>
              </div>
              <form onSubmit={handleAddPromoCode} className="p-8 space-y-6 max-h-[80vh] overflow-y-auto hidden-scrollbar">
                <div className="space-y-4">
                  <div>
                    <label className={`font-black text-zinc-400 dark:text-zinc-500 ml-4 mb-2 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>Code</label>
                    <input
                      type="text"
                      value={newPromoCode.code}
                      onChange={(e) => setNewPromoCode({...newPromoCode, code: e.target.value.toUpperCase().replace(/\s+/g, '')})}
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-2 border-transparent focus:border-zinc-900 dark:focus:border-amber-500 rounded-2xl outline-none font-bold text-zinc-900 dark:text-white transition-all uppercase placeholder-zinc-400 dark:placeholder-zinc-600"
                      placeholder="SUMMER20"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`font-black text-zinc-400 dark:text-zinc-500 ml-4 mb-2 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>Discount Type</label>
                      <select 
                        value={newPromoCode.discount_type}
                        onChange={(e) => setNewPromoCode({...newPromoCode, discount_type: e.target.value as 'percent' | 'fixed'})}
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-2 border-transparent focus:border-zinc-900 dark:focus:border-amber-500 rounded-2xl outline-none font-bold text-zinc-900 dark:text-white transition-all"
                      >
                        <option value="percent">Percentage (%)</option>
                        <option value="fixed">Fixed Amount (CHF)</option>
                      </select>
                    </div>
                    <div>
                      <label className={`font-black text-zinc-400 dark:text-zinc-500 ml-4 mb-2 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>Value</label>
                      <input
                        type="number"
                        value={newPromoCode.value}
                        onChange={(e) => setNewPromoCode({...newPromoCode, value: Number(e.target.value)})}
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-2 border-transparent focus:border-zinc-900 dark:focus:border-amber-500 rounded-2xl outline-none font-bold text-zinc-900 dark:text-white transition-all"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`font-black text-zinc-400 dark:text-zinc-500 ml-4 mb-2 block ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>Expiry Date (Optional)</label>
                    <input
                      type="date"
                      value={newPromoCode.expires_at ? newPromoCode.expires_at.split('T')[0] : ''}
                      onChange={(e) => setNewPromoCode({...newPromoCode, expires_at: e.target.value || undefined})}
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-2 border-transparent focus:border-zinc-900 dark:focus:border-amber-500 rounded-2xl outline-none font-bold text-zinc-900 dark:text-white transition-all"
                    />
                  </div>

                </div>
                <div className="flex justify-end gap-2 pt-4 border-t border-zinc-50 dark:border-zinc-800">
                  <button type="button" onClick={() => setIsAddingPromoCode(false)} className={`px-8 py-4 bg-zinc-50 dark:bg-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-700 text-zinc-400 dark:text-zinc-300 rounded-2xl transition-all font-black ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}>
                    {t('common.cancel')}
                  </button>
                  <button 
                    type="submit" 
                    disabled={loading} 
                    className={`px-8 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 rounded-2xl transition-all font-black flex items-center justify-center gap-2 min-w-[120px] ${language === 'ta' ? 'text-xs' : 'text-[10px] uppercase tracking-widest'}`}
                  >
                    {loading ? <LoadingSpinner /> : 'Save Promo'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {isAddingService && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsAddingService(false)}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-xl bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-zinc-100 dark:border-zinc-800 transition-colors"
            >
              <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.addService')}</h2>
                <button onClick={() => setIsAddingService(false)} className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors">
                  <X size={20} className="text-zinc-400 dark:text-zinc-300" />
                </button>
              </div>
              <form onSubmit={handleAddService} className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar scroll-smooth">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <label htmlFor="service-name" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.serviceName')}</label>
                    <input 
                      id="service-name"
                      required
                      placeholder={t('admin.serviceName')}
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                      value={newService.name}
                      onChange={(e) => setNewService({...newService, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="service-desc" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.description')}</label>
                    <textarea 
                      id="service-desc"
                      placeholder={t('admin.description')}
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-bold text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none h-24 resize-none text-zinc-900 dark:text-zinc-300 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                      value={newService.description}
                      onChange={(e) => setNewService({...newService, description: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label htmlFor="service-price" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.price')}</label>
                      <div className="relative">
                        <span className="absolute left-6 top-1/2 -translate-y-1/2 font-black text-zinc-400 dark:text-zinc-300">{t('common.currency')}</span>
                        <input 
                          id="service-price"
                          required
                          type="number"
                          placeholder={t('admin.price')}
                          className="w-full pl-20 pr-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                          value={newService.price}
                          onChange={(e) => setNewService({...newService, price: Number(e.target.value)})}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label htmlFor="service-duration" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('common.duration')}</label>
                      <input 
                        id="service-duration"
                        required
                        type="number"
                        placeholder={t('admin.min')}
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                        value={newService.duration}
                        onChange={(e) => setNewService({...newService, duration: Number(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="service-category" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.category')}</label>
                    <div className="flex gap-2">
                      {!isCreatingNewCategoryInForm ? (
                        <select 
                          id="service-category"
                          className="flex-1 px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none appearance-none text-zinc-900 dark:text-white"
                          value={newService.category}
                          onChange={(e) => {
                            if (e.target.value === 'ADD_NEW') {
                              setIsCreatingNewCategoryInForm(true);
                              setNewService({...newService, category: ''});
                            } else {
                              setNewService({...newService, category: e.target.value});
                            }
                          }}
                        >
                          {uniqueCategories.map(cat => (
                            <option key={cat} value={cat} className="dark:bg-zinc-900">{cat}</option>
                          ))}
                          <option value="ADD_NEW" className="text-zinc-400 dark:text-zinc-300 font-bold italic dark:bg-zinc-900">+ {t('admin.addNewCategory')}</option>
                        </select>
                      ) : (
                        <div className="flex-1 flex gap-2">
                          <input 
                            autoFocus
                            placeholder={t('admin.category')}
                            className="flex-1 px-6 py-4 bg-zinc-50 dark:bg-zinc-900 border-none rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-300 placeholder-zinc-400 dark:placeholder-zinc-400 placeholder:text-zinc-400 dark:placeholder:text-zinc-500"
                            value={newService.category}
                            onChange={(e) => setNewService({...newService, category: e.target.value})}
                          />
                          <button 
                            type="button"
                            onClick={() => {
                              setIsCreatingNewCategoryInForm(false);
                              setNewService({...newService, category: uniqueCategories[0] || ''});
                            }}
                            className="px-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 rounded-2xl hover:text-zinc-600 dark:hover:text-zinc-400 transition-colors"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button 
                    type="submit"
                    disabled={isSubmittingService}
                    className="flex-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-4 rounded-2xl font-black text-sm shadow-xl shadow-zinc-200 dark:shadow-black/50 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all flex items-center justify-center gap-2"
                  >
                    {isSubmittingService && <LoadingSpinner />}
                    {t('common.save')}
                  </button>
                  <button 
                    type="button"
                    onClick={() => setIsAddingService(false)}
                    disabled={isSubmittingService}
                    className="flex-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 py-4 rounded-2xl font-black text-sm hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Admin New Reservation Modal */}
      <AnimatePresence>
        {isAddingShop && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsAddingShop(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl relative w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden z-10 border border-zinc-200 dark:border-zinc-800"
            >
              <div className="flex items-center justify-between shrink-0 p-8 md:p-10 pb-6 border-b border-zinc-100 dark:border-zinc-800 z-20">
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
                    {t('admin.addShop') || 'Add Shop'}
                  </h3>
                  <p className="text-xs font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest mt-1">
                    Enter new shop details
                  </p>
                </div>
                <button 
                  onClick={() => setIsAddingShop(false)}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-all"
                >
                  <XCircle size={24} className="text-zinc-400" />
                </button>
              </div>

              <form onSubmit={handleAddShop} className="flex-1 overflow-y-auto p-8 md:p-10 pt-6 space-y-6">
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="shop-name" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.shopName') || 'Shop Name'}</label>
                    <input 
                      id="shop-name"
                      required
                      placeholder={t('admin.shopName') || 'Shop Name'}
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 transition-all"
                      value={newShop.name}
                      onChange={(e) => setNewShop({...newShop, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="shop-address" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.address') || 'Address'}</label>
                    <textarea 
                      id="shop-address"
                      placeholder={t('admin.address') || 'Address'}
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-bold text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none h-28 resize-none text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 transition-all"
                      value={newShop.address}
                      onChange={(e) => setNewShop({...newShop, address: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="shop-phone" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('booking.phoneNumber') || 'Phone Number'}</label>
                      <input 
                        id="shop-phone"
                        required
                        placeholder={t('booking.phoneNumber') || 'Phone Number'}
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 transition-all"
                        value={newShop.phone}
                        onChange={(e) => setNewShop({...newShop, phone: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="shop-email" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('booking.emailAddress') || 'Email Address'}</label>
                      <input 
                        id="shop-email"
                        type="email"
                        required
                        placeholder={t('booking.emailAddress') || 'Email Address'}
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-black focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 transition-all"
                        value={newShop.email}
                        onChange={(e) => setNewShop({...newShop, email: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="shop-opening" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.opening') || 'Opening'}</label>
                      <ModernTimePicker 
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-black text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white transition-all"
                        value={newShop.opening_time || '09:00'}
                        onChange={(value) => setNewShop({...newShop, opening_time: value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="shop-closing" className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.closing') || 'Closing'}</label>
                      <ModernTimePicker 
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-black text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white transition-all"
                        value={newShop.closing_time || '18:00'}
                        onChange={(value) => setNewShop({...newShop, closing_time: value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-6 pt-6 mt-6 border-t border-zinc-100 dark:border-zinc-800/50">
                    <div className="flex items-center justify-between p-4 bg-zinc-50 dark:bg-zinc-900/30 rounded-2xl">
                      <div>
                        <p className="font-black text-sm uppercase tracking-tight text-zinc-900 dark:text-white">{t('admin.enableReminders') || 'Enable Reminders'}</p>
                        <p className="text-[10px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-widest mt-0.5">{t('admin.enableRemindersDesc') || 'Send automatic reminders to customers'}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setNewShop({...newShop, reminder_enabled: !newShop.reminder_enabled})}
                        className={`w-14 h-8 rounded-full transition-all relative ${newShop.reminder_enabled ? 'bg-zinc-900 dark:bg-amber-500' : 'bg-zinc-200 dark:bg-zinc-800'}`}
                      >
                        <div className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all shadow-sm ${newShop.reminder_enabled ? 'left-7' : 'left-1'}`} />
                      </button>
                    </div>

                    {newShop.reminder_enabled && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-2">
                        <div className="space-y-2">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.reminderType') || 'Reminder Type'}</label>
                            <select
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-black text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white appearance-none transition-all"
                            value={newShop.reminder_type}
                            onChange={(e) => setNewShop({...newShop, reminder_type: e.target.value as any})}
                          >
                            <option value="email" className="dark:bg-zinc-900 font-bold">{t('admin.emailOnly') || 'Email Only'}</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4">{t('admin.hoursBefore') || 'Hours Before'}</label>
                          <input 
                            type="number"
                            min="1"
                            max="72"
                            className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-black text-sm focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 transition-all"
                            value={newShop.reminder_hours_before}
                            onChange={(e) => setNewShop({...newShop, reminder_hours_before: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-4 pt-6 pb-2 border-t border-zinc-100 dark:border-zinc-800">
                  <button 
                    type="submit"
                    disabled={isSubmittingShop}
                    className="flex-1 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 py-4 rounded-2xl font-black text-sm shadow-xl shadow-zinc-200 dark:shadow-black/50 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    {isSubmittingShop && <LoadingSpinner />}
                    {t('common.save') || 'Save'}
                  </button>
                  <button 
                    type="button"
                    onClick={() => setIsAddingShop(false)}
                    disabled={isSubmittingShop}
                    className="flex-1 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 py-4 rounded-2xl font-black text-sm hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-all uppercase tracking-widest disabled:opacity-50"
                  >
                    {t('common.cancel') || 'Cancel'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Admin New Reservation Modal */}
      <AnimatePresence>
        {isAddingReservation && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsAddingReservation(false)}
              className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white dark:bg-zinc-900 rounded-[40px] shadow-2xl overflow-hidden my-auto"
            >
              <div className="p-8 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
                <h3 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">New Booking</h3>
                <button 
                  onClick={() => setIsAddingReservation(false)}
                  className="w-10 h-10 bg-zinc-100 dark:bg-zinc-800 text-zinc-500 rounded-full flex items-center justify-center hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
                <div className="space-y-4">
                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-2">Customer Details</label>
                  <input 
                    type="text" 
                    placeholder="Name" 
                    value={bookingCustomerName}
                    onChange={e => setBookingCustomerName(e.target.value)}
                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                  />
                  <input 
                    type="email" 
                    placeholder="Email" 
                    value={bookingCustomerEmail}
                    onChange={e => setBookingCustomerEmail(e.target.value)}
                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                  />
                  <input 
                    type="tel" 
                    placeholder="Phone" 
                    value={bookingCustomerPhone}
                    onChange={e => setBookingCustomerPhone(e.target.value)}
                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                  />
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-2">Select Barber</label>
                  <select 
                    title="Select Barber"
                    value={adminBookingBarberId}
                    onChange={(e: any) => setAdminBookingBarberId(e.target.value)}
                    className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-amber-500 outline-none text-zinc-900 dark:text-white"
                  >
                    <option value="" disabled>Select a barber</option>
                    {barbers.map(barber => (
                      <option key={barber.id} value={barber.id}>{barber.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-2">Select Services</label>
                  <div className="space-y-2">
                    {services.map(service => (
                      <label key={service.id} className="flex items-center gap-3 p-4 bg-zinc-50 dark:bg-zinc-800 rounded-2xl cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-700/50 transition-colors">
                        <input 
                          type="checkbox"
                          checked={adminBookingServices.includes(service.id)}
                          onChange={(e) => {
                            if (e.target.checked) setAdminBookingServices([...adminBookingServices, service.id]);
                            else setAdminBookingServices(adminBookingServices.filter(id => id !== service.id));
                          }}
                          className="w-5 h-5 rounded-md border-2 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-amber-500 focus:ring-zinc-900 dark:focus:ring-amber-500 bg-transparent"
                        />
                        <div className="flex-1">
                          <p className="font-bold text-sm text-zinc-900 dark:text-white">{service.name}</p>
                          <p className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500">{service.duration} mins • ${service.price}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-zinc-100 dark:border-zinc-800">
                  <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-500 ml-2">Date & Time</label>
                  <div className="bg-zinc-50 dark:bg-zinc-800 p-4 rounded-2xl">
                    <input 
                      type="date"
                      value={format(adminBookingDate, 'yyyy-MM-dd')}
                      onChange={e => {
                        if (e.target.value) setAdminBookingDate(parseISO(e.target.value));
                      }}
                      className="w-full bg-transparent border-none font-bold text-sm outline-none text-zinc-900 dark:text-white focus:ring-0 p-0"
                    />
                  </div>
                  
                  {adminBookingBarberId && adminBookingDate && (
                    <div className="grid grid-cols-4 gap-2">
                      {adminAvailableSlots
                        .filter(s => s.is_available)
                        .sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time))
                        .map(slot => (
                          <button
                            key={slot.id}
                            onClick={() => setAdminBookingSlotId(slot.id)}
                            className={`p-3 rounded-xl font-black text-xs transition-all ${
                              adminBookingSlotId === slot.id
                                ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 shadow-lg'
                                : 'bg-zinc-50 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-700'
                            }`}
                          >
                            {slot.time.substring(0, 5)}
                          </button>
                      ))}
                      {adminAvailableSlots.filter(s => s.is_available).length === 0 && (
                        <p className="col-span-4 text-center text-xs font-bold text-zinc-400 dark:text-zinc-500 py-4">No available slots</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-8 border-t border-zinc-100 dark:border-zinc-800 flex gap-4">
                <button 
                  onClick={() => setIsAddingReservation(false)}
                  className="flex-1 py-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleAdminCreateReservation}
                  disabled={loading || !adminBookingSlotId || adminBookingServices.length === 0 || !adminBookingBarberId}
                  className="flex-1 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Booking...' : 'Book Appointment'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Book Slot Modal */}
      <AnimatePresence>
        {isBookSlotModalOpen && slotToBook && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsBookSlotModalOpen(false)}
              className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-white dark:bg-zinc-900 rounded-[40px] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
              <div className="p-8 border-b border-zinc-100 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <div className="text-left">
                  <h2 className="text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('booking.bookSlot')}</h2>
                  <p className="text-xs font-bold text-zinc-400 dark:text-zinc-300 mt-1">{slotToBook.date} @ {slotToBook.time.substring(0, 5)}</p>
                </div>
                <button 
                  onClick={() => setIsBookSlotModalOpen(false)}
                  className="w-10 h-10 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700 hover:text-zinc-600 dark:hover:text-zinc-200 rounded-full flex items-center justify-center transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-8 overflow-y-auto flex-1">
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('admin.services')}</label>
                    <div className="space-y-2">
                      {services.filter(s => s.barber_shop_id === selectedShopId).map(service => (
                        <label key={service.id} className="flex items-center gap-3 p-3 rounded-2xl border border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-all">
                          <input 
                            type="checkbox" 
                            className="w-5 h-5 rounded-md border-zinc-300 dark:border-zinc-700 text-zinc-900 dark:text-zinc-100 focus:ring-zinc-900 dark:focus:ring-zinc-100 bg-white dark:bg-zinc-800"
                            checked={bookingServices.includes(service.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBookingServices([...bookingServices, service.id]);
                              } else {
                                setBookingServices(bookingServices.filter(id => id !== service.id));
                              }
                            }}
                          />
                          <div className="flex-1">
                            <div className="font-bold text-sm text-zinc-900 dark:text-zinc-100">{service.name}</div>
                            <div className="text-xs text-zinc-400 dark:text-zinc-300">{service.duration} {t('common.min')}</div>
                          </div>
                          <div className="font-black text-sm text-zinc-900 dark:text-zinc-100">{t('common.currency')}{service.price}</div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('booking.fullName')}</label>
                    <input 
                      type="text" 
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-zinc-100 outline-none text-zinc-900 dark:text-zinc-100"
                      value={bookingCustomerName}
                      onChange={(e) => setBookingCustomerName(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('booking.emailAddress')}</label>
                    <input 
                      type="email" 
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-zinc-100 outline-none text-zinc-900 dark:text-zinc-100"
                      value={bookingCustomerEmail}
                      onChange={(e) => setBookingCustomerEmail(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('booking.phoneNumber')}</label>
                    <input 
                      type="tel" 
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800\/50 border border-zinc-200\/50 dark:border-zinc-800\/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-zinc-100 outline-none text-zinc-900 dark:text-zinc-100"
                      value={bookingCustomerPhone}
                      onChange={(e) => setBookingCustomerPhone(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="p-8 border-t border-zinc-100 dark:border-zinc-800 shrink-0">
                <button
                  onClick={handleBookSlot}
                  disabled={loading || bookingServices.length === 0}
                  className="w-full py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-all disabled:opacity-50 shadow-xl shadow-zinc-200 dark:shadow-none"
                >
                  {loading ? t('common.loading') : t('common.confirm')}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Inventory Action Modals */}
        <AnimatePresence>
          {(isRestockingItem || isUsingItem) && selectedInventoryItem && (
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
                onClick={() => {
                  setIsRestockingItem(false);
                  setIsUsingItem(false);
                  setSelectedInventoryItem(null);
                }}
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="w-full max-w-sm bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl overflow-hidden relative z-10 p-8 sm:p-10 border border-zinc-100 dark:border-zinc-800"
              >
                <div className="text-center mb-8">
                  <h3 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">
                    {isRestockingItem ? (t('admin.restock') || 'Restock') : (t('admin.useItem') || 'Use Item')}
                  </h3>
                  <p className="text-xs font-bold text-zinc-400 dark:text-zinc-500 mt-1">{selectedInventoryItem.name}</p>
                </div>

                <div className="flex items-center justify-center gap-8 mb-10">
                  <button 
                    onClick={() => setInventoryActionAmount(Math.max(1, inventoryActionAmount - 1))}
                    className="w-12 h-12 bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl flex items-center justify-center text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all font-black text-xl"
                  >
                    <Minus size={20} />
                  </button>
                  <div className="text-4xl font-black tracking-tighter text-zinc-900 dark:text-white min-w-[3rem] text-center">
                    {inventoryActionAmount}
                  </div>
                  <button 
                    onClick={() => setInventoryActionAmount(inventoryActionAmount + 1)}
                    className="w-12 h-12 bg-zinc-50 dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl flex items-center justify-center text-zinc-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all font-black text-xl"
                  >
                    <Plus size={20} />
                  </button>
                </div>

                <div className="flex gap-4">
                  <button 
                    onClick={() => {
                      setIsRestockingItem(false);
                      setIsUsingItem(false);
                      setSelectedInventoryItem(null);
                    }}
                    className="flex-1 py-4 bg-zinc-100 dark:bg-zinc-800 text-zinc-400 dark:text-zinc-300 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-zinc-200 transition-all"
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    onClick={() => handleUpdateInventory(
                      selectedInventoryItem.id, 
                      isRestockingItem 
                        ? selectedInventoryItem.quantity + inventoryActionAmount 
                        : selectedInventoryItem.quantity - inventoryActionAmount
                    )}
                    className={`flex-1 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest text-white transition-all shadow-xl font-black ${
                      isRestockingItem 
                        ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20' 
                        : 'bg-amber-600 hover:bg-amber-500 shadow-amber-500/20'
                    }`}
                  >
                    {t('common.confirm')}
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {isAddItemModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-zinc-900/40 dark:bg-black/60 backdrop-blur-sm"
              onClick={() => setIsAddItemModalOpen(false)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-xl bg-white dark:bg-zinc-950 rounded-[3rem] shadow-2xl flex flex-col max-h-[90vh] overflow-hidden border border-zinc-100 dark:border-zinc-800 transition-colors"
            >
              <div className="p-6 sm:p-8 border-b border-zinc-50 dark:border-zinc-800 flex items-center justify-between shrink-0">
                <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tighter text-zinc-900 dark:text-white">{t('admin.addItem') || 'Add Item'}</h2>
                <button 
                  onClick={() => setIsAddItemModalOpen(false)}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 dark:text-zinc-300"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6 custom-scrollbar">
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('admin.itemName') || 'Item Name'}</label>
                    <input 
                      type="text" 
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-zinc-100 outline-none text-zinc-900 dark:text-zinc-100"
                      value={newItem.name}
                      onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('admin.quantity') || 'Quantity'}</label>
                      <input 
                        type="number" 
                        min="0"
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-zinc-100 outline-none text-zinc-900 dark:text-zinc-100"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('admin.minStock') || 'Min. Stock'}</label>
                      <input 
                        type="number" 
                        min="0"
                        className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-zinc-100 outline-none text-zinc-900 dark:text-zinc-100"
                        value={newItem.min_stock_level}
                        onChange={(e) => setNewItem({...newItem, min_stock_level: parseInt(e.target.value) || 0})}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-zinc-400 dark:text-zinc-300 ml-4 mb-2 block">{t('admin.unit') || 'Unit'}</label>
                    <input 
                      type="text" 
                      placeholder="e.g., pcs, bottles, ml"
                      className="w-full px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200/50 dark:border-zinc-800/50 rounded-2xl font-bold focus:ring-2 ring-zinc-900 dark:ring-zinc-100 outline-none text-zinc-900 dark:text-zinc-100"
                      value={newItem.unit}
                      onChange={(e) => setNewItem({...newItem, unit: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="p-8 border-t border-zinc-100 dark:border-zinc-800 shrink-0">
                <button
                  onClick={handleAddInventoryItem}
                  disabled={loading || !newItem.name.trim()}
                  className="w-full py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-all disabled:opacity-50 shadow-xl shadow-zinc-200 dark:shadow-none"
                >
                  {loading ? t('common.loading') : t('admin.addItem') || 'Add Item'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
